import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../utils/api';
import { SectionRepliable, Loading, Badge, niveauMessage, couleurMessage, styleMessage, COULEUR_ATTENTION } from '../components/Shared';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';

// ─── Sous-composants ────────────────────────────────────────────

function SectionTitle({ children }) {
  return <h4 style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', margin: '1.5rem 0 0.8rem' }}>{children}</h4>;
}

function Field({ label, children, hint }) {
  return (
    <div style={{ marginBottom: '0.9rem' }}>
      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>{label}</label>
      {children}
      {hint && <p style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginTop: '0.2rem' }}>{hint}</p>}
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '0.5rem 0.7rem', borderRadius: '6px',
  border: '1px solid var(--border)', background: 'var(--bg-elevated)',
  color: 'var(--text-primary)', fontSize: '0.9rem', boxSizing: 'border-box',
};

// ─── Types de notes de suivi ─────────────────────────────────────

function NoteTypesManager() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newLabel, setNewLabel] = useState('');
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState('');

  function load() {
    setLoading(true);
    apiFetch('/note-types').then(d => { setTypes(d || []); setLoading(false); }).catch(() => setLoading(false));
  }
  useEffect(load, []);

  async function handleAdd() {
    if (!newLabel.trim()) return;
    try {
      await apiFetch('/note-types', { method: 'POST', body: JSON.stringify({ libelle: newLabel.trim() }) });
      setNewLabel('');
      setMsg('✓ Type ajouté');
      setTimeout(() => setMsg(''), 2500);
      load();
    } catch (e) { setMsg('❌ ' + e.message); }
  }

  async function handleRename(id, libelle) {
    if (!libelle.trim()) { setEditing(null); return; }
    try {
      await apiFetch(`/note-types/${id}`, { method: 'PUT', body: JSON.stringify({ libelle: libelle.trim() }) });
      setEditing(null);
      load();
    } catch (e) { setMsg('❌ ' + e.message); }
  }

  async function handleDelete(id) {
    if (!window.confirm('Supprimer ce type de note ?')) return;
    try {
      await apiFetch(`/note-types/${id}`, { method: 'DELETE' });
      setMsg('✓ Type supprimé');
      setTimeout(() => setMsg(''), 2500);
      load();
    } catch (e) { setMsg('❌ ' + e.message); }
  }

  return (
    <div>
      <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
        Ces types apparaissent dans l'onglet Suivi des sessions (comptes rendus, entretiens, bilans...).
      </p>
      {msg && (
        <div style={{ padding: '0.4rem 0.7rem', borderRadius: '6px', fontSize: '0.82rem', marginBottom: '0.8rem',
          ...styleMessage(niveauMessage(msg)) }}>
          {msg}
        </div>
      )}
      {loading ? <Loading /> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '0.8rem' }}>
          {types.length === 0 && <p style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>Aucun type défini.</p>}
          {types.map(t => (
            <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '7px', padding: '0.35rem 0.7rem' }}>
              {editing === t.id ? (
                <>
                  <input autoFocus defaultValue={t.libelle}
                    onKeyDown={e => { if (e.key === 'Enter') handleRename(t.id, e.target.value); if (e.key === 'Escape') setEditing(null); }}
                    onBlur={e => handleRename(t.id, e.target.value)}
                    style={{ flex: 1, padding: '0.2rem 0.4rem', borderRadius: '5px', border: '1px solid var(--border)', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontSize: '0.85rem' }} />
                  <button onClick={() => setEditing(null)}
                    style={{ padding: '0.15rem 0.5rem', borderRadius: '4px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.78rem' }}>
                    Annuler
                  </button>
                </>
              ) : (
                <>
                  <span style={{ flex: 1, fontSize: '0.88rem', color: 'var(--text-primary)' }}>{t.libelle}</span>
                  <button onClick={() => setEditing(t.id)}
                    style={{ padding: '0.15rem 0.4rem', borderRadius: '4px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.75rem' }}>
                    ✏️
                  </button>
                  <button onClick={() => handleDelete(t.id)}
                    style={{ padding: '0.15rem 0.4rem', borderRadius: '4px', border: 'none', background: 'rgba(239,68,68,0.1)', color: '#ef4444', cursor: 'pointer', fontSize: '0.75rem' }}>
                    ✕
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: '0.4rem' }}>
        <input value={newLabel} onChange={e => setNewLabel(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAdd()}
          placeholder="Nouveau type de note..."
          style={{ ...inputStyle, flex: 1 }} />
        <button onClick={handleAdd} disabled={!newLabel.trim()}
          style={{ padding: '0.4rem 0.9rem', borderRadius: '7px', border: 'none', background: '#E87722', color: '#fff', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, whiteSpace: 'nowrap', opacity: newLabel.trim() ? 1 : 0.5 }}>
          + Ajouter
        </button>
      </div>
    </div>
  );
}

// ─── Onglet Activités clients ────────────────────────────────────

function OngletActivites() {
  const { token } = useAuth();
  const [activites, setActivites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newLabel, setNewLabel] = useState('');
  const [adding, setAdding] = useState(false);
  const [msg, setMsg] = useState('');

  function load() {
    setLoading(true);
    apiFetch('/activites').then(d => { setActivites(Array.isArray(d) ? d : []); setLoading(false); }).catch(() => setLoading(false));
  }
  useEffect(load, []);

  async function handleAdd() {
    if (!newLabel.trim()) return;
    setAdding(true);
    try {
      await apiFetch('/activites', { method: 'POST', body: JSON.stringify({ libelle: newLabel.trim() }) });
      setNewLabel('');
      setMsg('✓ Activité ajoutée');
      setTimeout(() => setMsg(''), 2500);
      load();
    } catch (e) { setMsg('❌ ' + e.message); }
    finally { setAdding(false); }
  }

  async function handleDelete(id) {
    if (!window.confirm('Supprimer cette activité ?')) return;
    try {
      await apiFetch(`/activites/${id}`, { method: 'DELETE' });
      setMsg('✓ Activité supprimée');
      setTimeout(() => setMsg(''), 2500);
      load();
    } catch (e) { setMsg('❌ ' + e.message); }
  }

  return (
    <div>
      <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1.2rem' }}>
        Ces activités apparaissent dans les fiches d'inscription et sur les fiches client.
      </p>

        {msg && (
          <div style={{ padding: '0.4rem 0.7rem', borderRadius: '6px', fontSize: '0.82rem', marginBottom: '1rem',
            ...styleMessage(niveauMessage(msg)) }}>
            {msg}
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.2rem' }}>
          <input
            type="text" value={newLabel} onChange={e => setNewLabel(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
            placeholder="Nouvelle activité..."
            style={{ ...inputStyle, flex: 1 }}
          />
          <button className="btn-primary" onClick={handleAdd} disabled={adding || !newLabel.trim()} style={{ whiteSpace: 'nowrap' }}>
            + Ajouter
          </button>
        </div>

        {loading ? <Loading /> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            {activites.length === 0 && (
              <p style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>Aucune activité définie.</p>
            )}
            {activites.map(a => (
              <div key={a.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '0.5rem 0.8rem', borderRadius: '6px', background: 'var(--bg-elevated)',
                border: '1px solid var(--border)', fontSize: '0.88rem' }}>
                <span>{a.libelle}</span>
                <button onClick={() => handleDelete(a.id)}
                  style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '0.8rem', padding: '0.1rem 0.4rem' }}>
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

// ─── Catégories de formation ─────────────────────────────────────

function OngletCategoriesFormation() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [modal, setModal] = useState(null); // null | 'new' | cat
  const [form, setForm] = useState({ libelle: '' });
  const [saving, setSaving] = useState(false);

  function load() {
    setLoading(true);
    apiFetch('/formation-categories').then(d => { setCategories(d || []); setLoading(false); }).catch(() => setLoading(false));
  }
  useEffect(load, []);

  function openNew() { setForm({ libelle: '' }); setModal('new'); setMsg(''); }
  function openEdit(c) { setForm({ libelle: c.libelle }); setModal(c); setMsg(''); }

  async function handleSave() {
    if (!form.libelle.trim()) { setMsg('❌ Libellé requis'); return; }
    setSaving(true); setMsg('');
    try {
      if (modal === 'new') {
        await apiFetch('/formation-categories', { method: 'POST', body: JSON.stringify(form) });
        setMsg('✓ Catégorie créée');
      } else {
        await apiFetch(`/formation-categories/${modal.id}`, { method: 'PUT', body: JSON.stringify(form) });
        setMsg('✓ Catégorie mise à jour');
      }
      setTimeout(() => setMsg(''), 2500);
      setModal(null);
      load();
    } catch (e) { setMsg('❌ ' + e.message); }
    finally { setSaving(false); }
  }

  async function handleDelete(c) {
    if (!window.confirm(`Supprimer la catégorie "${c.libelle}" ?`)) return;
    try {
      await apiFetch(`/formation-categories/${c.id}`, { method: 'DELETE' });
      setMsg('✓ Catégorie supprimée');
      setTimeout(() => setMsg(''), 2500);
      load();
    } catch (e) { setMsg('❌ ' + e.message); }
  }

  return (
    <div>
      <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
        Catégories de formation — utilisées dans le catalogue et les filtres.
      </p>
      {msg && (
        <div style={{ padding: '0.4rem 0.7rem', borderRadius: '6px', fontSize: '0.82rem', marginBottom: '0.8rem',
          ...styleMessage(niveauMessage(msg)) }}>
          {msg}
        </div>
      )}
      <button className="btn-primary" onClick={openNew} style={{ marginBottom: '1rem', fontSize: '0.85rem' }}>
        + Ajouter une catégorie
      </button>
      {loading ? <Loading /> : categories.length === 0 ? (
        <p style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>Aucune catégorie définie.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          {categories.map(c => (
            <div key={c.id} style={{
              display: 'flex', alignItems: 'center', gap: '0.6rem',
              padding: '0.55rem 0.8rem', borderRadius: '8px',
              background: 'var(--bg-elevated)', border: '1px solid var(--border)', fontSize: '0.88rem',
            }}>
              <span style={{ fontWeight: 600, flex: 1 }}>{c.libelle}</span>
              <button onClick={() => openEdit(c)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem', color: 'var(--accent-blue)', padding: '0.1rem 0.4rem' }}>✎</button>
              <button onClick={() => handleDelete(c)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem', color: '#ef4444', padding: '0.1rem 0.4rem' }}>✕</button>
            </div>
          ))}
        </div>
      )}
      {modal !== null && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div style={{ background: 'var(--bg-elevated)', borderRadius: '12px', padding: '1.5rem',
            width: '380px', maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
            <h3 style={{ margin: '0 0 1.2rem', fontSize: '1rem', fontWeight: 700 }}>
              {modal === 'new' ? 'Nouvelle catégorie' : `Modifier — ${modal.libelle}`}
            </h3>
            <div style={{ marginBottom: '0.6rem' }}>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '0.25rem' }}>Libellé</label>
              <input value={form.libelle} onChange={e => setForm(f => ({ ...f, libelle: e.target.value }))}
                style={inputStyle} placeholder="ex : Sécurité, Amiante..." autoFocus />
            </div>
            {msg && <p style={{ color: '#f87171', fontSize: '0.82rem', margin: '0.4rem 0' }}>{msg}</p>}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button className="btn-secondary" onClick={() => setModal(null)}>Annuler</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? '⏳...' : '✓ Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Gestion des Salles ──────────────────────────────────────────

const COULEURS_SALLE = [
  { val: '',        label: '— Aucune —' },
  { val: '#3b82f6', label: 'Bleu' },
  { val: '#10b981', label: 'Vert' },
  { val: '#f59e0b', label: 'Orange' },
  { val: '#ef4444', label: 'Rouge' },
  { val: '#8b5cf6', label: 'Violet' },
  { val: '#06b6d4', label: 'Cyan' },
  { val: '#f97316', label: 'Orange foncé' },
  { val: '#64748b', label: 'Gris' },
];

function OngletSalles() {
  const [salles, setSalles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [modal, setModal] = useState(null); // null | 'new' | salle
  const [form, setForm] = useState({ salle_id: '', nom_salle: '', adresse: '', capacite: '', couleur: '' });
  const [saving, setSaving] = useState(false);

  function load() {
    setLoading(true);
    apiFetch('/salles').then(d => { setSalles(d.data || []); setLoading(false); }).catch(() => setLoading(false));
  }
  useEffect(load, []);

  function openNew() {
    setForm({ salle_id: '', nom_salle: '', adresse: '', capacite: '', couleur: '' });
    setModal('new');
    setMsg('');
  }
  function openEdit(s) {
    setForm({ salle_id: s.salle_id, nom_salle: s.nom_salle || '', adresse: s.adresse || '', capacite: s.capacite || '', couleur: s.couleur || '' });
    setModal(s);
    setMsg('');
  }

  async function handleSave() {
    if (!form.salle_id.trim() || !form.nom_salle.trim()) { setMsg('❌ ID et nom requis'); return; }
    setSaving(true); setMsg('');
    try {
      if (modal === 'new') {
        await apiFetch('/salles', { method: 'POST', body: JSON.stringify(form) });
        setMsg('✓ Salle créée');
      } else {
        await apiFetch(`/salles/${modal.salle_id}`, { method: 'PUT', body: JSON.stringify(form) });
        setMsg('✓ Salle mise à jour');
      }
      setTimeout(() => setMsg(''), 2500);
      setModal(null);
      load();
    } catch (e) { setMsg('❌ ' + e.message); }
    finally { setSaving(false); }
  }

  async function handleDelete(s) {
    if (!window.confirm(`Supprimer la salle "${s.nom_salle}" ?`)) return;
    try {
      await apiFetch(`/salles/${s.salle_id}`, { method: 'DELETE' });
      setMsg('✓ Salle supprimée');
      setTimeout(() => setMsg(''), 2500);
      load();
    } catch (e) { setMsg('❌ ' + e.message); }
  }

  const fld = (key, label, type = 'text', opts = {}) => (
    <div style={{ marginBottom: '0.6rem' }}>
      <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '0.25rem' }}>{label}</label>
      {type === 'select' ? (
        <select value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} style={inputStyle}>
          {COULEURS_SALLE.map(c => (
            <option key={c.val} value={c.val}>{c.label}</option>
          ))}
        </select>
      ) : (
        <input type={type} value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
          style={inputStyle} {...opts} />
      )}
    </div>
  );

  return (
    <div>
      <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
        Salles de formation disponibles — utilisées dans les sessions et le planning.
      </p>

      {msg && (
        <div style={{ padding: '0.4rem 0.7rem', borderRadius: '6px', fontSize: '0.82rem', marginBottom: '0.8rem',
          ...styleMessage(niveauMessage(msg)) }}>
          {msg}
        </div>
      )}

      <button className="btn-primary" onClick={openNew} style={{ marginBottom: '1rem', fontSize: '0.85rem' }}>
        + Ajouter une salle
      </button>

      {loading ? <Loading /> : salles.length === 0 ? (
        <p style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>Aucune salle définie.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          {salles.map(s => (
            <div key={s.salle_id} style={{
              display: 'flex', alignItems: 'center', gap: '0.6rem',
              padding: '0.55rem 0.8rem', borderRadius: '8px',
              background: 'var(--bg-elevated)', border: '1px solid var(--border)', fontSize: '0.88rem',
            }}>
              {/* Pastille couleur */}
              <div style={{
                width: 12, height: 12, borderRadius: '50%', flexShrink: 0,
                background: s.couleur || 'var(--border)', border: '1px solid rgba(0,0,0,0.1)',
              }} />
              {/* ID badge */}
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-tertiary)',
                background: 'var(--bg-main)', border: '1px solid var(--border)',
                padding: '0.1rem 0.4rem', borderRadius: '4px', flexShrink: 0 }}>
                {s.salle_id}
              </span>
              {/* Nom */}
              <span style={{ fontWeight: 600, flex: 1 }}>{s.nom_salle}</span>
              {/* Capacité */}
              {s.capacite && (
                <span style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', flexShrink: 0 }}>
                  {s.capacite} pers.
                </span>
              )}
              {/* Adresse */}
              {s.adresse && (
                <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', flexShrink: 0,
                  maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {s.adresse}
                </span>
              )}
              {/* Actions */}
              <button onClick={() => openEdit(s)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem',
                  color: 'var(--accent-blue)', padding: '0.1rem 0.4rem' }}>
                ✎
              </button>
              <button onClick={() => handleDelete(s)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem',
                  color: '#ef4444', padding: '0.1rem 0.4rem' }}>
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Modal ajout / édition */}
      {modal !== null && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div style={{ background: 'var(--bg-elevated)', borderRadius: '12px', padding: '1.5rem',
            width: '420px', maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
            <h3 style={{ margin: '0 0 1.2rem', fontSize: '1rem', fontWeight: 700 }}>
              {modal === 'new' ? 'Nouvelle salle' : `Modifier — ${modal.nom_salle}`}
            </h3>
            {fld('salle_id', 'ID (ex: S01, BULLY1)', 'text', { disabled: modal !== 'new', maxLength: 10, placeholder: 'S01' })}
            {fld('nom_salle', 'Nom de la salle', 'text', { placeholder: 'Salle 1 — Bully' })}
            {fld('adresse', 'Adresse', 'text', { placeholder: '106 rue Costes et Bellonte, 62160 Bully' })}
            {fld('capacite', 'Capacité (personnes)', 'number', { min: 1, placeholder: '12' })}
            {fld('couleur', 'Couleur (planning)', 'select')}
            {msg && <p style={{ fontSize: '0.82rem', color: '#f87171', margin: '0.5rem 0' }}>{msg}</p>}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.2rem' }}>
              <button className="btn-secondary" onClick={() => setModal(null)} style={{ fontSize: '0.85rem' }}>Annuler</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving} style={{ fontSize: '0.85rem' }}>
                {saving ? '⏳' : modal === 'new' ? '+ Créer' : '✓ Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Onglet Société ──────────────────────────────────────────────

function OngletSociete({ params, onChange, onSave, saving }) {
  const [newSalarie, setNewSalarie] = useState({ prenom: '', nom: '' });

  const salaries = (() => { try { return JSON.parse(params.cej_salaries || '[]'); } catch { return []; } })();

  function updateSalaries(list) { onChange('cej_salaries', JSON.stringify(list)); }

  function addSalarie() {
    const p = newSalarie.prenom.trim(), n = newSalarie.nom.trim();
    if (!p && !n) return;
    updateSalaries([...salaries, { prenom: p, nom: n }]);
    setNewSalarie({ prenom: '', nom: '' });
  }

  const fieldsSociete = [
    { key: 'societe_nom',        label: 'Nom de la société' },
    { key: 'societe_adresse1',   label: 'Adresse ligne 1' },
    { key: 'societe_adresse2',   label: 'Adresse ligne 2 (ville)' },
    { key: 'societe_siege',      label: 'Siège social' },
    { key: 'societe_tel',        label: 'Téléphone principal' },
    { key: 'societe_tel2',       label: 'Téléphone secondaire' },
    { key: 'societe_email',      label: 'Email formation' },
    { key: 'societe_siret',      label: 'SIRET' },
    { key: 'societe_tva',        label: 'N° TVA intracommunautaire' },
    { key: 'societe_nda',        label: 'N° déclaration activité' },
    { key: 'societe_prefecture', label: 'Préfecture' },
    { key: 'societe_capital',       label: 'Capital (€)' },
    { key: 'societe_representant',  label: 'Représentant légal' },
  ];

  function renderFields(fields) {
    return fields.map(f => (
      <Field key={f.key} label={f.label} hint={f.hint}>
        {f.textarea
          ? <textarea value={params[f.key] || ''} onChange={e => onChange(f.key, e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
          : <input type="text" value={params[f.key] || ''} onChange={e => onChange(f.key, e.target.value)} style={inputStyle} />
        }
      </Field>
    ));
  }

  return (
    <div style={{ maxWidth: '700px' }}>
      <SectionTitle>Coordonnées de la société</SectionTitle>
      {renderFields(fieldsSociete)}

      <SectionTitle>Salariés</SectionTitle>
      <div style={{ marginBottom: '1rem' }}>
        {salaries.length > 0 && (
          <div style={{ marginBottom: '0.6rem', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
            {salaries.map((s, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.35rem 0.7rem', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '7px' }}>
                <span style={{ flex: 1, fontSize: '0.9rem' }}>{s.prenom} {s.nom}</span>
                <button type="button"
                  onClick={() => updateSalaries(salaries.filter((_, idx) => idx !== i))}
                  style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '0.85rem', lineHeight: 1 }}>✕</button>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.2rem' }}>Prénom</label>
            <input type="text" value={newSalarie.prenom}
              onChange={e => setNewSalarie(s => ({ ...s, prenom: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && addSalarie()}
              style={inputStyle} placeholder="Prénom" />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.2rem' }}>Nom</label>
            <input type="text" value={newSalarie.nom}
              onChange={e => setNewSalarie(s => ({ ...s, nom: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && addSalarie()}
              style={inputStyle} placeholder="Nom" />
          </div>
          <button type="button" onClick={addSalarie}
            style={{ padding: '0.45rem 0.9rem', borderRadius: '6px', background: 'var(--accent-blue)', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
            + Ajouter
          </button>
        </div>
      </div>

      <button className="btn-primary" onClick={onSave} disabled={saving} style={{ marginTop: '0.5rem' }}>
        {saving ? '⏳ Enregistrement...' : '✓ Enregistrer'}
      </button>
    </div>
  );
}

// ─── Onglet Email ────────────────────────────────────────────────

function OngletEmail({ params, onChange, onSave, saving }) {
  const [testEmail, setTestEmail] = useState('');
  const [testMsg, setTestMsg] = useState('');
  const [testing, setTesting] = useState(false);

  async function handleTest() {
    if (!testEmail) return setTestMsg('❌ Saisissez un email de test');
    setTesting(true); setTestMsg('');
    try {
      await apiFetch('/parametres/test-email', {
        method: 'POST',
        body: JSON.stringify({ email: testEmail }),
      });
      setTestMsg('✓ Email de test envoyé avec succès');
    } catch (e) { setTestMsg(`❌ ${e.message}`); }
    finally { setTesting(false); }
  }

  return (
    <div style={{ maxWidth: '600px' }}>
      <SectionTitle>Configuration SMTP</SectionTitle>
      <Field label="Serveur SMTP" hint="Ex: smtp.gmail.com ou smtp.office365.com">
        <input type="text" value={params.smtp_host || ''} onChange={e => onChange('smtp_host', e.target.value)} style={inputStyle} placeholder="smtp.gmail.com" />
      </Field>
      <Field label="Port SMTP">
        <input type="number" value={params.smtp_port || '587'} onChange={e => onChange('smtp_port', e.target.value)} style={inputStyle} placeholder="587" />
      </Field>
      <Field label="Login SMTP (email expéditeur)">
        <input type="email" value={params.smtp_user || ''} onChange={e => onChange('smtp_user', e.target.value)} style={inputStyle} placeholder="formation@cejconcept.fr" />
      </Field>
      <Field label="Mot de passe SMTP" hint="Stocké de façon sécurisée en base">
        <input type="password" value={params.smtp_pass || ''} onChange={e => onChange('smtp_pass', e.target.value)} style={inputStyle} placeholder="••••••••" />
      </Field>
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', marginBottom: '1.5rem' }}>
        <button className="btn-primary" onClick={onSave} disabled={saving}>
          {saving ? '⏳...' : '✓ Enregistrer'}
        </button>
      </div>
      <SectionTitle>Test d'envoi</SectionTitle>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
        <div style={{ flex: 1 }}>
          <Field label="Adresse email de test">
            <input type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)} style={inputStyle} placeholder="votre@email.fr" />
          </Field>
        </div>
        <button className="btn-secondary" onClick={handleTest} disabled={testing} style={{ marginBottom: '0.9rem' }}>
          {testing ? '⏳...' : '📧 Tester'}
        </button>
      </div>
      {testMsg && (
        <div style={{ padding: '0.5rem 0.8rem', borderRadius: '6px', fontSize: '0.85rem', marginTop: '0.3rem',
          ...styleMessage(niveauMessage(testMsg)) }}>
          {testMsg}
        </div>
      )}
    </div>
  );
}

// ─── Onglet Documents ────────────────────────────────────────────

const TEMPLATES = [
  { key: 'devis',               label: 'Devis',                  file: 'devis.html',                icon: '💶' },
  { key: 'convention',          label: 'Convention',             file: 'convention.html',            icon: '📋' },
  { key: 'convocation',         label: 'Convocation',            file: 'convocation.html',           icon: '📨' },
  { key: 'contrat',             label: 'Contrat mission',        file: 'contrat_mission.html',       icon: '📝' },
  { key: 'commande_formateur',  label: 'Commande formateur',     file: 'commande_formateur.html',    icon: '📄' },
  { key: 'certificat',          label: 'Certificat',             file: 'certificat_realisation.html',icon: '📜' },
  { key: 'attestation_modele1', label: 'Attestation de fin de formation', file: 'attestation_modele1.html', icon: '🎓' },
  { key: 'attestation_modele2', label: 'Attestation de compétence',       file: 'attestation_modele2.html', icon: '🎓' },
  { key: 'ficheinscription',    label: "Fiche d'inscription",    file: 'fiche_inscription.html',     icon: '📋' },
  { key: 'emargement',          label: "Feuille d'émargement",   file: 'emargement.html',            icon: '✍️', styleOnly: true },
];

const CONV_SECTIONS = [
  { key: 'conv_art1', label: 'Art. 1 — Objet et nature de la prestation' },
  { key: 'conv_art2', label: 'Art. 2 — Inscription' },
  { key: 'conv_art3', label: 'Art. 3 — Annulation' },
  { key: 'conv_art4', label: 'Art. 4 — Déroulement de la formation' },
  { key: 'conv_art5', label: 'Art. 5 — Prix' },
  { key: 'conv_art6', label: 'Art. 6 — Modalités de règlement' },
  { key: 'conv_cg',   label: 'Conditions générales (une ligne = un article numéroté)' },
];

const CONTRAT_SECTIONS = [
  { key: 'contrat_expose', label: 'Exposé préalable' },
  { key: 'contrat_art1',   label: 'Art. 1 — Objet de la mission' },
  { key: 'contrat_art2',   label: 'Art. 2 — Durée' },
  { key: 'contrat_art4_1', label: 'Art. 4.1 — Prix' },
  { key: 'contrat_art4_2', label: 'Art. 4.2 — Facturation et modalités de paiement' },
  { key: 'contrat_art11',  label: 'Art. 11 — Non-concurrence' },
  { key: 'contrat_art22',  label: 'Art. 22 — Récapitulatif des pièces à produire' },
  { key: 'contrat_cg',     label: 'Conditions générales (une ligne = un article numéroté)' },
];

function SectionsPanel({ sectionDefs, params, onParamChange }) {
  const [values, setValues] = useState(() => {
    const init = {};
    sectionDefs.forEach(s => { init[s.key] = params[s.key] || ''; });
    return init;
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  // Sync with params when they change externally
  useEffect(() => {
    setValues(prev => {
      const next = { ...prev };
      sectionDefs.forEach(s => {
        if (params[s.key] !== undefined && params[s.key] !== prev[s.key]) {
          next[s.key] = params[s.key];
        }
      });
      return next;
    });
  }, [params, sectionDefs]);

  async function handleSave() {
    setSaving(true); setMsg('');
    try {
      await apiFetch('/parametres', { method: 'PUT', body: JSON.stringify(values) });
      sectionDefs.forEach(s => onParamChange(s.key, values[s.key]));
      setMsg('✓ Sections sauvegardées');
      setTimeout(() => setMsg(''), 3000);
    } catch (e) { setMsg(`❌ ${e.message}`); }
    finally { setSaving(false); }
  }

  function handleReset(key) {
    setValues(v => ({ ...v, [key]: '' }));
  }

  return (
    <div style={{ overflowY: 'auto', flex: 1 }}>
      <p style={{ fontSize: '0.82rem', color: 'var(--text-tertiary)', marginBottom: '1rem' }}>
        Personnalisez le contenu des sections du document. Laissez vide pour utiliser le texte par défaut.
        Formatage : <code style={{ color: 'var(--accent-blue)' }}>**texte**</code> = gras,
        ligne commençant par <code style={{ color: 'var(--accent-blue)' }}>- </code> = liste à puces,
        ligne vide = nouveau paragraphe.
      </p>
      {sectionDefs.map(s => (
        <div key={s.key} style={{ marginBottom: '1.2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.3rem' }}>
            <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)' }}>{s.label}</label>
            <button
              className="btn-secondary"
              onClick={() => handleReset(s.key)}
              style={{ fontSize: '0.72rem', padding: '0.15rem 0.5rem', color: '#f59e0b', borderColor: '#f59e0b' }}>
              ↺ Réinitialiser
            </button>
          </div>
          <textarea
            value={values[s.key] || ''}
            onChange={e => setValues(v => ({ ...v, [s.key]: e.target.value }))}
            placeholder="Texte par défaut utilisé si vide"
            rows={5}
            style={{
              ...inputStyle,
              resize: 'vertical',
              fontFamily: 'Consolas, "Courier New", monospace',
              fontSize: '0.82rem',
              lineHeight: 1.5,
            }}
          />
        </div>
      ))}
      {msg && (
        <div style={{ padding: '0.4rem 0.7rem', borderRadius: '6px', fontSize: '0.82rem', marginBottom: '0.8rem',
          ...styleMessage(niveauMessage(msg)) }}>
          {msg}
        </div>
      )}
      <button className="btn-primary" onClick={handleSave} disabled={saving} style={{ marginBottom: '1rem' }}>
        {saving ? '⏳ Enregistrement...' : '✓ Sauvegarder les sections'}
      </button>
    </div>
  );
}

function renderPreviewHtml(html) {
  if (!html) return '';
  let out = html;
  // Supprimer les balises conditionnelles et each (garder le contenu intérieur)
  out = out.replace(/\{\{#if\s+[^}]+\}\}/g, '');
  out = out.replace(/\{\{\/if\}\}/g, '');
  out = out.replace(/\{\{else\}\}/g, '');
  out = out.replace(/\{\{#each\s+[^}]+\}\}/g, '');
  out = out.replace(/\{\{\/each\}\}/g, '');
  // Remplacer les <img src="{{variable}}"> par un placeholder image lisible
  out = out.replace(/<img([^>]*?)src="[^"]*\{\{([^}]+)\}\}[^"]*"([^>]*?)>/gi, (_, _b, key) => {
    const label = key.trim().replace(/^.*\./, '');
    const isSig = /sign|cachet/i.test(key);
    const bg = isSig ? '#f0fdf4' : '#fff7ed';
    const color = isSig ? '#166534' : '#c2410c';
    const icon = isSig ? '✍' : '🖼';
    return `<div style="display:inline-flex;align-items:center;justify-content:center;gap:4px;padding:6px 12px;border:2px dashed ${color};border-radius:6px;background:${bg};color:${color};font-size:0.8em;font-weight:600;">${icon} ${label}</div>`;
  });
  // Remplacer logo_html (variable spéciale = balise <img> complète injectée côté serveur)
  out = out.replace(/\{\{logo_html\}\}/g,
    '<div style="display:inline-flex;align-items:center;justify-content:center;gap:4px;padding:6px 12px;border:2px dashed #c2410c;border-radius:6px;background:#fff7ed;color:#c2410c;font-size:0.8em;font-weight:600;">🖼 logo</div>'
  );
  // Remplacer les variables restantes par des placeholders colorés
  out = out.replace(/\{\{([^}]+)\}\}/g, (_, key) => {
    const label = key.trim().replace(/^.*\./, '');
    return `<span style="background:#dbeafe;color:#1e40af;padding:1px 5px;border-radius:3px;font-size:0.82em;font-weight:600;white-space:nowrap;">${label}</span>`;
  });
  return out;
}

function OngletDocuments({ params, onParamChange }) {
  const [selected, setSelected] = useState('devis');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [activeMode, setActiveMode] = useState('editor'); // 'editor' | 'preview' | 'sections'
  const [dirty, setDirty] = useState(false);
  const fileRef = useRef();

  const tpl = TEMPLATES.find(t => t.key === selected);
  const hasSections = selected === 'convention' || selected === 'contrat';

  function switchTemplate(key) {
    if (dirty && !window.confirm('Le template a été modifié mais pas sauvegardé. Changer de template sans sauvegarder ?')) return;
    setSelected(key);
  }

  useEffect(() => {
    loadTemplate(selected);
    // Reset to editor mode when switching templates (unless sections available keep mode)
    setActiveMode(prev => {
      if (prev === 'sections' && selected !== 'convention' && selected !== 'contrat') return 'editor';
      return prev;
    });
  }, [selected]);

  async function loadTemplate(key) {
    setLoading(true); setMsg('');
    try {
      const token = localStorage.getItem('gf_token');
      const res = await fetch(`${API_BASE}/parametres/template/${key}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) { setContent(await res.text()); setDirty(false); }
      else { setContent(''); setDirty(false); }
    } catch (e) { setContent(''); setDirty(false); }
    finally { setLoading(false); }
  }

  async function saveTemplate() {
    setSaving(true); setMsg('');
    try {
      const token = localStorage.getItem('gf_token');
      const res = await fetch(`${API_BASE}/parametres/template/${selected}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'text/html', Authorization: `Bearer ${token}` },
        body: content,
      });
      if (res.ok) { setMsg('✓ Template sauvegardé'); setDirty(false); }
      else setMsg('❌ Erreur lors de la sauvegarde');
    } catch (e) { setMsg(`❌ ${e.message}`); }
    finally { setSaving(false); }
  }

  function exportTemplate() {
    if (!content) return;
    const blob = new Blob([content], { type: 'text/html;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = (tpl?.label || selected).replace(/\s+/g, '_').toLowerCase() + '.html';
    a.click();
    URL.revokeObjectURL(a.href);
  }

  async function resetTemplate() {
    if (!window.confirm('Remettre le template par défaut ? Vos modifications seront perdues.')) return;
    try {
      const token = localStorage.getItem('gf_token');
      const res = await fetch(`${API_BASE}/parametres/template/${selected}/reset`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) { loadTemplate(selected); setMsg('✓ Template réinitialisé'); }
    } catch (e) { setMsg(`❌ ${e.message}`); }
  }

  function handleImport(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => { setContent(ev.target.result); setDirty(true); setMsg('✓ Fichier importé — pensez à sauvegarder'); };
    reader.readAsText(file, 'utf-8');
    e.target.value = '';
  }

  const sectionDefs = selected === 'convention' ? CONV_SECTIONS : selected === 'contrat' ? CONTRAT_SECTIONS : [];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '1rem', height: 'calc(100vh - 200px)', minHeight: '500px' }}>
      {/* Sidebar templates */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
        <button
          onClick={() => switchTemplate('docs_communs')}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.6rem 0.8rem', borderRadius: '8px', border: 'none',
            background: selected === 'docs_communs' ? 'var(--accent-blue)' : 'var(--bg-elevated)',
            color: selected === 'docs_communs' ? '#fff' : 'var(--text-primary)',
            cursor: 'pointer', fontSize: '0.9rem', fontWeight: selected === 'docs_communs' ? 600 : 400,
            textAlign: 'left',
          }}>
          <span>📎</span> Docs communs
        </button>
        <div style={{ borderTop: '1px solid var(--border)', margin: '0.2rem 0' }} />
        {TEMPLATES.map(t => (
          <button key={t.key}
            onClick={() => switchTemplate(t.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              padding: '0.6rem 0.8rem', borderRadius: '8px', border: 'none',
              background: selected === t.key ? 'var(--accent-blue)' : 'var(--bg-elevated)',
              color: selected === t.key ? '#fff' : 'var(--text-primary)',
              cursor: 'pointer', fontSize: '0.9rem', fontWeight: selected === t.key ? 600 : 400,
              textAlign: 'left',
            }}>
            <span>{t.icon}</span> {t.label}
          </button>
        ))}
        {/* Séparateur + section Fiche Progrès */}
        <div style={{ borderTop: '1px solid var(--border)', margin: '0.4rem 0' }} />
        <button
          onClick={() => switchTemplate('fiche_progres')}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.6rem 0.8rem', borderRadius: '8px', border: 'none',
            background: selected === 'fiche_progres' ? 'var(--accent-blue)' : 'var(--bg-elevated)',
            color: selected === 'fiche_progres' ? '#fff' : 'var(--text-primary)',
            cursor: 'pointer', fontSize: '0.9rem', fontWeight: selected === 'fiche_progres' ? 600 : 400,
            textAlign: 'left',
          }}>
          <span>📝</span> Fiche Progrès
        </button>
        <div style={{ marginTop: '1rem', padding: '0.8rem', background: 'var(--bg-elevated)', borderRadius: '8px', fontSize: '0.78rem', color: 'var(--text-tertiary)', lineHeight: 1.5 }}>
          <strong style={{ color: 'var(--text-secondary)' }}>Syntaxe</strong><br />
          <code style={{ color: 'var(--accent-blue)' }}>{'{{variable}}'}</code> — valeur<br />
          <code style={{ color: 'var(--accent-blue)' }}>{'{{#if v}}...{{/if}}'}</code> — condition<br />
          <code style={{ color: 'var(--accent-blue)' }}>{'{{p.cle}}'}</code> — paramètre<br />
          <code style={{ color: 'var(--accent-blue)' }}>{'{{devis.xxx}}'}</code> — champ devis<br />
          <code style={{ color: 'var(--accent-blue)' }}>{'{{client.xxx}}'}</code> — champ client
        </div>
      </div>

      {/* Contenu principal : Docs communs, Fiche Progrès ou éditeur template */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', minWidth: 0, overflow: 'hidden' }}>
        {selected === 'docs_communs' ? (
          <div style={{ overflowY: 'auto', flex: 1 }}>
            <OngletDocsCommuns />
          </div>
        ) : selected === 'fiche_progres' ? (
          <div style={{ overflowY: 'auto', flex: 1 }}>
            <OngletFicheProgres />
          </div>
        ) : (<>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.9rem' }}>
            {tpl?.icon} Template {tpl?.label}
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Modes de vue */}
            <button
              className={activeMode === 'editor' ? 'btn-primary' : 'btn-secondary'}
              onClick={() => setActiveMode('editor')}
              style={{ fontSize: '0.8rem', padding: '0.3rem 0.7rem' }}>
              ✏ Éditeur
            </button>
            <button
              className={activeMode === 'preview' ? 'btn-primary' : 'btn-secondary'}
              onClick={() => setActiveMode('preview')}
              style={{ fontSize: '0.8rem', padding: '0.3rem 0.7rem' }}>
              👁 Aperçu
            </button>
            {hasSections && (
              <button
                className={activeMode === 'sections' ? 'btn-primary' : 'btn-secondary'}
                onClick={() => setActiveMode('sections')}
                style={{ fontSize: '0.8rem', padding: '0.3rem 0.7rem' }}>
                ✎ Sections
              </button>
            )}
            {/* Séparateur */}
            <span style={{ width: 1, height: 20, background: 'var(--border)', display: 'inline-block', margin: '0 0.2rem' }} />
            {/* Actions fichier — toujours visibles */}
            <button className="btn-secondary" onClick={exportTemplate} disabled={!content} style={{ fontSize: '0.8rem', padding: '0.3rem 0.7rem', color: '#16a34a', borderColor: '#16a34a' }}>
              📤 Exporter HTML
            </button>
            <button className="btn-secondary" onClick={() => fileRef.current.click()} style={{ fontSize: '0.8rem', padding: '0.3rem 0.7rem' }}>
              📂 Importer HTML
            </button>
            <button className="btn-secondary" onClick={resetTemplate} style={{ fontSize: '0.8rem', padding: '0.3rem 0.7rem', color: '#f59e0b', borderColor: '#f59e0b' }}>
              ↺ Défaut
            </button>
            {/* Sauvegarder — uniquement en mode éditeur ou sections */}
            {(activeMode === 'editor' || activeMode === 'sections') && (
              <button className="btn-primary" onClick={saveTemplate} disabled={saving} style={{ fontSize: '0.8rem', padding: '0.3rem 0.7rem' }}>
                {saving ? '⏳' : '✓ Sauvegarder'}
              </button>
            )}
          </div>
          <input ref={fileRef} type="file" accept=".html" style={{ display: 'none' }} onChange={handleImport} />
        </div>

        {msg && (
          <div style={{ padding: '0.4rem 0.7rem', borderRadius: '6px', fontSize: '0.82rem',
            ...styleMessage(niveauMessage(msg)) }}>
            {msg}
          </div>
        )}

        {/* Bandeau info pour la feuille d'émargement (CSS uniquement) */}
        {tpl?.styleOnly && (
          <div style={{
            padding: '0.6rem 0.9rem', borderRadius: '8px', fontSize: '0.82rem',
            background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.25)',
            color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: '0.2rem',
          }}>
            <strong style={{ color: 'var(--accent-blue)' }}>ℹ️ Template CSS uniquement</strong><br />
            Seul le bloc <code style={{ color: 'var(--accent-blue)' }}>&lt;style&gt;…&lt;/style&gt;</code> est utilisé.
            Modifiez les <strong>variables CSS</strong> (<code>:root</code>) pour changer couleurs et dimensions.
            Le corps HTML (structure, colonnes, signatures) est toujours généré automatiquement.
          </div>
        )}

        {activeMode === 'sections' && hasSections ? (
          <SectionsPanel sectionDefs={sectionDefs} params={params} onParamChange={onParamChange} />
        ) : loading ? <Loading /> : activeMode === 'preview' ? (
          <div style={{ flex: 1, width: '100%', overflow: 'auto', background: '#f0f0f0', borderRadius: '8px', padding: '1rem', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', justifyContent: 'center', minWidth: '210mm' }}>
              <iframe
                srcDoc={renderPreviewHtml(content)}
                title="Aperçu template"
                style={{ background: '#fff', width: '210mm', minWidth: '210mm', minHeight: '297mm', boxShadow: '0 2px 10px rgba(0,0,0,0.2)', border: 'none', flexShrink: 0 }}
              />
            </div>
          </div>
        ) : (
          <textarea
            value={content}
            onChange={e => { setContent(e.target.value); setDirty(true); }}
            spellCheck={false}
            style={{
              flex: 1, width: '100%', minHeight: '500px',
              padding: '0.8rem', borderRadius: '8px',
              border: '1px solid var(--border)', background: 'var(--bg-elevated)',
              color: 'var(--text-primary)', fontSize: '0.82rem',
              fontFamily: 'Consolas, "Courier New", monospace',
              lineHeight: 1.5, resize: 'vertical', boxSizing: 'border-box',
            }}
          />
        )}
        </>)}
      </div>
    </div>
  );
}

// ─── Onglet Utilisateurs ─────────────────────────────────────────

function OngletUtilisateurs() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [formateurs, setFormateurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'nouveau' | user
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [resetModal, setResetModal] = useState(null); // null | { user, tempPwd }
  const [resetting, setResetting] = useState(false);
  const [mfaAction, setMfaAction] = useState(null); // null | { user, loading }
  const { token: authToken } = useAuth();

  const ROLE_COLORS = { admin: '#f59e0b', utilisateur: '#3b82f6', formateur: '#8b5cf6' };

  useEffect(() => {
    loadUsers();
    apiFetch('/roles').then(r => setRoles(r.data || [])).catch(() => {});
    apiFetch('/formateurs').then(r => setFormateurs(r.data || r || [])).catch(() => {});
  }, []);

  async function loadUsers() {
    setLoading(true);
    try {
      const r = await apiFetch('/utilisateurs');
      setUsers(r.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  function openNouveau() {
    setForm({ login: '', password: '', nom: '', prenom: '', email: '', role: 'utilisateur', actif: true });
    setModal('nouveau');
    setMsg('');
  }

  function openEdit(user) {
    setForm({ ...user, password: '' });
    setModal(user);
    setMsg('');
  }

  async function handleSave() {
    setSaving(true); setMsg('');
    try {
      if (modal === 'nouveau') {
        await apiFetch('/utilisateurs', { method: 'POST', body: JSON.stringify(form) });
      } else {
        const body = { ...form };
        if (!body.password) delete body.password;
        await apiFetch(`/utilisateurs/${modal.id}`, { method: 'PUT', body: JSON.stringify(body) });
      }
      setModal(null);
      loadUsers();
    } catch (e) { setMsg(`❌ ${e.message}`); }
    finally { setSaving(false); }
  }

  async function handleMfaDisable(u, mode) {
    setMfaAction({ user: u, loading: true });
    try {
      const body = mode === 'bypass' ? { bypass: true } : {};
      await apiFetch(`/auth/2fa/disable/${u.id}`, { method: 'POST', body: JSON.stringify(body) });
      await loadUsers();
    } catch (e) { alert(`Erreur : ${e.message}`); }
    finally { setMfaAction(null); }
  }

  async function handleMfaEnable(u) {
    setMfaAction({ user: u, loading: true });
    try {
      await apiFetch(`/auth/2fa/enable/${u.id}`, { method: 'POST', body: JSON.stringify({}) });
      await loadUsers();
    } catch (e) { alert(`Erreur : ${e.message}`); }
    finally { setMfaAction(null); }
  }

  async function handleToggleActif(user) {
    try {
      await apiFetch(`/utilisateurs/${user.id}`, {
        method: 'PUT',
        body: JSON.stringify({ ...user, actif: !user.actif }),
      });
      loadUsers();
    } catch (e) { console.error(e); }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{users.length} utilisateur(s)</p>
        <button className="btn-primary" onClick={openNouveau}>+ Nouvel utilisateur</button>
      </div>

      {loading ? <Loading /> : (
        <table className="data-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Login</th><th>Nom</th><th>Email</th><th>Rôle</th><th>Formateur lié</th><th>Statut</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id}>
                <td><strong>{u.login}</strong></td>
                <td>{u.prenom} {u.nom}</td>
                <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{u.email || '—'}</td>
                <td>
                  <span style={{ padding: '0.15rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 700, background: `${(ROLE_COLORS[u.role] || '#6b7280')}22`, color: ROLE_COLORS[u.role] || '#6b7280' }}>
                    {u.role}
                  </span>
                </td>
                <td style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                  {u.formateur_id ? <span style={{ fontFamily: 'monospace', background: '#f1f5f9', padding: '0.1rem 0.4rem', borderRadius: 4 }}>{u.formateur_id}</span> : '—'}
                </td>
                <td>
                  <span style={{ color: u.actif ? 'var(--accent-emerald)' : '#ef4444', fontSize: '0.8rem', fontWeight: 600 }}>
                    {u.actif ? '● Actif' : '○ Inactif'}
                  </span>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                    <button className="btn-secondary" onClick={() => openEdit(u)} style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem' }}>✏</button>
                    <button className="btn-secondary" onClick={() => handleToggleActif(u)}
                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem', color: u.actif ? '#ef4444' : 'var(--accent-emerald)', borderColor: u.actif ? '#ef4444' : 'var(--accent-emerald)' }}>
                      {u.actif ? 'Désactiver' : 'Réactiver'}
                    </button>
                    <button className="btn-secondary" onClick={() => setResetModal({ user: u, tempPwd: null })}
                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem' }} title="Réinitialiser le mot de passe">
                      🔑
                    </button>
                    {u.totp_bypass ? (
                      <button className="btn-secondary" onClick={() => handleMfaEnable(u)}
                        disabled={mfaAction?.user?.id === u.id}
                        style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem', color: '#f59e0b', borderColor: '#f59e0b' }}
                        title="2FA désactivée (bypass admin) — cliquer pour réactiver">
                        2FA off
                      </button>
                    ) : u.totp_enabled ? (
                      <button className="btn-secondary" onClick={() => handleMfaDisable(u, 'reset')}
                        disabled={mfaAction?.user?.id === u.id}
                        style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem', color: '#10b981', borderColor: '#10b981' }}
                        title="2FA active — cliquer pour réinitialiser (l'utilisateur devra reconfigurer)">
                        2FA ✓
                      </button>
                    ) : (
                      <button className="btn-secondary" onClick={() => handleMfaDisable(u, 'bypass')}
                        disabled={mfaAction?.user?.id === u.id}
                        style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem', color: '#94a3b8', borderColor: '#94a3b8' }}
                        title="2FA non configurée — cliquer pour exempter cet utilisateur">
                        2FA —
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Modal réinitialisation mot de passe */}
      {resetModal !== null && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '2rem', width: '400px', maxWidth: '95vw' }}>
            <h3 style={{ marginBottom: '0.5rem', color: 'var(--text-primary)' }}>🔑 Réinitialiser le mot de passe</h3>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1.2rem' }}>
              Compte : <strong>{resetModal.user.login}</strong> ({resetModal.user.prenom} {resetModal.user.nom})
            </p>
            {resetModal.tempPwd ? (
              <>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.6rem' }}>
                  Mot de passe temporaire généré — communiquez-le à l'utilisateur :
                </p>
                <div style={{ background: 'var(--bg-elevated)', border: '2px solid var(--accent-blue)', borderRadius: '8px', padding: '0.8rem 1.2rem', fontFamily: 'monospace', fontSize: '1.1rem', fontWeight: 700, letterSpacing: '0.1em', color: 'var(--accent-blue)', marginBottom: '1.2rem', userSelect: 'all', textAlign: 'center' }}>
                  {resetModal.tempPwd}
                </div>
                <p style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', marginBottom: '1.2rem' }}>
                  L'utilisateur devra changer ce mot de passe dans Paramètres › Mon compte.
                </p>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button className="btn-primary" onClick={() => setResetModal(null)}>Fermer</button>
                </div>
              </>
            ) : (
              <>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
                  Un nouveau mot de passe aléatoire sera généré et affiché ici. L'ancien mot de passe sera immédiatement invalidé.
                </p>
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                  <button className="btn-secondary" onClick={() => setResetModal(null)} disabled={resetting}>Annuler</button>
                  <button className="btn-primary" disabled={resetting} onClick={async () => {
                    setResetting(true);
                    try {
                      const d = await apiFetch(`/utilisateurs/${resetModal.user.id}/reset-password`, { method: 'POST' });
                      setResetModal(m => ({ ...m, tempPwd: d.tempPassword }));
                    } catch (e) { alert('Erreur : ' + e.message); }
                    finally { setResetting(false); }
                  }}>{resetting ? 'Génération...' : 'Générer un mot de passe'}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal */}
      {modal !== null && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '2rem', width: '480px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ marginBottom: '1.2rem', color: 'var(--text-primary)' }}>
              {modal === 'nouveau' ? '+ Nouvel utilisateur' : `Modifier — ${modal.login}`}
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
              <Field label="Prénom"><input type="text" value={form.prenom || ''} onChange={e => setForm(f => ({ ...f, prenom: e.target.value }))} style={inputStyle} /></Field>
              <Field label="Nom"><input type="text" value={form.nom || ''} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))} style={inputStyle} /></Field>
            </div>
            <Field label="Login"><input type="text" value={form.login || ''} onChange={e => setForm(f => ({ ...f, login: e.target.value }))} style={inputStyle} /></Field>
            <Field label={modal === 'nouveau' ? 'Mot de passe' : 'Nouveau mot de passe (laisser vide = inchangé)'}>
              <input type="password" value={form.password || ''} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} style={inputStyle} placeholder={modal !== 'nouveau' ? '••••••••' : ''} />
            </Field>
            <Field label="Email"><input type="email" value={form.email || ''} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} style={inputStyle} /></Field>
            <Field label="Rôle">
              <select value={form.role || 'utilisateur'} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} style={inputStyle}>
                {roles.map(r => <option key={r.nom} value={r.nom}>{r.nom.charAt(0).toUpperCase() + r.nom.slice(1)}{r.description ? ` — ${r.description}` : ''}</option>)}
              </select>
            </Field>

            {/* Lien formateur (seulement si rôle formateur) */}
            {form.role === 'formateur' && (
              <Field label="Profil formateur lié">
                <select value={form.formateur_id || ''} onChange={e => setForm(f => ({ ...f, formateur_id: e.target.value || null }))} style={inputStyle}>
                  <option value="">— Aucun —</option>
                  {formateurs.map(f => (
                    <option key={f.formateur_id} value={f.formateur_id}>
                      {f.nom_formateur} ({f.formateur_id})
                    </option>
                  ))}
                </select>
              </Field>
            )}

            {modal !== 'nouveau' && (
              <Field label="Statut">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.actif ?? true} onChange={e => setForm(f => ({ ...f, actif: e.target.checked }))} />
                  <span style={{ fontSize: '0.9rem' }}>Compte actif</span>
                </label>
              </Field>
            )}

            {msg && <div style={{ padding: '0.4rem 0.7rem', borderRadius: '6px', fontSize: '0.82rem', ...styleMessage(false), marginBottom: '0.8rem' }}>{msg}</div>}

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button className="btn-secondary" onClick={() => setModal(null)}>Annuler</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving}>{saving ? '⏳...' : '✓ Enregistrer'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Configuration des vues disponibles (pour les rôles) ──────────

const VUES_CONFIG = [
  { key: 'dashboard',      label: 'Tableau de bord',  onglets: [] },
  { key: 'sessions',       label: 'Sessions',          onglets: [
    { key: 'groupes',              label: 'Groupes' },
    { key: 'inscrits',             label: 'Inscrits' },
    { key: 'documents_clients',    label: 'Documents clients' },
    { key: 'documents_formateurs', label: 'Documents formateurs' },
    { key: 'presences',            label: 'Présences' },
    { key: 'documents_stagiaires', label: 'Documents stagiaires' },
    { key: 'questionnaires',       label: 'Questionnaires' },
    { key: 'resultats',            label: 'Résultats' },
  ]},
  { key: 'calendrier',     label: 'Planning',          onglets: [] },
  { key: 'formations',     label: 'Formations',        onglets: [
    { key: 'informations', label: 'Informations' },
    { key: 'documents',    label: 'Documents' },
  ]},
  { key: 'clients',        label: 'Clients',           onglets: [
    { key: 'sessions',  label: 'Sessions' },
    { key: 'devis',     label: 'Devis' },
    { key: 'contacts',  label: 'Contacts' },
  ]},
  { key: 'devis',          label: 'Devis',             onglets: [] },
  { key: 'formateurs',     label: 'Formateurs',        onglets: [
    { key: 'informations', label: 'Informations' },
    { key: 'tarifs',       label: 'Tarifs' },
    { key: 'documents',    label: 'Documents' },
  ]},
  { key: 'stagiaires',     label: 'Stagiaires',        onglets: [
    { key: 'informations', label: 'Informations' },
    { key: 'sessions',     label: 'Sessions' },
    { key: 'documents',    label: 'Documents' },
  ]},
  { key: 'prefacturation', label: 'Préfacturation',    onglets: [] },
  { key: 'crm',            label: 'CRM',               onglets: [] },
  { key: 'parametres',     label: 'Paramètres',        onglets: [
    { key: 'societe',        label: 'Société' },
    { key: 'regles',         label: 'Règles' },
    { key: 'documents',      label: 'Documents' },
    { key: 'questionnaires', label: 'Questionnaires' },
    { key: 'messages',       label: 'Messages' },
  ]},
];

function PermissionsEditor({ value, onChange }) {
  const perms = value || {};
  const [expanded, setExpanded] = useState({});
  const accesComplet = '*' in perms;

  const modeBtn = (active, color) => ({
    padding: '0.15rem 0.5rem', borderRadius: '4px', fontSize: '0.72rem', fontWeight: 600,
    cursor: 'pointer', border: `1px solid ${active ? color : 'var(--border)'}`,
    background: active ? `${color}22` : 'transparent',
    color: active ? color : 'var(--text-secondary)',
    transition: 'all 0.15s',
  });

  function setAccesComplet(mode) {
    if (mode) {
      // Activer accès complet
      onChange({ '*': { mode: mode } });
    } else {
      // Désactiver accès complet → garder les vues déjà configurées (sans *)
      const next = { ...perms };
      delete next['*'];
      onChange(next);
    }
  }

  function toggleVue(vueKey) {
    const next = { ...perms };
    if (next[vueKey]) { delete next[vueKey]; }
    else { next[vueKey] = { mode: 'lecture', onglets: {} }; }
    onChange(next);
  }

  function setVueMode(vueKey, mode) {
    onChange({ ...perms, [vueKey]: { ...(perms[vueKey] || {}), mode } });
  }

  function setOngletMode(vueKey, ongletKey, mode) {
    const vueConfig = perms[vueKey] || { mode: 'lecture', onglets: {} };
    const onglets = { ...(vueConfig.onglets || {}) };
    if (mode === 'idem') { delete onglets[ongletKey]; }
    else { onglets[ongletKey] = mode; }
    onChange({ ...perms, [vueKey]: { ...vueConfig, onglets } });
  }

  return (
    <div>
      {/* Option accès complet */}
      <div style={{ border: '1px solid var(--border)', borderRadius: '8px', padding: '0.7rem 0.8rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.7rem', background: accesComplet ? 'rgba(59,130,246,0.05)' : 'transparent' }}>
        <input type="checkbox" id="acces-complet" checked={accesComplet}
          onChange={e => setAccesComplet(e.target.checked ? (perms['*']?.mode || 'modification') : false)} />
        <label htmlFor="acces-complet" style={{ flex: 1, fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer' }}>
          Toutes les vues
        </label>
        {accesComplet && (
          <div style={{ display: 'flex', gap: '0.3rem' }}>
            <button style={modeBtn(perms['*']?.mode === 'lecture', '#64748b')}
              onClick={() => onChange({ '*': { mode: 'lecture' } })}>👁 Lecture</button>
            <button style={modeBtn(perms['*']?.mode === 'modification', '#3b82f6')}
              onClick={() => onChange({ '*': { mode: 'modification' } })}>✏️ Modification</button>
          </div>
        )}
      </div>

      {/* Vues individuelles (si pas accès complet) */}
      {!accesComplet && (
        <div style={{ border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
          {VUES_CONFIG.map((vue, i) => {
            const isChecked = !!perms[vue.key];
            const vueMode = perms[vue.key]?.mode || 'lecture';
            const isExpanded = expanded[vue.key];
            const hasOnglets = vue.onglets.length > 0;
            return (
              <div key={vue.key} style={{ borderBottom: i < VUES_CONFIG.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.8rem', background: isChecked ? 'var(--bg-elevated)' : 'transparent' }}>
                  <input type="checkbox" checked={isChecked} onChange={() => toggleVue(vue.key)} style={{ cursor: 'pointer' }} />
                  <span style={{ flex: 1, fontSize: '0.87rem', fontWeight: isChecked ? 600 : 400, color: isChecked ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                    {vue.label}
                  </span>
                  {isChecked && (
                    <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
                      <button style={modeBtn(vueMode === 'lecture', '#64748b')} onClick={() => setVueMode(vue.key, 'lecture')}>👁 Lecture</button>
                      <button style={modeBtn(vueMode === 'modification', '#3b82f6')} onClick={() => setVueMode(vue.key, 'modification')}>✏️ Modif.</button>
                      {hasOnglets && (
                        <button onClick={() => setExpanded(e => ({ ...e, [vue.key]: !e[vue.key] }))}
                          style={{ padding: '0.15rem 0.4rem', borderRadius: '4px', fontSize: '0.7rem', cursor: 'pointer', border: '1px solid var(--border)', background: isExpanded ? 'var(--bg-hover)' : 'transparent', color: 'var(--text-secondary)' }}>
                          {isExpanded ? '▼' : '►'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {isChecked && isExpanded && hasOnglets && (
                  <div style={{ background: 'var(--bg-card)', borderTop: '1px solid var(--border)' }}>
                    {vue.onglets.map(onglet => {
                      const ongletMode = perms[vue.key]?.onglets?.[onglet.key] || 'idem';
                      return (
                        <div key={onglet.key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 0.8rem 0.35rem 2rem', borderBottom: '1px solid var(--border)' }}>
                          <span style={{ flex: 1, fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{onglet.label}</span>
                          <div style={{ display: 'flex', gap: '0.25rem' }}>
                            <button style={modeBtn(ongletMode === 'idem', '#94a3b8')} onClick={() => setOngletMode(vue.key, onglet.key, 'idem')}>↑ Idem</button>
                            <button style={modeBtn(ongletMode === 'aucun', '#ef4444')} onClick={() => setOngletMode(vue.key, onglet.key, 'aucun')}>🚫</button>
                            <button style={modeBtn(ongletMode === 'lecture', '#64748b')} onClick={() => setOngletMode(vue.key, onglet.key, 'lecture')}>👁</button>
                            <button style={modeBtn(ongletMode === 'modification', '#3b82f6')} onClick={() => setOngletMode(vue.key, onglet.key, 'modification')}>✏️</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Onglet Rôles ────────────────────────────────────────────────

function OngletRoles() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'new' | role
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => { loadRoles(); }, []);

  async function loadRoles() {
    setLoading(true);
    try { const r = await apiFetch('/roles'); setRoles(r.data || []); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  function openNew() {
    setForm({ nom: '', description: '', permissions: {} });
    setModal('new'); setMsg('');
  }

  function openEdit(role) {
    setForm({ ...role, permissions: role.permissions || {} });
    setModal(role); setMsg('');
  }

  async function handleSave() {
    setSaving(true); setMsg('');
    try {
      if (modal === 'new') {
        await apiFetch('/roles', { method: 'POST', body: JSON.stringify(form) });
      } else {
        await apiFetch(`/roles/${modal.id}`, { method: 'PUT', body: JSON.stringify(form) });
      }
      setModal(null);
      loadRoles();
    } catch (e) { setMsg(`❌ ${e.message}`); }
    finally { setSaving(false); }
  }

  async function handleDelete(role) {
    if (!window.confirm(`Supprimer le rôle "${role.nom}" ?`)) return;
    try {
      await apiFetch(`/roles/${role.id}`, { method: 'DELETE' });
      loadRoles();
    } catch (e) { alert(e.message); }
  }

  const isAdmin = (r) => r.nom === 'admin';

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
          Définissez les rôles et leurs droits d'accès. Chaque utilisateur est associé à un rôle.
        </p>
        <button className="btn-primary" onClick={openNew}>+ Nouveau rôle</button>
      </div>

      {loading ? <Loading /> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          {roles.map(role => (
            <div key={role.id} style={{ border: '1px solid var(--border)', borderRadius: '8px', padding: '0.8rem 1rem', display: 'flex', alignItems: 'center', gap: '1rem', background: 'var(--bg-elevated)' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <strong style={{ color: isAdmin(role) ? '#f59e0b' : 'var(--text-primary)' }}>
                    {role.nom.charAt(0).toUpperCase() + role.nom.slice(1)}
                  </strong>
                  {role.systeme && <span style={{ fontSize: '0.7rem', padding: '0.1rem 0.4rem', borderRadius: '4px', background: 'rgba(245,158,11,0.15)', color: '#f59e0b' }}>système</span>}
                </div>
                {role.description && <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: '0.1rem 0 0' }}>{role.description}</p>}
              </div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                {isAdmin(role) ? '🔓 Accès total' :
                  '*' in (role.permissions || {}) ? `🌐 Toutes vues — ${role.permissions['*'].mode}` :
                  `${Object.keys(role.permissions || {}).length} vue(s)`}
              </div>
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <button className="btn-secondary" onClick={() => openEdit(role)} style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem' }}>✏️ Modifier</button>
                {!role.systeme && (
                  <button className="btn-secondary" onClick={() => handleDelete(role)}
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem', color: '#ef4444', borderColor: '#ef4444' }}>
                    🗑 Supprimer
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal création / édition */}
      {modal !== null && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '2rem', width: '580px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ marginBottom: '1.2rem', color: 'var(--text-primary)' }}>
              {modal === 'new' ? 'Nouveau rôle' : `Modifier — ${modal.nom}`}
            </h3>

            {modal !== 'new' && modal?.nom === 'admin' ? (
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '1rem' }}>
                Le rôle admin est un rôle système avec accès total. Seule la description est modifiable.
              </p>
            ) : null}

            <Field label="Nom du rôle *">
              <input type="text" value={form.nom || ''} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))}
                readOnly={modal !== 'new' && modal?.nom === 'admin'}
                placeholder="ex : formateur, gestionnaire..." style={inputStyle} />
            </Field>
            <Field label="Description">
              <input type="text" value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Courte description du rôle" style={inputStyle} />
            </Field>

            {!(modal !== 'new' && modal?.nom === 'admin') && (
              <Field label="Droits d'accès"
                hint="Définissez les vues accessibles et les droits de modification pour ce rôle.">
                <div style={{ marginTop: '0.4rem' }}>
                  <PermissionsEditor
                    value={form.permissions}
                    onChange={p => setForm(f => ({ ...f, permissions: p }))}
                  />
                </div>
              </Field>
            )}

            {msg && <div style={{ padding: '0.4rem 0.7rem', borderRadius: '6px', fontSize: '0.82rem', ...styleMessage(false), marginBottom: '0.8rem' }}>{msg}</div>}

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
              <button className="btn-secondary" onClick={() => setModal(null)}>Annuler</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? '⏳...' : '✓ Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Onglet Règles métier ─────────────────────────────────────────

function OngletRegles({ params, onChange, onSave, saving }) {
  function NumField({ label, hint, paramKey, min, max, unite }) {
    return (
      <Field label={label} hint={hint}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="number" min={min || 1} max={max || 365}
            value={params[paramKey] || ''}
            onChange={e => onChange(paramKey, e.target.value)}
            style={{ ...inputStyle, width: '100px' }}
          />
          <span style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>{unite || 'jours'}</span>
        </div>
      </Field>
    );
  }

  function ToggleRelance({ docLabel, keyActif, keyJours, keyNbMax }) {
    const actif = params[keyActif] === '1';
    return (
      <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '10px', padding: '1rem 1.2rem', marginBottom: '0.8rem' }}>
        {/* Ligne titre + toggle */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: actif ? '0.9rem' : 0 }}>
          <div>
            <span style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-primary)' }}>{docLabel}</span>
            {!actif && <span style={{ marginLeft: '0.6rem', fontSize: '0.75rem', color: '#94a3b8' }}>désactivé</span>}
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', userSelect: 'none' }}>
            <span style={{ fontSize: '0.82rem', color: actif ? 'var(--accent-emerald)' : '#94a3b8', fontWeight: 600 }}>
              {actif ? 'Activé' : 'Désactivé'}
            </span>
            <div onClick={() => onChange(keyActif, actif ? '0' : '1')}
              style={{ width: 40, height: 22, borderRadius: 11, background: actif ? 'var(--accent-emerald)' : '#cbd5e1',
                position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0 }}>
              <div style={{ position: 'absolute', top: 3, left: actif ? 21 : 3, width: 16, height: 16,
                borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
            </div>
          </label>
        </div>
        {/* Options visibles uniquement si activé */}
        {actif && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8rem' }}>
            <Field label="Délai avant relance" hint="Nombre de jours sans réponse avant envoi automatique">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <input type="number" min={1} max={90} value={params[keyJours] || ''}
                  onChange={e => onChange(keyJours, e.target.value)}
                  style={{ ...inputStyle, width: '80px' }} />
                <span style={{ color: 'var(--text-tertiary)', fontSize: '0.82rem' }}>jours</span>
              </div>
            </Field>
            <Field label="Nb max de relances" hint="Nombre maximum de relances automatiques envoyées">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <input type="number" min={1} max={10} value={params[keyNbMax] || ''}
                  onChange={e => onChange(keyNbMax, e.target.value)}
                  style={{ ...inputStyle, width: '80px' }} />
                <span style={{ color: 'var(--text-tertiary)', fontSize: '0.82rem' }}>envois</span>
              </div>
            </Field>
          </div>
        )}
      </div>
    );
  }

  const catHeader = (icon, label) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '1.8rem', marginBottom: '0.6rem', paddingBottom: '0.4rem', borderBottom: '2px solid var(--border)' }}>
      <span style={{ fontSize: '1rem' }}>{icon}</span>
      <span style={{ fontWeight: 700, fontSize: '0.8rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>{label}</span>
    </div>
  );

  return (
    <div style={{ maxWidth: '640px' }}>

      {catHeader('📄', 'Documents produits')}

      <SectionRepliable variante="titre" title="Devis">
        <Field label="Responsable dossiers (affiché sur les devis)">
          <input type="text" value={params.devis_responsable || ''} onChange={e => onChange('devis_responsable', e.target.value)} style={inputStyle} />
        </Field>
        <Field label="Taux TVA (%)">
          <input type="text" value={params.devis_tva_taux || ''} onChange={e => onChange('devis_tva_taux', e.target.value)} style={inputStyle} />
        </Field>
        <NumField
          label="Validité devis (jours)"
          hint="Nombre de jours avant expiration d'un devis (utilisé pour calculer la date de validité lors de la création)"
          paramKey="devis_validite_jours"
        />
        <Field label="Délai d'accès formations">
          <input type="text" value={params.devis_delai_acces || ''} onChange={e => onChange('devis_delai_acces', e.target.value)} style={inputStyle} placeholder="15 jours en moyenne" />
        </Field>
        <Field label="Mention bas de page devis">
          <textarea value={params.devis_mention || ''} onChange={e => onChange('devis_mention', e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
        </Field>
        <NumField
          label="Durée de validité du lien devis client"
          hint="Nombre de jours pendant lesquels le lien envoyé au client pour signer le devis reste actif"
          paramKey="lien_devis_jours"
        />
      </SectionRepliable>

      <SectionRepliable variante="titre" title="Fiches d'inscription">
        <NumField
          label="Blocage des modifications avant la session"
          hint="Nombre de jours avant le début de la session à partir duquel le client ne peut plus modifier sa fiche d'inscription"
          paramKey="fiche_blocage_jours"
        />
      </SectionRepliable>

      <SectionRepliable variante="titre" title="Conventions & Contrats formateurs">
        <NumField
          label="Durée de validité du lien convention client"
          hint="Nombre de jours pendant lesquels le lien envoyé au client pour signer la convention reste actif"
          paramKey="lien_convention_jours"
        />
        <NumField
          label="Durée de validité du lien commande formateur"
          hint="Nombre de jours pendant lesquels le lien envoyé au formateur pour signer la commande reste actif"
          paramKey="lien_commande_jours"
        />
        <NumField
          label="Durée de validité du lien contrat formateur"
          hint="Nombre de jours pendant lesquels le lien envoyé au formateur pour signer le contrat reste actif"
          paramKey="lien_contrat_jours"
        />
      </SectionRepliable>

      <SectionRepliable variante="titre" title="Certificat de réalisation">
        <Field label="Représentant légal signataire du certificat de réalisation" hint='Si vide, "Philippe TRAMOUILLE" est utilisé par défaut'>
          <input type="text" value={params.certificat_responsable || ''} onChange={e => onChange('certificat_responsable', e.target.value)} style={inputStyle} />
        </Field>
      </SectionRepliable>

      <SectionRepliable variante="titre" title="Envoi des documents">
        <OngletRegleEnvoi params={params} onChange={onChange} />
        <NumField
          label="Regrouper en ZIP au-delà de"
          hint="Documents de fin de formation (émargement, certificats, attestations) : jusqu'à ce nombre de fichiers, ils sont envoyés séparément en pièces jointes ; au-delà, ils sont regroupés dans un seul ZIP"
          paramKey="seuil_zip_documents" min={1} max={100} unite="fichiers"
        />
      </SectionRepliable>

      <SectionRepliable variante="titre" title="Numérotation des documents">
        <OngletNumerotation />
      </SectionRepliable>

      {catHeader('📬', 'Communications')}

      <SectionRepliable variante="titre" title="Relances automatiques">
        <p style={{ fontSize: '0.83rem', color: 'var(--text-secondary)', marginBottom: '1rem', lineHeight: 1.5 }}>
          Les relances sont envoyées automatiquement chaque matin à 08h00 aux destinataires n'ayant pas encore répondu.
          Le programme de formation est joint si disponible.
        </p>
        <ToggleRelance
          docLabel="Devis en attente de validation"
          keyActif="relance_devis_actif"
          keyJours="relance_devis_jours"
          keyNbMax="relance_devis_nb_max"
        />
        <ToggleRelance
          docLabel="Conventions en attente de signature"
          keyActif="relance_convention_actif"
          keyJours="relance_convention_jours"
          keyNbMax="relance_convention_nb_max"
        />
        <ToggleRelance
          docLabel="Fiches d'inscription non complétées"
          keyActif="relance_inscription_actif"
          keyJours="relance_inscription_jours"
          keyNbMax="relance_inscription_nb_max"
        />
        <ToggleRelance
          docLabel="Commandes formateur en attente de signature"
          keyActif="relance_commande_formateur_actif"
          keyJours="relance_commande_formateur_jours"
          keyNbMax="relance_commande_formateur_nb_max"
        />
        {/* Recyclage */}
        {(() => {
          const actif = params.recyclage_relance_actif === '1';
          return (
            <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '10px', padding: '1rem 1.2rem', marginBottom: '0.8rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: actif ? '0.9rem' : 0 }}>
                <div>
                  <span style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-primary)' }}>Recyclages à venir (stagiaires)</span>
                  {!actif && <span style={{ marginLeft: '0.6rem', fontSize: '0.75rem', color: '#94a3b8' }}>désactivé</span>}
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', userSelect: 'none' }}>
                  <span style={{ fontSize: '0.82rem', color: actif ? 'var(--accent-emerald)' : '#94a3b8', fontWeight: 600 }}>{actif ? 'Activé' : 'Désactivé'}</span>
                  <div onClick={() => onChange('recyclage_relance_actif', actif ? '0' : '1')}
                    style={{ width: 40, height: 22, borderRadius: 11, background: actif ? 'var(--accent-emerald)' : '#cbd5e1', position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0 }}>
                    <div style={{ position: 'absolute', top: 3, left: actif ? 21 : 3, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                  </div>
                </label>
              </div>
              {actif && (
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8rem', marginBottom: '0.8rem' }}>
                    <Field label="1ère relance (jours avant échéance)" hint="Ex: 60 = relance 60 jours avant la date d'expiration">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <input type="number" min={1} max={365} value={params.recyclage_relance_j1 || ''}
                          onChange={e => onChange('recyclage_relance_j1', e.target.value)}
                          style={{ ...inputStyle, width: '80px' }} />
                        <span style={{ color: 'var(--text-tertiary)', fontSize: '0.82rem' }}>jours avant</span>
                      </div>
                    </Field>
                    <Field label="2ème relance (jours avant échéance)" hint="Ex: 30 = relance 30 jours avant la date d'expiration">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <input type="number" min={1} max={365} value={params.recyclage_relance_j2 || ''}
                          onChange={e => onChange('recyclage_relance_j2', e.target.value)}
                          style={{ ...inputStyle, width: '80px' }} />
                        <span style={{ color: 'var(--text-tertiary)', fontSize: '0.82rem' }}>jours avant</span>
                      </div>
                    </Field>
                  </div>
                  <Field label="Horizon affiché dans le tableau de bord" hint="Nombre de jours par défaut pour l'affichage des recyclages à venir">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <input type="number" min={30} max={730} value={params.recyclage_dashboard_jours || ''}
                        onChange={e => onChange('recyclage_dashboard_jours', e.target.value)}
                        style={{ ...inputStyle, width: '80px' }} />
                      <span style={{ color: 'var(--text-tertiary)', fontSize: '0.82rem' }}>jours</span>
                    </div>
                  </Field>
                  <p style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', marginTop: '0.6rem' }}>
                    Les emails sont envoyés au stagiaire et au contact client. Le modèle est configurable dans l'onglet Messages (« Relance recyclage »).
                  </p>
                </div>
              )}
            </div>
          );
        })()}
      </SectionRepliable>

      <SectionRepliable variante="titre" title="SMS automatiques">
        <p style={{ fontSize: '0.83rem', color: 'var(--text-secondary)', marginBottom: '1rem', lineHeight: 1.5 }}>
          Les SMS sont envoyés par le serveur sans intervention de l'utilisateur. Requiert une configuration OVH active.
        </p>
        {/* SMS Confirmation */}
        {(() => {
          const actif = params.sms_confirmation_auto_actif === '1';
          return (
            <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '10px', padding: '1rem 1.2rem', marginBottom: '0.8rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: actif ? '0.6rem' : 0 }}>
                <div>
                  <span style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-primary)' }}>Confirmation d'inscription (SMS immédiat)</span>
                  {!actif && <span style={{ marginLeft: '0.6rem', fontSize: '0.75rem', color: '#94a3b8' }}>désactivé</span>}
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', userSelect: 'none' }}>
                  <span style={{ fontSize: '0.82rem', color: actif ? 'var(--accent-emerald)' : '#94a3b8', fontWeight: 600 }}>{actif ? 'Activé' : 'Désactivé'}</span>
                  <div onClick={() => onChange('sms_confirmation_auto_actif', actif ? '0' : '1')}
                    style={{ width: 40, height: 22, borderRadius: 11, background: actif ? 'var(--accent-emerald)' : '#cbd5e1', position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0 }}>
                    <div style={{ position: 'absolute', top: 3, left: actif ? 21 : 3, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                  </div>
                </label>
              </div>
              {actif && (
                <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', margin: 0 }}>
                  Un SMS est envoyé au stagiaire dès qu'il est inscrit manuellement à une session.
                </p>
              )}
            </div>
          );
        })()}
        {/* SMS Rappel */}
        {(() => {
          const actif = params.sms_rappel_auto_actif === '1';
          return (
            <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '10px', padding: '1rem 1.2rem', marginBottom: '0.8rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: actif ? '0.9rem' : 0 }}>
                <div>
                  <span style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-primary)' }}>Rappel de formation (SMS J−N)</span>
                  {!actif && <span style={{ marginLeft: '0.6rem', fontSize: '0.75rem', color: '#94a3b8' }}>désactivé</span>}
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', userSelect: 'none' }}>
                  <span style={{ fontSize: '0.82rem', color: actif ? 'var(--accent-emerald)' : '#94a3b8', fontWeight: 600 }}>{actif ? 'Activé' : 'Désactivé'}</span>
                  <div onClick={() => onChange('sms_rappel_auto_actif', actif ? '0' : '1')}
                    style={{ width: 40, height: 22, borderRadius: 11, background: actif ? 'var(--accent-emerald)' : '#cbd5e1', position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0 }}>
                    <div style={{ position: 'absolute', top: 3, left: actif ? 21 : 3, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                  </div>
                </label>
              </div>
              {actif && (
                <Field label="Jours avant la session" hint="Le SMS est envoyé automatiquement à 08h00 ce nombre de jours avant le début de la session">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <input type="number" min={1} max={30} value={params.sms_rappel_jours || ''}
                      onChange={e => onChange('sms_rappel_jours', e.target.value)}
                      style={{ ...inputStyle, width: '80px' }} />
                    <span style={{ color: 'var(--text-tertiary)', fontSize: '0.82rem' }}>jours</span>
                  </div>
                </Field>
              )}
            </div>
          );
        })()}
        {/* SMS Report */}
        {(() => {
          const actif = params.sms_report_auto_actif === '1';
          return (
            <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '10px', padding: '1rem 1.2rem', marginBottom: '0.8rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <span style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-primary)' }}>Report de session (SMS auto)</span>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.15rem' }}>Envoi automatique à tous les inscrits lors d'un changement de date de début.</div>
                  {!actif && <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>désactivé</span>}
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', userSelect: 'none' }}>
                  <span style={{ fontSize: '0.82rem', color: actif ? 'var(--accent-emerald)' : '#94a3b8', fontWeight: 600 }}>{actif ? 'Activé' : 'Désactivé'}</span>
                  <div onClick={() => onChange('sms_report_auto_actif', actif ? '0' : '1')}
                    style={{ width: 40, height: 22, borderRadius: 11, background: actif ? 'var(--accent-emerald)' : '#cbd5e1', position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0 }}>
                    <div style={{ position: 'absolute', top: 3, left: actif ? 21 : 3, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                  </div>
                </label>
              </div>
            </div>
          );
        })()}
        {/* SMS Changement de session */}
        {(() => {
          const actif = params.sms_changement_session_auto_actif === '1';
          return (
            <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '10px', padding: '1rem 1.2rem', marginBottom: '0.8rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <span style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-primary)' }}>Changement de session (SMS auto)</span>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.15rem' }}>Envoi automatique au stagiaire lors d'un transfert vers une autre session.</div>
                  {!actif && <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>désactivé</span>}
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', userSelect: 'none' }}>
                  <span style={{ fontSize: '0.82rem', color: actif ? 'var(--accent-emerald)' : '#94a3b8', fontWeight: 600 }}>{actif ? 'Activé' : 'Désactivé'}</span>
                  <div onClick={() => onChange('sms_changement_session_auto_actif', actif ? '0' : '1')}
                    style={{ width: 40, height: 22, borderRadius: 11, background: actif ? 'var(--accent-emerald)' : '#cbd5e1', position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0 }}>
                    <div style={{ position: 'absolute', top: 3, left: actif ? 21 : 3, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                  </div>
                </label>
              </div>
            </div>
          );
        })()}
        {/* SMS Annulation inscription */}
        {(() => {
          const actif = params.sms_annulation_auto_actif === '1';
          return (
            <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '10px', padding: '1rem 1.2rem', marginBottom: '0.8rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <span style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-primary)' }}>Annulation d'inscription (SMS auto)</span>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.15rem' }}>Envoi automatique au stagiaire lors de sa désinscription d'une session.</div>
                  {!actif && <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>désactivé</span>}
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', userSelect: 'none' }}>
                  <span style={{ fontSize: '0.82rem', color: actif ? 'var(--accent-emerald)' : '#94a3b8', fontWeight: 600 }}>{actif ? 'Activé' : 'Désactivé'}</span>
                  <div onClick={() => onChange('sms_annulation_auto_actif', actif ? '0' : '1')}
                    style={{ width: 40, height: 22, borderRadius: 11, background: actif ? 'var(--accent-emerald)' : '#cbd5e1', position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0 }}>
                    <div style={{ position: 'absolute', top: 3, left: actif ? 21 : 3, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                  </div>
                </label>
              </div>
            </div>
          );
        })()}
      </SectionRepliable>

      {catHeader('👥', 'Référentiels clients')}

      <SectionRepliable variante="titre" title="Types de client">
        <OngletListeClient
          cle="types_client"
          defauts={['Entreprise', 'Particulier', 'Association', 'Collectivité', 'Autre']}
          description="Ces types apparaissent dans les menus déroulants Type de client (fiches client, création)."
          placeholder="Nouveau type de client…"
          params={params} onChange={onChange}
        />
      </SectionRepliable>

      <SectionRepliable variante="titre" title="Statuts client">
        <OngletListeClient
          cle="statuts_client"
          defauts={['Prospect', 'Client actif', 'Client inactif', 'Partenaire']}
          description="Ces statuts apparaissent dans les menus déroulants Statut client (fiches client, CRM)."
          placeholder="Nouveau statut client…"
          params={params} onChange={onChange}
        />
      </SectionRepliable>

      <SectionRepliable variante="titre" title="Activités clients">
        <OngletActivites />
      </SectionRepliable>

      <SectionRepliable variante="titre" title="Organismes financeurs">
        <OngletOrganismesFinanceurs />
      </SectionRepliable>

      {catHeader('🏫', 'Référentiels formation')}

      <SectionRepliable variante="titre" title="Catégories de formation">
        <OngletCategoriesFormation />
      </SectionRepliable>

      <SectionRepliable variante="titre" title="Salles de formation">
        <OngletSalles />
      </SectionRepliable>

      <SectionRepliable variante="titre" title="Jours de fermeture CEJ">
        <JoursFermetureCEJ params={params} onChange={onChange} />
      </SectionRepliable>

      <SectionRepliable variante="titre" title="Types de questionnaire">
        <TypesQEditor params={params} onChange={onChange} />
      </SectionRepliable>

      <SectionRepliable variante="titre" title="Types de documents formateur">
        <TypesDocumentFormateur params={params} onChange={onChange} />
      </SectionRepliable>

      <SectionRepliable variante="titre" title="Types de documents stagiaire">
        <TypesDocumentStagiaire params={params} onChange={onChange} />
      </SectionRepliable>

      <SectionRepliable variante="titre" title="Types de notes de suivi">
        <NoteTypesManager />
      </SectionRepliable>

      {catHeader('💰', 'Comptabilité Sage')}

      <SectionRepliable variante="titre" title="Comptes comptables">
        <Field label="Compte de vente (HT)" hint="Compte de produit utilisé pour la ligne vente dans l'export Sage (ex : 706000)">
          <input type="text" value={params.compte_vente_sage || ''} onChange={e => onChange('compte_vente_sage', e.target.value)} style={inputStyle} placeholder="706000" />
        </Field>
        <Field label="Compte TVA collectée" hint="Compte de TVA utilisé pour la ligne TVA dans l'export Sage (ex : 445710)">
          <input type="text" value={params.compte_tva_sage || ''} onChange={e => onChange('compte_tva_sage', e.target.value)} style={inputStyle} placeholder="445710" />
        </Field>
      </SectionRepliable>

      {catHeader('📣', 'Réclamations')}

      <SectionRepliable variante="titre" title="Paramètres réclamations">
        <Field label="Délai de réponse (jours ouvrés)" hint="Délai contractuel pour traiter une réclamation. Par défaut : 10 jours.">
          <input type="number" min="1" max="90" value={params.reclamation_delai_jours || '10'} onChange={e => onChange('reclamation_delai_jours', e.target.value)} style={inputStyle} />
        </Field>
        <Field label="Email référent (copie systématique)" hint="Email mis en copie de tous les emails réclamations. Laisser vide si non utilisé.">
          <input type="email" value={params.reclamation_email_referent || ''} onChange={e => onChange('reclamation_email_referent', e.target.value)} style={inputStyle} placeholder="qualite@cejconcept.fr" />
        </Field>
        <Field label="Objets de réclamation prédéfinis" hint='Liste JSON. Exemple : ["Qualité","Organisation","Autre"]'>
          <textarea
            value={params.reclamation_objets || '[]'}
            onChange={e => onChange('reclamation_objets', e.target.value)}
            style={{ ...inputStyle, resize: 'vertical', minHeight: 120, fontFamily: 'monospace', fontSize: '0.82rem' }}
            spellCheck={false}
          />
          {(() => {
            try { JSON.parse(params.reclamation_objets || '[]'); return null; }
            catch { return <div style={{ fontSize: '0.75rem', color: '#dc2626', marginTop: '0.25rem' }}>⚠ JSON invalide</div>; }
          })()}
          {(() => {
            try {
              const items = JSON.parse(params.reclamation_objets || '[]');
              if (!Array.isArray(items) || items.length === 0) return null;
              return (
                <div style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                  {items.map((o, i) => (
                    <span key={i} style={{ fontSize: '0.75rem', padding: '0.15rem 0.5rem', borderRadius: '8px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}>{o}</span>
                  ))}
                </div>
              );
            } catch { return null; }
          })()}
        </Field>
      </SectionRepliable>

      <button className="btn-primary" onClick={onSave} disabled={saving} style={{ marginTop: '1.5rem' }}>
        {saving ? '⏳ Enregistrement...' : '✓ Enregistrer'}
      </button>
    </div>
  );
}

// ─── Règles d'envoi des documents ────────────────────────────────

const DOCS_ENVOI = [
  { key: 'devis',               label: 'Devis' },
  { key: 'fiche_inscription',   label: "Fiche d'inscription" },
  { key: 'convention',          label: 'Convention' },
  { key: 'convocation',         label: 'Convocation' },
  { key: 'desinscription',      label: "Désinscription d'un stagiaire" },
  { key: 'report_session',      label: 'Report de session' },
  { key: 'annulation_session',  label: 'Annulation de session' },
  { key: 'reouverture_session', label: 'Réouverture de session' },
  { key: 'changement_session',  label: 'Changement de session (stagiaire)' },
  { key: 'attestation',         label: 'Certificat / attestation / émargement' },
];

const REGLES_ENVOI_DEFAUT = {
  devis:               { principal: true,  secondaire: false },
  fiche_inscription:   { principal: true,  secondaire: false },
  convention:          { principal: true,  secondaire: false },
  convocation:         { principal: true,  secondaire: false },
  desinscription:      { principal: false, secondaire: false },
  report_session:      { principal: false, secondaire: false },
  annulation_session:  { principal: false, secondaire: false },
  reouverture_session: { principal: false, secondaire: false },
  changement_session:  { principal: false, secondaire: false },
  attestation:         { principal: false, secondaire: false },
};

function OngletRegleEnvoi({ params, onChange }) {
  function parse(raw) {
    try { return { ...REGLES_ENVOI_DEFAUT, ...JSON.parse(raw || '{}') }; } catch { return { ...REGLES_ENVOI_DEFAUT }; }
  }
  const [regles, setRegles] = useState(() => parse(params.regles_envoi));
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => { setRegles(parse(params.regles_envoi)); }, [params.regles_envoi]);

  function toggle(docKey, contact) {
    setRegles(r => ({ ...r, [docKey]: { ...r[docKey], [contact]: !r[docKey]?.[contact] } }));
  }

  async function handleSave() {
    setSaving(true); setMsg('');
    try {
      const val = JSON.stringify(regles);
      await apiFetch('/parametres', { method: 'PUT', body: JSON.stringify({ regles_envoi: val }) });
      onChange('regles_envoi', val);
      setMsg("✓ Règles d'envoi sauvegardées");
      setTimeout(() => setMsg(''), 3000);
    } catch (e) { setMsg('❌ ' + e.message); }
    finally { setSaving(false); }
  }

  const thStyle = { fontSize: '0.74rem', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', textAlign: 'center', padding: '0.5rem 1rem' };
  const tdStyle = { padding: '0.7rem 1rem', textAlign: 'center' };

  return (
    <div>
      <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1.2rem' }}>
        Pour chaque type de document, définissez quel(s) contact(s) client reçoivent l'email. Règle fixe appliquée à tous les clients.
      </p>
      <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
        <thead>
          <tr style={{ background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border)' }}>
            <th style={{ ...thStyle, textAlign: 'left', width: '55%' }}>Document</th>
            <th style={thStyle}>Contact principal</th>
            <th style={thStyle}>Contact secondaire</th>
          </tr>
        </thead>
        <tbody>
          {DOCS_ENVOI.map((doc, i) => (
            <tr key={doc.key} style={{ borderBottom: i < DOCS_ENVOI.length - 1 ? '1px solid var(--border)' : 'none', background: i % 2 === 1 ? 'rgba(0,0,0,0.02)' : 'transparent' }}>
              <td style={{ ...tdStyle, textAlign: 'left' }}>
                <span style={{ fontSize: '0.88rem', color: 'var(--text-primary)', fontWeight: 500 }}>{doc.label}</span>
                {doc.note && (
                  <span style={{ display: 'block', fontSize: '0.74rem', color: '#f59e0b', marginTop: '0.15rem' }}>⚠ {doc.note}</span>
                )}
              </td>
              <td style={tdStyle}>
                <input type="checkbox"
                  checked={regles[doc.key]?.principal ?? false}
                  onChange={() => toggle(doc.key, 'principal')}
                  style={{ width: 16, height: 16, cursor: 'pointer', accentColor: 'var(--accent-blue)' }}
                />
              </td>
              <td style={tdStyle}>
                <input type="checkbox"
                  checked={regles[doc.key]?.secondaire ?? false}
                  onChange={() => toggle(doc.key, 'secondaire')}
                  style={{ width: 16, height: 16, cursor: 'pointer', accentColor: 'var(--accent-blue)' }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {msg && (
        <div style={{ padding: '0.4rem 0.7rem', borderRadius: '6px', fontSize: '0.82rem', marginTop: '0.8rem',
          ...styleMessage(niveauMessage(msg)) }}>
          {msg}
        </div>
      )}
      <button className="btn-primary" onClick={handleSave} disabled={saving} style={{ marginTop: '0.9rem', fontSize: '0.85rem' }}>
        {saving ? '⏳ Enregistrement...' : "✓ Enregistrer les règles d'envoi"}
      </button>
    </div>
  );
}

// ─── Types / Statuts client (liste configurable) ─────────────────

function OngletListeClient({ cle, defauts, description, placeholder, params, onChange }) {
  function parse(raw) {
    if (!raw) return defauts;
    try { return JSON.parse(raw); } catch { return defauts; }
  }
  const raw = params[cle];
  const init = React.useMemo(() => parse(raw), [raw]); // eslint-disable-line react-hooks/exhaustive-deps
  const [items, setItemsLocal] = React.useState(init);
  const [newItem, setNewItem] = React.useState('');

  React.useEffect(() => { setItemsLocal(parse(raw)); }, [raw]); // eslint-disable-line react-hooks/exhaustive-deps

  async function setItems(arr) {
    setItemsLocal(arr);
    onChange(cle, JSON.stringify(arr));
    try { await apiFetch('/parametres', { method: 'PUT', body: JSON.stringify({ [cle]: JSON.stringify(arr) }) }); } catch {}
  }

  function ajouter() {
    const v = newItem.trim();
    if (!v || items.includes(v)) return;
    setItems([...items, v]);
    setNewItem('');
  }

  function supprimer(i) { setItems(items.filter((_, idx) => idx !== i)); }

  function monter(i) {
    if (i === 0) return;
    const arr = [...items]; [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]]; setItems(arr);
  }

  function descendre(i) {
    if (i === items.length - 1) return;
    const arr = [...items]; [arr[i], arr[i + 1]] = [arr[i + 1], arr[i]]; setItems(arr);
  }

  return (
    <div>
      <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: '0.8rem' }}>{description}</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', marginBottom: '0.7rem' }}>
        {items.length === 0 && <p style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>Aucune valeur définie.</p>}
        {items.map((item, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '7px', padding: '0.35rem 0.7rem' }}>
            <span style={{ flex: 1, fontSize: '0.85rem', color: 'var(--text-primary)' }}>{item}</span>
            <button onClick={() => monter(i)} disabled={i === 0} title="Monter"
              style={{ padding: '0.15rem 0.4rem', borderRadius: '4px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: i === 0 ? 'default' : 'pointer', opacity: i === 0 ? 0.3 : 1, fontSize: '0.75rem' }}>▲</button>
            <button onClick={() => descendre(i)} disabled={i === items.length - 1} title="Descendre"
              style={{ padding: '0.15rem 0.4rem', borderRadius: '4px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: i === items.length - 1 ? 'default' : 'pointer', opacity: i === items.length - 1 ? 0.3 : 1, fontSize: '0.75rem' }}>▼</button>
            <button onClick={() => supprimer(i)} title="Supprimer"
              style={{ padding: '0.15rem 0.4rem', borderRadius: '4px', border: 'none', background: 'rgba(239,68,68,0.1)', color: '#ef4444', cursor: 'pointer', fontSize: '0.75rem' }}>✕</button>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '0.4rem' }}>
        <input value={newItem} onChange={e => setNewItem(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && ajouter()}
          placeholder={placeholder}
          style={{ ...inputStyle, flex: 1 }} />
        <button onClick={ajouter}
          style={{ padding: '0.4rem 0.9rem', borderRadius: '7px', border: 'none', background: '#E87722', color: '#fff', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
          + Ajouter
        </button>
      </div>
    </div>
  );
}

// Jours de fermeture propres à CEJ (ponts, fermeture annuelle…) : paramètre « jours_fermeture » = JSON [{ debut, fin, libelle }].
// Ils s'ajoutent aux jours fériés de France (calculés automatiquement) : exclus des jours générés en mode « Consécutives »
// et rappelés (« sauf … ») dans les plages de dates des documents.
function JoursFermetureCEJ({ params, onChange }) {
  const [ajout, setAjout] = useState({ debut: '', fin: '', libelle: '' });
  const [feries, setFeries] = useState([]);
  let liste = [];
  try { liste = JSON.parse(params.jours_fermeture || '[]'); } catch { liste = []; }
  if (!Array.isArray(liste)) liste = [];
  const enregistrer = l => onChange('jours_fermeture', JSON.stringify([...l].sort((a, b) => String(a.debut).localeCompare(String(b.debut)))));
  const fr = iso => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' }) : '');

  useEffect(() => {
    const du = new Date().toISOString().slice(0, 10);
    apiFetch(`/jours-non-travailles?du=${du}&au=${Number(du.slice(0, 4)) + 1}-12-31`)
      .then(r => setFeries((r.jours || []).filter(j => j.type === 'ferie')))
      .catch(() => {});
  }, []);

  function ajouter() {
    if (!ajout.debut) return;
    const fin = ajout.fin && ajout.fin >= ajout.debut ? ajout.fin : '';
    enregistrer([...liste, { debut: ajout.debut, fin, libelle: ajout.libelle.trim() || 'Fermeture CEJ' }]);
    setAjout({ debut: '', fin: '', libelle: '' });
  }
  const champ = { ...inputStyle, width: 'auto' };
  return (
    <div>
      <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', margin: '0 0 0.8rem' }}>
        Les jours fériés de France (1er janvier, lundi de Pâques, 1er mai, 8 mai, Ascension, lundi de Pentecôte, 14 juillet, 15 août, 1er novembre,
        11 novembre, 25 décembre) sont gérés automatiquement. Ajoutez ici les jours où CEJ est fermé : ils ne seront pas proposés
        dans les sessions en mode « Consécutives » et seront rappelés (« sauf … ») dans les dates des documents.
      </p>
      {liste.length === 0 && <p style={{ fontSize: '0.85rem', color: 'var(--text-tertiary)' }}>Aucun jour de fermeture enregistré.</p>}
      {liste.map((f, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.4rem 0.7rem', marginBottom: '0.3rem', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, fontSize: '0.85rem' }}>
          <span style={{ flex: 1 }}>
            <strong>{fr(f.debut)}{f.fin && f.fin !== f.debut ? ` → ${fr(f.fin)}` : ''}</strong> — {f.libelle}
          </span>
          <button type="button" className="btn-secondary" style={{ color: '#dc2626' }} onClick={() => enregistrer(liste.filter((_, k) => k !== i))}>✕ Retirer</button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap', margin: '0.8rem 0' }}>
        <label style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>Du<br /><input type="date" value={ajout.debut} onChange={e => setAjout(a => ({ ...a, debut: e.target.value }))} style={champ} /></label>
        <label style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>Au (facultatif)<br /><input type="date" value={ajout.fin} min={ajout.debut} onChange={e => setAjout(a => ({ ...a, fin: e.target.value }))} style={champ} /></label>
        <label style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', flex: 1, minWidth: 160 }}>Libellé<br /><input type="text" value={ajout.libelle} placeholder="Ex. : Fermeture de fin d'année" onChange={e => setAjout(a => ({ ...a, libelle: e.target.value }))} style={{ ...champ, width: '100%' }} /></label>
        <button type="button" className="btn-primary" disabled={!ajout.debut} onClick={ajouter}>+ Ajouter</button>
      </div>
      <p style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', margin: 0 }}>Pensez à cliquer sur « Enregistrer » en bas de la page.</p>
      {feries.length > 0 && (
        <details style={{ marginTop: '0.8rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
          <summary style={{ cursor: 'pointer' }}>Jours fériés pris en compte ({feries.length} à venir)</summary>
          <div style={{ marginTop: '0.4rem', columns: 2, columnGap: '1.5rem' }}>
            {feries.map(j => <div key={j.date}>{fr(j.date)} — {j.libelle}</div>)}
          </div>
        </details>
      )}
    </div>
  );
}

function TypesDocumentFormateur({ params, onChange }) {
  const DEFAUTS = [
    { nom: 'KBIS / Certificat d\'immatriculation', duree_mois: null },
    { nom: 'Attestation fiscale (URSSAF)', duree_mois: null },
    { nom: 'Attestation sociale (URSSAF)', duree_mois: null },
    { nom: 'Attestation d\'assurance RC Pro', duree_mois: null },
    { nom: 'Attestation d\'assurance décennale', duree_mois: null },
    { nom: 'Carte professionnelle / Habilitation', duree_mois: null },
    { nom: 'Curriculum Vitae', duree_mois: null },
    { nom: 'Diplôme / Certification', duree_mois: null },
    { nom: 'Contrat / Convention cadre', duree_mois: null },
    { nom: 'Autre', duree_mois: null },
  ];

  function parseTypes(raw) {
    if (!raw) return DEFAUTS;
    try {
      const arr = JSON.parse(raw);
      return arr.map(item => typeof item === 'string' ? { nom: item, duree_mois: null } : item);
    } catch { return DEFAUTS; }
  }

  const rawTypes = params['types_document_formateur'];
  const initTypes = React.useMemo(() => parseTypes(rawTypes), [rawTypes]);

  const [types, setTypesLocal] = useState(initTypes);
  const [newType, setNewType] = useState('');
  const [saving, setSaving] = useState(false);

  React.useEffect(() => { setTypesLocal(initTypes); }, [rawTypes]);

  async function setTypes(arr) {
    setTypesLocal(arr);
    onChange('types_document_formateur', JSON.stringify(arr));
    setSaving(true);
    try { await apiFetch('/parametres', { method: 'PUT', body: JSON.stringify({ types_document_formateur: JSON.stringify(arr) }) }); }
    catch {}
    finally { setSaving(false); }
  }

  function ajouter() {
    const t = newType.trim();
    if (!t || types.find(x => x.nom === t)) return;
    setTypes([...types, { nom: t, duree_mois: null }]);
    setNewType('');
  }

  function supprimer(i) { setTypes(types.filter((_, idx) => idx !== i)); }

  function monter(i) {
    if (i === 0) return;
    const arr = [...types];
    [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
    setTypes(arr);
  }

  function descendre(i) {
    if (i === types.length - 1) return;
    const arr = [...types];
    [arr[i], arr[i + 1]] = [arr[i + 1], arr[i]];
    setTypes(arr);
  }

  function setDuree(i, val) {
    const arr = types.map((t, idx) => idx === i ? { ...t, duree_mois: val === '' ? null : parseInt(val) || null } : t);
    setTypes(arr);
  }

  return (
    <div style={{ marginBottom: '1rem' }}>
      <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: '0.4rem' }}>
        Ces types apparaissent dans le menu déroulant des documents administratifs formateur.
      </p>
      <p style={{ fontSize: '0.79rem', color: 'var(--text-tertiary)', marginBottom: '0.8rem' }}>
        La durée de validité (en mois) permet à l'IA de calculer automatiquement la date d'expiration à partir de la date d'émission du document.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.15rem 0', marginBottom: '0.4rem' }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-tertiary)', paddingLeft: '0.7rem' }}>Type de document</div>
        <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-tertiary)', textAlign: 'center', paddingRight: '5rem' }}>Validité (mois)</div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', marginBottom: '0.7rem' }}>
        {types.map((t, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '7px', padding: '0.35rem 0.7rem' }}>
            <span style={{ flex: 1, fontSize: '0.85rem', color: 'var(--text-primary)' }}>{t.nom}</span>
            <input
              type="number" min="1" max="240"
              value={t.duree_mois || ''}
              onChange={e => setDuree(i, e.target.value)}
              placeholder="—"
              style={{ width: '60px', padding: '0.2rem 0.4rem', borderRadius: '5px', border: '1px solid var(--border)', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontSize: '0.82rem', textAlign: 'center' }}
              title="Durée de validité en mois (laisser vide si non applicable)"
            />
            <span style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', width: '28px' }}>mois</span>
            <button onClick={() => monter(i)} disabled={i === 0} title="Monter"
              style={{ padding: '0.15rem 0.4rem', borderRadius: '4px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: i === 0 ? 'default' : 'pointer', opacity: i === 0 ? 0.3 : 1, fontSize: '0.75rem' }}>▲</button>
            <button onClick={() => descendre(i)} disabled={i === types.length - 1} title="Descendre"
              style={{ padding: '0.15rem 0.4rem', borderRadius: '4px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: i === types.length - 1 ? 'default' : 'pointer', opacity: i === types.length - 1 ? 0.3 : 1, fontSize: '0.75rem' }}>▼</button>
            <button onClick={() => supprimer(i)} title="Supprimer"
              style={{ padding: '0.15rem 0.4rem', borderRadius: '4px', border: 'none', background: 'rgba(239,68,68,0.1)', color: '#ef4444', cursor: 'pointer', fontSize: '0.75rem' }}>✕</button>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '0.4rem' }}>
        <input value={newType} onChange={e => setNewType(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && ajouter()}
          placeholder="Nouveau type de document…"
          style={{ ...inputStyle, flex: 1 }} />
        <button onClick={ajouter}
          style={{ padding: '0.4rem 0.9rem', borderRadius: '7px', border: 'none', background: '#E87722', color: '#fff', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, whiteSpace: 'nowrap' }}>+ Ajouter</button>
      </div>
    </div>
  );
}

function TypesDocumentStagiaire({ params, onChange }) {
  const DEFAUTS = [
    { nom: 'Aptitude médicale', duree_mois: null },
    { nom: 'Attestation de formation', duree_mois: null },
    { nom: 'Diplôme / Certification', duree_mois: null },
    { nom: 'Carte d\'identité', duree_mois: null },
    { nom: 'CV', duree_mois: null },
    { nom: 'Contrat', duree_mois: null },
    { nom: 'Permis de conduire', duree_mois: null },
    { nom: 'Habilitation', duree_mois: null },
    { nom: 'Autre', duree_mois: null },
  ];

  function parseTypes(raw) {
    if (!raw) return DEFAUTS;
    try {
      const arr = JSON.parse(raw);
      return arr.map(item => typeof item === 'string' ? { nom: item, duree_mois: null, abreviation: '' } : { abreviation: '', ...item });
    } catch { return DEFAUTS; }
  }

  const rawTypes = params['types_docs_stagiaires'];
  const initTypes = React.useMemo(() => parseTypes(rawTypes), [rawTypes]);
  const [types, setTypesLocal] = useState(initTypes);
  const [newType, setNewType] = useState('');

  React.useEffect(() => { setTypesLocal(initTypes); }, [rawTypes]);

  async function setTypes(arr) {
    setTypesLocal(arr);
    onChange('types_docs_stagiaires', JSON.stringify(arr));
    try { await apiFetch('/parametres', { method: 'PUT', body: JSON.stringify({ types_docs_stagiaires: JSON.stringify(arr) }) }); }
    catch {}
  }
  function ajouter() { const t = newType.trim(); if (!t || types.find(x => x.nom === t)) return; setTypes([...types, { nom: t, abreviation: '' }]); setNewType(''); }
  function supprimer(i) { setTypes(types.filter((_, idx) => idx !== i)); }
  function monter(i) { if (i === 0) return; const arr = [...types]; [arr[i-1],arr[i]]=[arr[i],arr[i-1]]; setTypes(arr); }
  function descendre(i) { if (i === types.length-1) return; const arr = [...types]; [arr[i],arr[i+1]]=[arr[i+1],arr[i]]; setTypes(arr); }
  function setAbreviation(i, val) { setTypes(types.map((t, idx) => idx === i ? { ...t, abreviation: val } : t)); }

  return (
    <div style={{ marginBottom: '1rem' }}>
      <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: '0.4rem' }}>
        Ces types apparaissent dans le menu déroulant des documents administratifs stagiaire.
      </p>
      <p style={{ fontSize: '0.79rem', color: 'var(--text-tertiary)', marginBottom: '0.8rem' }}>
        Le champ <strong>Abrégé</strong> est requis pour que le type apparaisse dans l'onglet Attestations et serve au nommage automatique du fichier. La date de fin de validité est calculée depuis la période de recyclage de la formation (si recyclage obligatoire).
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px auto', gap: '0.15rem 0.5rem', marginBottom: '0.4rem', alignItems: 'center' }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-tertiary)', paddingLeft: '0.7rem' }}>Type de document</div>
        <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-tertiary)', textAlign: 'center' }}>Abrégé</div>
        <div />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', marginBottom: '0.7rem' }}>
        {types.map((t, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '7px', padding: '0.35rem 0.7rem' }}>
            <span style={{ flex: 1, fontSize: '0.85rem', color: 'var(--text-primary)' }}>{t.nom}</span>
            <input type="text" value={t.abreviation || ''} onChange={e => setAbreviation(i, e.target.value)}
              placeholder="ex: CACES" title="Nom abrégé (utilisé pour les attestations)"
              style={{ width: '110px', padding: '0.2rem 0.4rem', borderRadius: '5px', border: '1px solid var(--border)', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontSize: '0.82rem', textTransform: 'uppercase' }} />
            <button onClick={() => monter(i)} disabled={i===0} title="Monter" style={{ padding:'0.15rem 0.4rem',borderRadius:'4px',border:'1px solid var(--border)',background:'transparent',color:'var(--text-secondary)',cursor:i===0?'default':'pointer',opacity:i===0?0.3:1,fontSize:'0.75rem' }}>▲</button>
            <button onClick={() => descendre(i)} disabled={i===types.length-1} title="Descendre" style={{ padding:'0.15rem 0.4rem',borderRadius:'4px',border:'1px solid var(--border)',background:'transparent',color:'var(--text-secondary)',cursor:i===types.length-1?'default':'pointer',opacity:i===types.length-1?0.3:1,fontSize:'0.75rem' }}>▼</button>
            <button onClick={() => supprimer(i)} title="Supprimer" style={{ padding:'0.15rem 0.4rem',borderRadius:'4px',border:'none',background:'rgba(239,68,68,0.1)',color:'#ef4444',cursor:'pointer',fontSize:'0.75rem' }}>✕</button>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '0.4rem' }}>
        <input value={newType} onChange={e => setNewType(e.target.value)} onKeyDown={e => e.key==='Enter' && ajouter()}
          placeholder="Nouveau type de document…" style={{ ...inputStyle, flex: 1 }} />
        <button onClick={ajouter} style={{ padding:'0.4rem 0.9rem',borderRadius:'7px',border:'none',background:'#E87722',color:'#fff',cursor:'pointer',fontSize:'0.85rem',fontWeight:600,whiteSpace:'nowrap' }}>+ Ajouter</button>
      </div>
    </div>
  );
}



// ══════════════════════════════════════════════════════════════════════════════
// ONGLET QUESTIONNAIRES
// ══════════════════════════════════════════════════════════════════════════════

const TYPES_QUESTIONNAIRE = [
  { val: 'positionnement',       label: 'Positionnement',        color: '#7c3aed', bg: '#f5f3ff', is_common: false },
  { val: 'evaluation',           label: 'Évaluation',            color: '#2563eb', bg: '#eff6ff', is_common: false },
  { val: 'evaluation_formative', label: 'Évaluation formative',  color: '#ea580c', bg: '#fff7ed', is_common: false },
  { val: 'satisfaction',         label: 'Satisfaction',          color: '#16a34a', bg: '#f0fdf4', is_common: true  },
];

function parseTypesQ(raw) {
  if (!raw) return TYPES_QUESTIONNAIRE;
  try { return JSON.parse(raw); } catch { return TYPES_QUESTIONNAIRE; }
}

const TYPES_REPONSE = [
  { val: 'chapitre', label: 'Titre / Chapitre (bloc de texte)' },
  { val: 'smiley',  label: 'Smileys' },
  { val: 'note',    label: 'Note numerique' },
  { val: 'choix',   label: 'Choix multiple' },
  { val: 'oui_non', label: 'Oui / Non' },
  { val: 'texte',   label: 'Texte libre (reponse ouverte)' },
  { val: 'photo',   label: 'Photo (le stagiaire prend une photo)' },
];

function QuestionEditor({ question, onSave, onDelete, onDuplicate, onMove, onImageUploaded, onImageDeleted, isFirst, isLast }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    texte: question.texte,
    type_reponse: question.type_reponse,
    options: Array.isArray(question.options) ? question.options : [],
    reponse_correcte: question.reponse_correcte || [],
    points: question.points || 0,
    obligatoire: question.obligatoire !== false,
    nb_smileys: (question.options && !Array.isArray(question.options) && question.options.nb_smileys) ? question.options.nb_smileys : 5,
    max_note: (question.options && !Array.isArray(question.options) && question.options.max_note) ? question.options.max_note : 10,
  });
  const [newOption, setNewOption] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const imgRef = React.useRef();

  const inputS = { padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.85rem', width: '100%', boxSizing: 'border-box' };
  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  async function handleSave() {
    setSaving(true);
    let saveData = { ...form };
    if (form.type_reponse === 'smiley') {
      saveData = { ...saveData, options: { nb_smileys: form.nb_smileys } };
    } else if (form.type_reponse === 'note') {
      saveData = { ...saveData, options: { max_note: form.max_note } };
    } else if (form.type_reponse === 'chapitre') {
      saveData = { ...saveData, options: null, reponse_correcte: [], points: 0, obligatoire: false };
    }
    try {
      await onSave(saveData);
      setEditing(false);
    } catch (e) {
      alert('Erreur lors de la sauvegarde : ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  async function uploadImage(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingImg(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const authToken = localStorage.getItem('token');
      const r = await fetch(`${apiBase}/api/questions/${question.question_id}/image`, { method: 'POST', headers: { Authorization: 'Bearer ' + authToken }, body: fd });
      const d = await r.json();
      if (d.ok) onImageUploaded(d.image_url);
    } catch (e) { alert('Erreur upload : ' + e.message); }
    finally { setUploadingImg(false); e.target.value = ''; }
  }

  async function deleteImage() {
    if (!window.confirm('Supprimer cette image ?')) return;
    const authToken = localStorage.getItem('token');
    await fetch(`${apiBase}/api/questions/${question.question_id}/image`, { method: 'DELETE', headers: { Authorization: 'Bearer ' + authToken } });
    onImageDeleted();
  }

  if (!editing && question.type_reponse === 'chapitre') return (
    <div style={{ borderRadius: '8px', background: '#e0e7ff', border: '2px solid #6366f1', marginBottom: '0.4rem', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.7rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginRight: '0.2rem' }}>
          <button onClick={() => onMove(-1)} disabled={isFirst} style={{ background: 'none', border: 'none', cursor: isFirst ? 'default' : 'pointer', color: isFirst ? '#a5b4fc' : '#6366f1', fontSize: '0.7rem', padding: '0 2px' }}>▲</button>
          <button onClick={() => onMove(1)} disabled={isLast} style={{ background: 'none', border: 'none', cursor: isLast ? 'default' : 'pointer', color: isLast ? '#a5b4fc' : '#6366f1', fontSize: '0.7rem', padding: '0 2px' }}>▼</button>
        </div>
        <span style={{ fontSize: '0.72rem', color: '#6366f1', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginRight: '0.3rem' }}>§</span>
        <div style={{ flex: 1, fontWeight: 700, fontSize: '0.92rem', color: '#3730a3' }}>{question.texte}</div>
        <button onClick={() => setEditing(true)} title="Modifier" style={{ background: 'none', border: '1px solid #a5b4fc', borderRadius: '6px', padding: '0.2rem 0.5rem', cursor: 'pointer', fontSize: '0.78rem', color: '#6366f1' }}>✏</button>
        <button onClick={onDelete} title="Supprimer" style={{ background: 'none', border: '1px solid #fecaca', borderRadius: '6px', padding: '0.2rem 0.5rem', cursor: 'pointer', fontSize: '0.78rem', color: '#dc2626' }}>✕</button>
      </div>
    </div>
  );

  if (!editing) return (
    <div style={{ borderRadius: '8px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', marginBottom: '0.4rem', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.7rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginRight: '0.2rem' }}>
          <button onClick={() => onMove(-1)} disabled={isFirst} style={{ background: 'none', border: 'none', cursor: isFirst ? 'default' : 'pointer', color: isFirst ? '#ccc' : 'var(--text-secondary)', fontSize: '0.7rem', padding: '0 2px' }}>▲</button>
          <button onClick={() => onMove(1)} disabled={isLast} style={{ background: 'none', border: 'none', cursor: isLast ? 'default' : 'pointer', color: isLast ? '#ccc' : 'var(--text-secondary)', fontSize: '0.7rem', padding: '0 2px' }}>▼</button>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '0.88rem', color: 'var(--text-primary)', fontWeight: 500 }}>{question.texte}</div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: '2px' }}>
            {question.type_reponse === 'smiley'
              ? `Smileys — ${(question.options && !Array.isArray(question.options) && question.options.nb_smileys) ? question.options.nb_smileys : 5} niveaux`
              : question.type_reponse === 'note'
              ? `Note — 1 à ${(question.options && !Array.isArray(question.options) && question.options.max_note) ? question.options.max_note : 10}`
              : TYPES_REPONSE.find(t => t.val === question.type_reponse)?.label || question.type_reponse}
            {Array.isArray(question.options) && question.options.length > 0 && ' — ' + question.options.join(', ')}
            {question.image_url && ' — 🖼 image'}
            {(question.type_reponse === 'choix' || question.type_reponse === 'smiley' || question.type_reponse === 'note') && question.points > 0 && (
              <span style={{ marginLeft: 6, color: '#16a34a', fontWeight: 700 }}>✓ {question.points} pts</span>
            )}
            {question.obligatoire === false && (
              <span style={{ marginLeft: 6, padding: '0 5px', borderRadius: '4px', background: '#f1f5f9', color: '#94a3b8', fontSize: '0.68rem', fontWeight: 600, border: '1px solid #e2e8f0' }}>optionnel</span>
            )}
          </div>
        </div>
        {/* Bouton upload image */}
        <input ref={imgRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={uploadImage} />
        <button onClick={() => imgRef.current?.click()} disabled={uploadingImg} title="Ajouter une image illustrative"
          style={{ background: question.image_url ? '#f0fdf4' : 'none', border: '1px solid ' + (question.image_url ? '#16a34a' : 'var(--border)'), borderRadius: '6px', padding: '0.2rem 0.5rem', cursor: 'pointer', fontSize: '0.78rem', color: question.image_url ? '#16a34a' : 'var(--text-secondary)' }}>
          {uploadingImg ? '...' : '🖼'}
        </button>
        {question.image_url && (
          <button onClick={deleteImage} title="Supprimer l'image"
            style={{ background: 'none', border: '1px solid #fecaca', borderRadius: '6px', padding: '0.2rem 0.5rem', cursor: 'pointer', fontSize: '0.78rem', color: '#dc2626' }}>🗑</button>
        )}
        <button onClick={() => setEditing(true)} title="Modifier" style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '6px', padding: '0.2rem 0.5rem', cursor: 'pointer', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>✏</button>
        <button onClick={onDuplicate} title="Dupliquer" style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '6px', padding: '0.2rem 0.5rem', cursor: 'pointer', fontSize: '0.78rem', color: '#7c3aed' }}>⧉</button>
        <button onClick={onDelete} title="Supprimer" style={{ background: 'none', border: '1px solid #fecaca', borderRadius: '6px', padding: '0.2rem 0.5rem', cursor: 'pointer', fontSize: '0.78rem', color: '#dc2626' }}>✕</button>
      </div>
      {question.image_url && (
        <div style={{ padding: '0 0.7rem 0.5rem' }}>
          <img src={`${apiBase}${question.image_url}`} alt="Illustration" style={{ maxHeight: '120px', maxWidth: '100%', borderRadius: '6px', border: '1px solid var(--border)' }} />
        </div>
      )}
    </div>
  );

  return (
    <div style={{ padding: '0.8rem', borderRadius: '8px', background: 'var(--bg-elevated)', border: '2px solid var(--accent-blue)', marginBottom: '0.4rem' }}>
      <div style={{ marginBottom: '0.6rem' }}>
        <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 3 }}>
          {form.type_reponse === 'chapitre' ? 'Titre du chapitre' : 'Texte de la question'}
        </label>
        <textarea value={form.texte} onChange={e => setForm(f => ({ ...f, texte: e.target.value }))} rows={2} style={{ ...inputS, resize: 'vertical' }} />
      </div>
      <div style={{ marginBottom: '0.6rem' }}>
        <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 3 }}>Type de reponse</label>
        <select value={form.type_reponse} onChange={e => setForm(f => ({ ...f, type_reponse: e.target.value, options: [], reponse_correcte: [] }))} style={inputS}>
          {TYPES_REPONSE.map(t => <option key={t.val} value={t.val}>{t.label}</option>)}
        </select>
      </div>
      {form.type_reponse !== 'chapitre' && (
        <div style={{ marginBottom: '0.6rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.82rem', color: 'var(--text-primary)' }}>
            <input type="checkbox" checked={form.obligatoire} onChange={e => setForm(f => ({ ...f, obligatoire: e.target.checked }))}
              style={{ accentColor: '#2563eb', width: 15, height: 15 }} />
            Réponse obligatoire pour cette question
          </label>
        </div>
      )}
      {form.type_reponse === 'smiley' && (
        <div style={{ marginBottom: '0.6rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 3 }}>Nombre de niveaux (2 à 5)</label>
            <select value={form.nb_smileys} onChange={e => setForm(f => ({ ...f, nb_smileys: parseInt(e.target.value) }))} style={{ ...inputS, width: 'auto' }}>
              {[2, 3, 4, 5].map(n => <option key={n} value={n}>{n} smileys</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 3 }}>Points (évaluation)</label>
            <input type="number" min={0} value={form.points} onChange={e => setForm(f => ({ ...f, points: parseInt(e.target.value) || 0 }))} style={{ ...inputS, width: '80px' }} />
          </div>
        </div>
      )}
      {form.type_reponse === 'note' && (
        <div style={{ marginBottom: '0.6rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 3 }}>Note maximale (2 à 20)</label>
            <input type="number" min={2} max={20} value={form.max_note} onChange={e => setForm(f => ({ ...f, max_note: parseInt(e.target.value) || 10 }))} style={{ ...inputS, width: '80px' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 3 }}>Points (évaluation)</label>
            <input type="number" min={0} value={form.points} onChange={e => setForm(f => ({ ...f, points: parseInt(e.target.value) || 0 }))} style={{ ...inputS, width: '80px' }} />
          </div>
        </div>
      )}
      {form.type_reponse === 'choix' && (
        <div style={{ marginBottom: '0.6rem' }}>
          <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 3 }}>Options <span style={{ fontStyle: 'italic' }}>(cochez la/les bonne(s) réponse(s))</span></label>
          {(form.options || []).map((opt, i) => {
            const isCorrect = (form.reponse_correcte || []).includes(opt);
            return (
              <div key={i} style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.3rem', alignItems: 'center' }}>
                <input type="checkbox" checked={isCorrect} onChange={e => {
                  const rc = form.reponse_correcte || [];
                  setForm(f => ({ ...f, reponse_correcte: e.target.checked ? [...rc, opt] : rc.filter(v => v !== opt) }));
                }} style={{ accentColor: '#16a34a', width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }} title="Bonne réponse" />
                <input value={opt} onChange={e => {
                  const o = [...form.options]; const oldVal = o[i]; o[i] = e.target.value;
                  const rc = (form.reponse_correcte || []).map(v => v === oldVal ? e.target.value : v);
                  setForm(f => ({ ...f, options: o, reponse_correcte: rc }));
                }} style={{ ...inputS, flex: 1, background: isCorrect ? '#f0fdf4' : 'var(--bg-card)', borderColor: isCorrect ? '#16a34a' : 'var(--border)', color: isCorrect ? '#166534' : 'var(--text-primary)', fontWeight: isCorrect ? 700 : 400 }} />
                <button onClick={() => setForm(f => ({ ...f, options: f.options.filter((_, j) => j !== i), reponse_correcte: (f.reponse_correcte || []).filter(v => v !== opt) }))} style={{ background: 'none', border: '1px solid #fecaca', borderRadius: '5px', padding: '0 0.5rem', color: '#dc2626', cursor: 'pointer' }}>✕</button>
              </div>
            );
          })}
          <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.2rem' }}>
            <input value={newOption} onChange={e => setNewOption(e.target.value)} placeholder="Nouvelle option..." style={{ ...inputS, flex: 1 }} onKeyDown={e => { if (e.key === 'Enter' && newOption.trim()) { setForm(f => ({ ...f, options: [...(f.options || []), newOption.trim()] })); setNewOption(''); }}} />
            <button onClick={() => { if (newOption.trim()) { setForm(f => ({ ...f, options: [...(f.options || []), newOption.trim()] })); setNewOption(''); }}} style={{ padding: '0.3rem 0.7rem', borderRadius: '6px', background: 'var(--accent-blue)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '0.8rem' }}>+ Ajouter</button>
          </div>
          <div style={{ marginTop: '0.5rem' }}>
            <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 3 }}>Points pour cette question</label>
            <input type="number" min={0} value={form.points} onChange={e => setForm(f => ({ ...f, points: parseInt(e.target.value) || 0 }))} style={{ ...inputS, width: '100px' }} />
          </div>
        </div>
      )}
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
        <button onClick={() => setEditing(false)} style={{ padding: '0.3rem 0.8rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.82rem' }}>Annuler</button>
        <button onClick={handleSave} disabled={saving || !form.texte.trim()} style={{ padding: '0.3rem 0.8rem', borderRadius: '6px', background: saving ? '#94a3b8' : 'var(--accent-blue)', color: '#fff', border: 'none', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '0.82rem', fontWeight: 600 }}>
          {saving ? '...' : 'Enregistrer'}
        </button>
      </div>
    </div>
  );
}

function QuestionnaireDetail({ qId, onBack, formations, typesQ = TYPES_QUESTIONNAIRE }) {
  const [q, setQ] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [titre, setTitre] = useState('');
  const [formationId, setFormationId] = useState('');
  const [typeQ, setTypeQ] = useState('positionnement');
  const [description, setDescription] = useState('');
  const [reponsesObligatoires, setReponsesObligatoires] = useState(true);
  const [preview, setPreview] = useState(false);
  const [addingQ, setAddingQ] = useState(false);
  const [newQ, setNewQ] = useState({ texte: '', type_reponse: 'smiley', options: [], reponse_correcte: [], points: 0, obligatoire: true, nb_smileys: 5, max_note: 10 });
  const [newQOption, setNewQOption] = useState('');
  const [msg, setMsg] = useState('');
  const [satSections, setSatSections] = useState([]);

  useEffect(() => {
    if (typeQ !== 'satisfaction') return;
    apiFetch('/satisfaction/indicateurs').then(d => {
      const noms = (d || []).filter(i => i.actif && i.section_nom).map(i => i.section_nom);
      setSatSections(noms);
    }).catch(() => {});
  }, [typeQ]);

  const inputS = { padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.85rem', width: '100%', boxSizing: 'border-box' };

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiFetch('/questionnaires/' + qId);
      setQ(d);
      setTitre(d.titre);
      setFormationId(d.formation_id || '');
      setTypeQ(d.type || 'positionnement');
      setDescription(d.description || '');
      setReponsesObligatoires(d.reponses_obligatoires !== false);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [qId]);

  useEffect(() => { charger(); }, [charger]);

  async function saveMeta() {
    setSaving(true);
    try {
      await apiFetch('/questionnaires/' + qId, { method: 'PUT', body: JSON.stringify({
        titre, formation_id: formationId || null,
        type: typeQ, description: description || null,
        reponses_obligatoires: reponsesObligatoires,
      }) });
      setQ(prev => ({ ...prev, titre, type: typeQ, description, reponses_obligatoires: reponsesObligatoires }));
      showMsg('Enregistré');
    } catch (e) { showMsg('Erreur : ' + e.message, true); }
    finally { setSaving(false); }
  }

  function showMsg(text, err) {
    setMsg({ text, err });
    setTimeout(() => setMsg(''), 2500);
  }

  async function saveQuestion(questionId, data) {
    await apiFetch('/questions/' + questionId, { method: 'PUT', body: JSON.stringify(data) });
    charger();
  }

  async function deleteQuestion(questionId) {
    if (!window.confirm('Supprimer cette question ?')) return;
    await apiFetch('/questions/' + questionId, { method: 'DELETE' });
    charger();
  }

  async function duplicateQuestion(questionId) {
    const questions = q.questions || [];
    const src = questions.find(qu => qu.question_id === questionId);
    if (!src) return;
    const maxOrdre = Math.max(...questions.map(qu => qu.ordre), -1);
    await apiFetch('/questionnaires/' + qId + '/questions', {
      method: 'POST', body: JSON.stringify({
        texte: src.texte,
        type_reponse: src.type_reponse,
        options: src.options || null,
        reponse_correcte: src.reponse_correcte || null,
        points: src.points || 0,
        image_url: src.image_url || null,
        obligatoire: src.obligatoire !== false,
        ordre: maxOrdre + 1,
      })
    });
    charger();
  }

  async function moveQuestion(questionId, direction) {
    const questions = q.questions || [];
    const idx = questions.findIndex(qu => qu.question_id === questionId);
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= questions.length) return;
    // Update ordre for both
    const a = questions[idx];
    const b = questions[newIdx];
    await Promise.all([
      apiFetch('/questions/' + a.question_id, { method: 'PUT', body: JSON.stringify({ texte: a.texte, type_reponse: a.type_reponse, options: a.options, ordre: b.ordre }) }),
      apiFetch('/questions/' + b.question_id, { method: 'PUT', body: JSON.stringify({ texte: b.texte, type_reponse: b.type_reponse, options: b.options, ordre: a.ordre }) }),
    ]);
    charger();
  }

  async function addQuestion() {
    if (!newQ.texte.trim()) return;
    const questions = q.questions || [];
    const maxOrdre = questions.length > 0 ? Math.max(...questions.map(qu => qu.ordre)) : -1;
    let options = newQ.options;
    if (newQ.type_reponse === 'smiley') options = { nb_smileys: newQ.nb_smileys };
    else if (newQ.type_reponse === 'note') options = { max_note: newQ.max_note };
    else if (newQ.type_reponse === 'chapitre') options = null;
    const isChapitre = newQ.type_reponse === 'chapitre';
    await apiFetch('/questionnaires/' + qId + '/questions', { method: 'POST', body: JSON.stringify({
      ...newQ,
      options: options || null,
      obligatoire: isChapitre ? false : newQ.obligatoire,
      points: isChapitre ? 0 : newQ.points,
      ordre: maxOrdre + 1,
    }) });
    setNewQ({ texte: '', type_reponse: 'smiley', options: [], reponse_correcte: [], points: 0, obligatoire: true, nb_smileys: 5, max_note: 10 });
    setAddingQ(false);
    charger();
  }

  async function archiverQuestionnaire() {
    if (!window.confirm('Archiver ce questionnaire ? Il ne sera plus disponible dans les sessions.')) return;
    await apiFetch('/questionnaires/' + qId, { method: 'DELETE' });
    onBack(true);
  }

  if (loading) return <Loading />;
  if (!q) return <div style={{ color: 'var(--text-secondary)' }}>Questionnaire introuvable.</div>;

  const typeCfg = typesQ.find(t => t.val === q.type);
  const questions = q.questions || [];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginBottom: '1.2rem' }}>
        <button onClick={() => onBack(false)} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '7px', padding: '0.35rem 0.8rem', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>← Retour</button>
        <span style={{ padding: '0.2rem 0.7rem', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 700, background: typeCfg?.bg, color: typeCfg?.color }}>{typeCfg?.label}</span>
        <h3 style={{ flex: 1, margin: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)' }}>{q.titre}</h3>
        <button onClick={() => setPreview(true)} style={{ padding: '0.3rem 0.7rem', borderRadius: '6px', border: '1px solid #2563eb', background: '#eff6ff', color: '#2563eb', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>👁 Aperçu</button>
        <button onClick={archiverQuestionnaire} style={{ padding: '0.3rem 0.7rem', borderRadius: '6px', border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>🗑 Archiver</button>
      </div>

      {/* Modal aperçu */}
      {preview && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: '#f8fafc', borderRadius: '24px', width: '100%', maxWidth: 400, maxHeight: '85vh', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
            {/* Barre téléphone */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.8rem 1rem', background: '#fff', borderRadius: '24px 24px 0 0', borderBottom: '1px solid #e2e8f0' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b' }}>Aperçu mobile</span>
              <button onClick={() => setPreview(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: '1.1rem' }}>✕</button>
            </div>
            <div style={{ padding: '1.2rem', flex: 1 }}>
              {/* En-tête */}
              <div style={{ textAlign: 'center', marginBottom: '1.2rem' }}>
                <div style={{ fontSize: '0.7rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '0.3rem' }}>
                  {typesQ.find(t => t.val === q.type)?.label || q.type}
                </div>
                <h2 style={{ fontSize: '1.1rem', color: '#1e293b', fontWeight: 700, margin: '0 0 0.5rem' }}>{q.titre}</h2>
                {description && (
                  <div style={{ fontSize: '0.85rem', color: '#475569', lineHeight: 1.5, padding: '0.7rem', background: '#f1f5f9', borderRadius: '8px', textAlign: 'left', marginBottom: '0.8rem' }}>
                    {description}
                  </div>
                )}
                <div style={{ padding: '0.75rem', background: '#2563eb', color: '#fff', borderRadius: '10px', fontWeight: 700, fontSize: '0.95rem', cursor: 'default' }}>Commencer</div>
              </div>
              <div style={{ borderTop: '2px dashed #e2e8f0', marginBottom: '1rem', paddingTop: '1rem' }}>
                <div style={{ fontSize: '0.72rem', color: '#94a3b8', textAlign: 'center', marginBottom: '0.8rem', fontStyle: 'italic' }}>— Questions —</div>
                {(questions).map((qu, idx) => {
                  const estObligatoire = qu.obligatoire !== false;
                  if (qu.type_reponse === 'chapitre') return (
                    <div key={qu.question_id} style={{ margin: '0.8rem 0 0.4rem', padding: '0.4rem 0.8rem', background: '#e0e7ff', borderLeft: '3px solid #6366f1', borderRadius: '0 6px 6px 0', fontWeight: 700, color: '#3730a3', fontSize: '0.85rem' }}>
                      {qu.texte}
                    </div>
                  );
                  return (
                    <div key={qu.question_id} style={{ marginBottom: '1.2rem' }}>
                      {qu.image_url && (
                        <div style={{ marginBottom: '0.5rem', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                          <img src={`${(process.env.REACT_APP_API_URL||'http://localhost:3002/api').replace('/api','')}${qu.image_url}`} alt="" style={{ width: '100%', maxHeight: '140px', objectFit: 'contain', background: '#f8fafc' }} />
                        </div>
                      )}
                      <p style={{ fontWeight: 600, fontSize: '0.88rem', color: '#1e293b', margin: '0 0 0.4rem', lineHeight: 1.4 }}>
                        {questions.filter(q2 => q2.type_reponse !== 'chapitre').indexOf(qu) + 1}. {qu.texte}
                        {estObligatoire && <span style={{ color: '#dc2626', marginLeft: 3 }}>*</span>}
                      </p>
                      {qu.type_reponse === 'smiley' && (
                        <div style={{ display: 'flex', gap: '0.3rem', justifyContent: 'space-around' }}>
                          {['😞','😕','😐','🙂','😄'].slice(5 - ((qu.options && !Array.isArray(qu.options) && qu.options.nb_smileys) || 5)).map((e,i) => <span key={i} style={{ fontSize: '1.4rem', opacity: 0.5 }}>{e}</span>)}
                        </div>
                      )}
                      {qu.type_reponse === 'note' && (
                        <div style={{ display: 'flex', gap: '0.2rem', flexWrap: 'wrap' }}>
                          {Array.from({ length: (qu.options && !Array.isArray(qu.options) && qu.options.max_note) || 10 }, (_, i) => i + 1).map(n => <span key={n} style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.75rem', color: '#94a3b8' }}>{n}</span>)}
                        </div>
                      )}
                      {qu.type_reponse === 'choix' && (qu.options||[]).map((opt, oi) => (
                        <div key={oi} style={{ padding: '0.4rem 0.8rem', borderRadius: '6px', border: '1px solid #e2e8f0', marginBottom: '0.25rem', fontSize: '0.82rem', color: '#475569' }}>
                          {String.fromCharCode(65+oi)}) {opt}
                        </div>
                      ))}
                      {qu.type_reponse === 'oui_non' && (
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          {['Oui','Non'].map(v => <span key={v} style={{ flex: 1, padding: '0.4rem', textAlign: 'center', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.85rem', color: '#475569' }}>{v}</span>)}
                        </div>
                      )}
                      {qu.type_reponse === 'texte' && <div style={{ border: '1px solid #e2e8f0', borderRadius: '6px', padding: '0.5rem', fontSize: '0.78rem', color: '#94a3b8', minHeight: 40 }}>Réponse libre...</div>}
                      {qu.type_reponse === 'photo' && <div style={{ border: '2px dashed #e2e8f0', borderRadius: '6px', padding: '0.7rem', textAlign: 'center', fontSize: '0.78rem', color: '#94a3b8' }}>📷 Photo</div>}
                    </div>
                  );
                })}
              </div>
              <div style={{ padding: '0.75rem', background: '#2563eb', color: '#fff', borderRadius: '10px', fontWeight: 700, fontSize: '0.95rem', textAlign: 'center', cursor: 'default' }}>Valider mes réponses</div>
            </div>
          </div>
        </div>
      )}

      {/* Meta */}
      <div style={{ background: 'var(--bg-elevated)', borderRadius: '10px', padding: '1rem', border: '1px solid var(--border)', marginBottom: '1.2rem' }}>
        <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '0.7rem' }}>
          <div style={{ flex: 2, minWidth: 200 }}>
            <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 3 }}>Titre</label>
            <input value={titre} onChange={e => setTitre(e.target.value)} style={inputS} />
          </div>
          <div style={{ flex: 1, minWidth: 140 }}>
            <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 3 }}>Type</label>
            <select value={typeQ} onChange={e => setTypeQ(e.target.value)} style={inputS}>
              {typesQ.map(t => <option key={t.val} value={t.val}>{t.label}</option>)}
            </select>
          </div>
          {!typesQ.find(t => t.val === typeQ)?.is_common && (
            <div style={{ flex: 1, minWidth: 160 }}>
              <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 3 }}>Formation associée (optionnel)</label>
              <select value={formationId} onChange={e => setFormationId(e.target.value)} style={inputS}>
                <option value="">Toutes formations</option>
                {(formations || []).map(f => <option key={f.formation_id} value={f.formation_id}>{f.intitule}</option>)}
              </select>
            </div>
          )}
        </div>
        <div style={{ marginBottom: '0.7rem' }}>
          <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 3 }}>
            Description / consignes <span style={{ fontStyle: 'italic', fontWeight: 400 }}>(affichée avant le début du questionnaire)</span>
          </label>
          <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3}
            placeholder="Ex : Ce questionnaire permet d'évaluer vos connaissances avant la formation. Répondez à toutes les questions de votre mieux."
            style={{ ...inputS, resize: 'vertical' }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.2rem', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.85rem', color: 'var(--text-primary)' }}>
            <input type="checkbox" checked={reponsesObligatoires} onChange={e => {
              const val = e.target.checked;
              setReponsesObligatoires(val);
              // Propager immédiatement à toutes les questions existantes
              setQ(prev => prev ? ({ ...prev, questions: (prev.questions || []).map(qu => ({ ...qu, obligatoire: val })) }) : prev);
              // Sauvegarder en base pour chaque question
              (q?.questions || []).forEach(qu => {
                apiFetch('/questions/' + qu.question_id, { method: 'PUT', body: JSON.stringify({
                  texte: qu.texte, type_reponse: qu.type_reponse,
                  options: qu.options || null, reponse_correcte: qu.reponse_correcte || null,
                  points: qu.points || 0, ordre: qu.ordre, obligatoire: val,
                }) });
              });
            }}
              style={{ accentColor: '#2563eb', width: 16, height: 16 }} />
            Réponses obligatoires par défaut
            <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>(appliqué à toutes les questions)</span>
          </label>
          <div style={{ flex: 1 }} />
          <button onClick={saveMeta} disabled={saving} style={{ padding: '0.45rem 1.1rem', borderRadius: '7px', background: saving ? '#94a3b8' : 'var(--accent-blue)', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
            {saving ? '...' : 'Enregistrer'}
          </button>
        </div>
        {msg && <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: couleurMessage(!msg.err) }}>{msg.err ? '❌ ' : '✓ '}{msg.text}</div>}
      </div>

      {/* Questions */}
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.7rem' }}>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-primary)' }}>Questions ({questions.length})</div>
          <button onClick={() => setAddingQ(true)} style={{ padding: '0.35rem 0.8rem', borderRadius: '7px', background: 'var(--accent-blue)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}>+ Ajouter une question</button>
        </div>

        {questions.length === 0 && !addingQ && (
          <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-secondary)', background: 'var(--bg-elevated)', borderRadius: '8px', fontSize: '0.9rem' }}>
            Aucune question. Cliquez sur "+ Ajouter une question".
          </div>
        )}

        {questions.map((qu, idx) => (
          <QuestionEditor
            key={`${qu.question_id}_${qu.obligatoire}`}
            question={qu}
            isFirst={idx === 0}
            isLast={idx === questions.length - 1}
            onSave={data => saveQuestion(qu.question_id, { ...data, ordre: qu.ordre })}
            onDelete={() => deleteQuestion(qu.question_id)}
            onDuplicate={() => duplicateQuestion(qu.question_id)}
            onMove={dir => moveQuestion(qu.question_id, dir)}
            onImageUploaded={() => charger()}
            onImageDeleted={() => charger()}
          />
        ))}

        {/* Formulaire ajout question */}
        {addingQ && (
          <div style={{ padding: '0.8rem', borderRadius: '8px', background: 'var(--bg-elevated)', border: '2px solid var(--accent-blue)', marginBottom: '0.4rem' }}>
            <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '0.6rem', color: 'var(--text-primary)' }}>Nouvelle question</div>
            <div style={{ marginBottom: '0.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 3 }}>
                {newQ.type_reponse === 'chapitre' ? 'Titre du chapitre' : 'Texte de la question'}
              </label>
              <textarea value={newQ.texte} onChange={e => setNewQ(q => ({ ...q, texte: e.target.value }))} rows={2} style={{ ...inputS, resize: 'vertical' }} autoFocus />
              {newQ.type_reponse === 'chapitre' && satSections.length > 0 && (
                <div style={{ marginTop: '0.35rem', display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                  <span style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', alignSelf: 'center' }}>Indicateurs :</span>
                  {satSections.map(nom => (
                    <button key={nom} type="button" onClick={() => setNewQ(q => ({ ...q, texte: nom }))}
                      style={{
                        fontSize: '0.72rem', padding: '0.15rem 0.5rem', borderRadius: '10px',
                        border: '1px solid var(--accent-blue)', background: newQ.texte === nom ? 'var(--accent-blue)' : 'transparent',
                        color: newQ.texte === nom ? '#fff' : 'var(--accent-blue)',
                        cursor: 'pointer', transition: 'all 0.15s',
                      }}>
                      {nom}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div style={{ marginBottom: '0.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 3 }}>Type de reponse</label>
              <select value={newQ.type_reponse} onChange={e => setNewQ(q => ({ ...q, type_reponse: e.target.value, options: [], reponse_correcte: [] }))} style={inputS}>
                {TYPES_REPONSE.map(t => <option key={t.val} value={t.val}>{t.label}</option>)}
              </select>
            </div>
            {newQ.type_reponse !== 'chapitre' && (
              <div style={{ marginBottom: '0.5rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.82rem', color: 'var(--text-primary)' }}>
                  <input type="checkbox" checked={newQ.obligatoire !== false} onChange={e => setNewQ(q => ({ ...q, obligatoire: e.target.checked }))}
                    style={{ accentColor: '#2563eb', width: 15, height: 15 }} />
                  Réponse obligatoire
                </label>
              </div>
            )}
            {newQ.type_reponse === 'smiley' && (
              <div style={{ marginBottom: '0.5rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 3 }}>Nombre de niveaux (2 à 5)</label>
                  <select value={newQ.nb_smileys} onChange={e => setNewQ(q => ({ ...q, nb_smileys: parseInt(e.target.value) }))} style={{ ...inputS, width: 'auto' }}>
                    {[2, 3, 4, 5].map(n => <option key={n} value={n}>{n} smileys</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 3 }}>Points (évaluation)</label>
                  <input type="number" min={0} value={newQ.points} onChange={e => setNewQ(q => ({ ...q, points: parseInt(e.target.value) || 0 }))} style={{ ...inputS, width: '80px' }} />
                </div>
              </div>
            )}
            {newQ.type_reponse === 'note' && (
              <div style={{ marginBottom: '0.5rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 3 }}>Note maximale (2 à 20)</label>
                  <input type="number" min={2} max={20} value={newQ.max_note} onChange={e => setNewQ(q => ({ ...q, max_note: parseInt(e.target.value) || 10 }))} style={{ ...inputS, width: '80px' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 3 }}>Points (évaluation)</label>
                  <input type="number" min={0} value={newQ.points} onChange={e => setNewQ(q => ({ ...q, points: parseInt(e.target.value) || 0 }))} style={{ ...inputS, width: '80px' }} />
                </div>
              </div>
            )}
            {newQ.type_reponse === 'choix' && (
              <div style={{ marginBottom: '0.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 3 }}>
                  Options <span style={{ color: '#dc2626' }}>*</span> <span style={{ fontStyle: 'italic' }}>(cochez la/les bonne(s) réponse(s))</span>
                  {newQ.options.length === 0 && <span style={{ color: '#f97316', marginLeft: 6 }}>— ajoutez au moins 2 options</span>}
                </label>
                {(newQ.options || []).map((opt, i) => {
                  const isCorrect = (newQ.reponse_correcte || []).includes(opt);
                  return (
                    <div key={i} style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.3rem', alignItems: 'center' }}>
                      <input type="checkbox" checked={isCorrect} onChange={e => {
                        const rc = newQ.reponse_correcte || [];
                        setNewQ(q => ({ ...q, reponse_correcte: e.target.checked ? [...rc, opt] : rc.filter(v => v !== opt) }));
                      }} style={{ accentColor: '#16a34a', width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }} title="Bonne réponse" />
                      <span style={{ flex: 1, padding: '0.3rem 0.6rem', background: isCorrect ? '#f0fdf4' : 'var(--bg-elevated)', borderRadius: '5px', fontSize: '0.85rem', border: '1px solid ' + (isCorrect ? '#16a34a' : 'var(--border)'), color: isCorrect ? '#166534' : 'var(--text-primary)', fontWeight: isCorrect ? 700 : 400 }}>
                        {String.fromCharCode(65 + i)}. {opt}
                      </span>
                      <button onClick={() => setNewQ(q => ({ ...q, options: q.options.filter((_, j) => j !== i), reponse_correcte: (q.reponse_correcte || []).filter(v => v !== opt) }))} style={{ background: 'none', border: '1px solid #fecaca', borderRadius: '5px', padding: '0 0.5rem', color: '#dc2626', cursor: 'pointer' }}>✕</button>
                    </div>
                  );
                })}
                <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.3rem' }}>
                  <input
                    value={newQOption}
                    onChange={e => setNewQOption(e.target.value)}
                    placeholder="Saisir une option..."
                    style={{ ...inputS, flex: 1 }}
                    onKeyDown={e => { if (e.key === 'Enter' && newQOption.trim()) { setNewQ(q => ({ ...q, options: [...(q.options || []), newQOption.trim()] })); setNewQOption(''); e.preventDefault(); }}}
                  />
                  <button
                    onClick={() => { if (newQOption.trim()) { setNewQ(q => ({ ...q, options: [...(q.options || []), newQOption.trim()] })); setNewQOption(''); }}}
                    disabled={!newQOption.trim()}
                    style={{ padding: '0.3rem 0.7rem', borderRadius: '6px', background: 'var(--accent-blue)', color: '#fff', border: 'none', cursor: newQOption.trim() ? 'pointer' : 'not-allowed', fontSize: '0.8rem', opacity: newQOption.trim() ? 1 : 0.5 }}>
                    + Ajouter
                  </button>
                </div>
                <div style={{ marginTop: '0.5rem' }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 3 }}>Points pour cette question</label>
                  <input type="number" min={0} value={newQ.points} onChange={e => setNewQ(q => ({ ...q, points: parseInt(e.target.value) || 0 }))} style={{ ...inputS, width: '100px' }} />
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
              <button onClick={() => { setAddingQ(false); setNewQ({ texte: '', type_reponse: 'smiley', options: [], reponse_correcte: [], points: 0, obligatoire: true, nb_smileys: 5, max_note: 10 }); setNewQOption(''); }} style={{ padding: '0.3rem 0.8rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.82rem' }}>Annuler</button>
              <button onClick={addQuestion} disabled={!newQ.texte.trim() || (newQ.type_reponse === 'choix' && (newQ.options || []).length < 2)} style={{ padding: '0.3rem 0.8rem', borderRadius: '6px', background: 'var(--accent-blue)', color: '#fff', border: 'none', cursor: (!newQ.texte.trim() || (newQ.type_reponse === 'choix' && (newQ.options || []).length < 2)) ? 'not-allowed' : 'pointer', fontSize: '0.82rem', fontWeight: 600, opacity: (!newQ.texte.trim() || (newQ.type_reponse === 'choix' && (newQ.options || []).length < 2)) ? 0.5 : 1 }}>{newQ.type_reponse === 'chapitre' ? 'Ajouter le titre' : 'Ajouter la question'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function OngletQuestionnaires({ typesQ = TYPES_QUESTIONNAIRE }) {
  const [questionnaires, setQuestionnaires] = useState([]);
  const [formations, setFormations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newForm, setNewForm] = useState({ titre: '', type: 'positionnement', formation_id: '' });
  const [typeFilter, setTypeFilter] = useState('tous');
  const [wordImport, setWordImport] = useState(null); // { questions, titre, type, saving }
  const [showWordInstructions, setShowWordInstructions] = useState(false);
  const wordFileRef = useRef(null);

  const inputS = { padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.85rem', width: '100%', boxSizing: 'border-box' };

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const [qrs, fors] = await Promise.all([
        apiFetch('/questionnaires'),
        apiFetch('/formations?limit=500'),
      ]);
      setQuestionnaires(qrs.data || []);
      setFormations(fors.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  async function creer() {
    if (!newForm.titre.trim()) return;
    const d = await apiFetch('/questionnaires', { method: 'POST', body: JSON.stringify({ titre: newForm.titre, type: newForm.type, formation_id: newForm.formation_id || null }) });
    setCreating(false);
    setNewForm({ titre: '', type: 'positionnement', formation_id: '' });
    charger();
    setSelectedId(d.questionnaire_id);
  }

  async function handleWordFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';
      const token = localStorage.getItem('gf_token');
      const resp = await fetch(API_BASE + '/questionnaires/import-word', {
        method: 'POST', body: fd,
        headers: token ? { Authorization: 'Bearer ' + token } : {},
      });
      const json = await resp.json();
      if (json.error) return alert('Erreur: ' + json.error);
      const titre = file.name.replace(/\.docx?$/i, '').replace(/[-_]/g, ' ');
      setWordImport({
        questions: (json.questions || []).map(q => ({ ...q, reponse_correcte: q.reponse_correcte || [] })),
        titre, type: 'positionnement', formation_id: '', saving: false, warnings: json.warnings || []
      });
    } catch (e) { alert('Erreur lecture fichier: ' + e.message); }
    e.target.value = '';
  }

  async function saveWordImport() {
    if (!wordImport || !wordImport.titre.trim()) return;
    setWordImport(w => ({ ...w, saving: true }));
    try {
      const q = await apiFetch('/questionnaires', { method: 'POST', body: JSON.stringify({ titre: wordImport.titre, type: wordImport.type, formation_id: wordImport.formation_id || null }) });
      for (let i = 0; i < wordImport.questions.length; i++) {
        const qu = wordImport.questions[i];
        await apiFetch('/questionnaires/' + q.questionnaire_id + '/questions', {
          method: 'POST', body: JSON.stringify({
            texte: qu.texte,
            type_reponse: qu.type_reponse,
            options: qu.options,
            reponse_correcte: qu.reponse_correcte || [],
            points: qu.points || 0,
            image_url: qu.image_url || null,
            ordre: i + 1,
          })
        });
      }
      setWordImport(null); charger(); setSelectedId(q.questionnaire_id);
    } catch (e) { alert('Erreur: ' + e.message); setWordImport(w => ({ ...w, saving: false })); }
  }

  if (selectedId) return (
    <QuestionnaireDetail
      qId={selectedId}
      formations={formations}
      typesQ={typesQ}
      onBack={(refresh) => { setSelectedId(null); if (refresh) charger(); }}
    />
  );

  const filtered = typeFilter === 'tous' ? questionnaires : questionnaires.filter(q => q.type === typeFilter);

  return (
    <div>
      {/* Section Satisfaction (repliable) */}
      <SectionRepliable variante="titre" title="⭐ Satisfaction" defaultOpen={false}>
        <OngletSatisfaction />
      </SectionRepliable>
      <div style={{ borderTop: '1px solid var(--border)', margin: '1rem 0' }} />

      {/* Filtres et action */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {[{ val: 'tous', label: 'Tous', color: '#475569', bg: 'var(--bg-elevated)' }, ...typesQ].map(t => (
          <button key={t.val} onClick={() => setTypeFilter(t.val)}
            style={{ padding: '0.3rem 0.8rem', borderRadius: '20px', border: '1px solid var(--border)', cursor: 'pointer', fontSize: '0.82rem', fontWeight: typeFilter === t.val ? 700 : 400,
              background: typeFilter === t.val ? (t.bg || 'var(--accent-blue)') : 'var(--bg-elevated)',
              color: typeFilter === t.val ? (t.color || '#fff') : 'var(--text-secondary)' }}>
            {t.label}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <input ref={wordFileRef} type="file" accept=".doc,.docx" style={{ display: 'none' }} onChange={handleWordFile} />
        <button onClick={() => setShowWordInstructions(true)} style={{ padding: '0.4rem 0.9rem', borderRadius: '8px', background: '#7c3aed', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>📄 Importer Word</button>
        <button onClick={() => setCreating(true)} style={{ padding: '0.4rem 1rem', borderRadius: '8px', background: 'var(--accent-blue)', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>+ Nouveau questionnaire</button>
      </div>

      {/* Modale consignes import Word */}
      {showWordInstructions && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: 'var(--bg-card)', borderRadius: '14px', padding: '1.5rem', width: '100%', maxWidth: 640, maxHeight: '88vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
            <div style={{ fontWeight: 700, fontSize: '1.05rem', color: 'var(--text-primary)' }}>📄 Importer depuis Word — Consignes de mise en forme</div>

            {/* Bloc questions */}
            <div style={{ background: 'var(--bg-elevated)', borderRadius: '10px', padding: '0.9rem 1rem', border: '1px solid var(--border)' }}>
              <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#2563eb', marginBottom: '0.5rem' }}>❶ Questions</div>
              <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.83rem', color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                <li>Chaque question commence par un <b>numéro suivi d'un point</b> ou d'une parenthèse : <code>1.</code> ou <code>1)</code></li>
                <li>La question peut être sur <b>une ou plusieurs lignes</b> avant les réponses</li>
                <li>Laissez une ligne vide entre chaque question pour mieux les séparer</li>
              </ul>
              <div style={{ marginTop: '0.5rem', background: '#f1f5f9', borderRadius: '6px', padding: '0.5rem 0.8rem', fontSize: '0.78rem', color: '#334155', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>{'1. Quel est le rôle du formateur ?\na) Transmettre des savoirs\nb) Évaluer les stagiaires\nc) Les deux\n\n2) Comment appelle-t-on la loi de 1971 ?\na) Loi Faure\nb) Loi Delors\nc) Loi Haby'}</div>
            </div>

            {/* Bloc choix multiple */}
            <div style={{ background: 'var(--bg-elevated)', borderRadius: '10px', padding: '0.9rem 1rem', border: '1px solid var(--border)' }}>
              <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#16a34a', marginBottom: '0.5rem' }}>❷ Choix multiples — réponses</div>
              <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.83rem', color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                <li>Chaque option commence par une <b>lettre suivie d'un point ou d'une parenthèse</b> : <code>a.</code> <code>a)</code> <code>A.</code> <code>A)</code></li>
                <li>Pour marquer la <b>bonne réponse</b>, ajoutez <code>*</code> devant la lettre ou faite la apparaître en <b>gras</b> dans Word : <code>*a)</code> ou <code><b>a)</b></code></li>
                <li>Plusieurs bonnes réponses possibles : marquez chacune avec <code>*</code></li>
              </ul>
              <div style={{ marginTop: '0.5rem', background: '#f0fdf4', borderRadius: '6px', padding: '0.5rem 0.8rem', fontSize: '0.78rem', color: '#166534', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>{'1. Quels sont les types de contrat ?\n*a) CDI\nb) CDD\n*c) Intérim\nd) Aucun'}</div>
            </div>

            {/* Bloc points */}
            <div style={{ background: 'var(--bg-elevated)', borderRadius: '10px', padding: '0.9rem 1rem', border: '1px solid var(--border)' }}>
              <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#f59e0b', marginBottom: '0.5rem' }}>❸ Points (optionnel)</div>
              <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.83rem', color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                <li>Ajoutez <code>(X pts)</code> ou <code>[X points]</code> à la fin du texte de la question</li>
                <li>Exemple : <code>1. Quelle est la durée légale ? (2 pts)</code></li>
              </ul>
            </div>

            {/* Bloc autres types */}
            <div style={{ background: 'var(--bg-elevated)', borderRadius: '10px', padding: '0.9rem 1rem', border: '1px solid var(--border)' }}>
              <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#7c3aed', marginBottom: '0.5rem' }}>❹ Autres types de questions</div>
              <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.83rem', color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                <li><b>Oui/Non</b> : réponses <code>a) Oui</code> et <code>b) Non</code> uniquement → détecté automatiquement</li>
                <li><b>Texte libre</b> : question sans options → détecté automatiquement</li>
                <li><b>Smileys / Note</b> : à définir manuellement après import dans la liste de révision</li>
                <li><b>Photos illustratives</b> : insérez-les directement dans le document Word sous la question — elles seront importées automatiquement</li>
              </ul>
            </div>

            {/* Avertissement */}
            <div style={{ padding: '0.7rem 1rem', background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '8px', fontSize: '0.8rem', color: '#92400e' }}>
              ⚠ <b>Conseil :</b> Utilisez un fichier <code>.docx</code> (Word 2007 et plus récent). Après import, vous pourrez réviser et corriger chaque question avant de créer le questionnaire.
            </div>

            <div style={{ display: 'flex', gap: '0.8rem', justifyContent: 'flex-end', marginTop: '0.3rem' }}>
              <button onClick={() => setShowWordInstructions(false)} style={{ padding: '0.45rem 1rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.85rem' }}>Fermer</button>
              <button onClick={() => { setShowWordInstructions(false); wordFileRef.current?.click(); }} style={{ padding: '0.45rem 1.2rem', borderRadius: '8px', background: '#7c3aed', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem' }}>📂 Choisir le fichier Word</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal import Word */}
      {wordImport && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: 'var(--bg-card)', borderRadius: '14px', padding: '1.5rem', width: '100%', maxWidth: 680, maxHeight: '85vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ fontWeight: 700, fontSize: '1.05rem', color: 'var(--text-primary)' }}>📄 Importer depuis Word</div>
            <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap' }}>
              <div style={{ flex: 2, minWidth: 180 }}>
                <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'block', marginBottom: 3 }}>Titre du questionnaire</label>
                <input value={wordImport.titre} onChange={e => setWordImport(w => ({ ...w, titre: e.target.value }))} style={inputS} />
              </div>
              <div style={{ flex: 1, minWidth: 140 }}>
                <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'block', marginBottom: 3 }}>Type</label>
                <select value={wordImport.type} onChange={e => setWordImport(w => ({ ...w, type: e.target.value }))} style={inputS}>
                  {typesQ.map(t => <option key={t.val} value={t.val}>{t.label}</option>)}
                </select>
              </div>
              <div style={{ flex: 1, minWidth: 150 }}>
                <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'block', marginBottom: 3 }}>Formation (optionnel)</label>
                <select value={wordImport.formation_id} onChange={e => setWordImport(w => ({ ...w, formation_id: e.target.value }))} style={inputS}>
                  <option value="">Toutes formations</option>
                  {formations.map(f => <option key={f.formation_id} value={f.formation_id}>{f.intitule}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
                {wordImport.questions.length} question(s) détectée(s) — vérifiez les types et les points :
              </div>
              {(() => {
                const totalPts = wordImport.questions.reduce((s, q) => s + (q.points || 0), 0);
                const avecCorrection = wordImport.questions.filter(q => q.type_reponse === 'choix' && (q.reponse_correcte || []).length > 0).length;
                const avecChoix = wordImport.questions.filter(q => q.type_reponse === 'choix').length;
                return totalPts > 0 ? (
                  <span style={{ fontSize: '0.78rem', padding: '0.15rem 0.6rem', borderRadius: '12px', background: '#f0fdf4', border: '1px solid #86efac', color: '#166534', fontWeight: 600 }}>
                    Total : {totalPts} pts · {avecCorrection}/{avecChoix} question(s) avec correction auto
                  </span>
                ) : null;
              })()}
            </div>
            {wordImport.warnings && wordImport.warnings.length > 0 && wordImport.warnings.map((w, i) => (
              <div key={i} style={{ padding: '0.5rem 0.8rem', background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '6px', fontSize: '0.8rem', color: '#92400e' }}>
                ⚠ {w}
              </div>
            ))}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {wordImport.questions.map((q, i) => (
                <div key={i} style={{ background: 'var(--bg-elevated)', borderRadius: '8px', padding: '0.5rem 0.8rem', border: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', minWidth: 22 }}>{i + 1}.</span>
                    <input value={q.texte} onChange={e => setWordImport(w => { const qs = [...w.questions]; qs[i] = { ...qs[i], texte: e.target.value }; return { ...w, questions: qs }; })}
                      style={{ ...inputS, flex: 1, minWidth: 140, fontSize: '0.82rem' }} />
                    <select value={q.type_reponse} onChange={e => setWordImport(w => { const qs = [...w.questions]; qs[i] = { ...qs[i], type_reponse: e.target.value }; return { ...w, questions: qs }; })}
                      style={{ ...inputS, width: 120, fontSize: '0.8rem' }}>
                      {TYPES_REPONSE.map(t => <option key={t.val} value={t.val}>{t.label}</option>)}
                    </select>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <label style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>pts :</label>
                      <input type="number" min={0} value={q.points || 0}
                        onChange={e => setWordImport(w => { const qs = [...w.questions]; qs[i] = { ...qs[i], points: parseInt(e.target.value) || 0 }; return { ...w, questions: qs }; })}
                        style={{ ...inputS, width: 52, fontSize: '0.8rem', textAlign: 'center' }} />
                    </div>
                    <button onClick={() => setWordImport(w => ({ ...w, questions: w.questions.filter((_, j) => j !== i) }))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '1rem', padding: '0 4px' }}>✕</button>
                  </div>
                  {q.image_url && (
                    <div style={{ marginTop: '0.4rem', marginLeft: 28 }}>
                      <img src={(process.env.REACT_APP_API_URL || 'http://localhost:3002/api').replace('/api','') + q.image_url} alt="" style={{ maxHeight: 80, borderRadius: 4, border: '1px solid var(--border)' }} />
                    </div>
                  )}
                  {q.options && q.options.length > 0 && (
                    <div style={{ marginTop: '0.4rem', marginLeft: 28, display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                      {q.type_reponse === 'choix' && (
                        <div style={{ fontSize: '0.7rem', color: '#16a34a', fontWeight: 600, marginBottom: 2 }}>
                          ☑ Cochez la/les bonne(s) réponse(s) pour la correction automatique :
                        </div>
                      )}
                      {q.options.map((opt, oi) => {
                        const rc = q.reponse_correcte || [];
                        const isCorrect = rc.includes(opt);
                        return (
                          <div key={oi} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                            {q.type_reponse === 'choix' && (
                              <input type="checkbox" checked={isCorrect}
                                onChange={e => setWordImport(w => {
                                  const qs = [...w.questions];
                                  const prevRc = qs[i].reponse_correcte || [];
                                  qs[i] = { ...qs[i], reponse_correcte: e.target.checked ? [...prevRc, opt] : prevRc.filter(v => v !== opt) };
                                  return { ...w, questions: qs };
                                })}
                                style={{ accentColor: '#16a34a', width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }}
                              />
                            )}
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.15rem 0.5rem', borderRadius: '4px',
                              background: isCorrect ? '#f0fdf4' : 'var(--bg-card)',
                              border: '1px solid ' + (isCorrect ? '#16a34a' : 'var(--border)'),
                              fontSize: '0.78rem',
                              color: isCorrect ? '#166534' : 'var(--text-secondary)',
                              fontWeight: isCorrect ? 700 : 400 }}>
                              {String.fromCharCode(65 + oi)}- {opt}
                              {q.options_images && q.options_images[oi] && (
                                <img src={(process.env.REACT_APP_API_URL || 'http://localhost:3002/api').replace('/api','') + q.options_images[oi]} alt="" style={{ maxHeight: 36, borderRadius: 3 }} />
                              )}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setWordImport(null)} style={{ padding: '0.45rem 1rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.85rem' }}>Annuler</button>
              <button onClick={saveWordImport} disabled={wordImport.saving || !wordImport.titre.trim() || wordImport.questions.length === 0}
                style={{ padding: '0.45rem 1.2rem', borderRadius: '8px', background: '#7c3aed', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem' }}>
                {wordImport.saving ? '⏳ Création...' : '✓ Créer le questionnaire'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Formulaire creation */}
      {creating && (
        <div style={{ padding: '1rem', borderRadius: '10px', background: 'var(--bg-elevated)', border: '2px solid var(--accent-blue)', marginBottom: '1rem' }}>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.8rem', color: 'var(--text-primary)' }}>Nouveau questionnaire</div>
          <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: 2, minWidth: 200 }}>
              <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 3 }}>Titre</label>
              <input value={newForm.titre} onChange={e => setNewForm(f => ({ ...f, titre: e.target.value }))} style={inputS} placeholder="Ex: Positionnement securite incendie" autoFocus />
            </div>
            <div style={{ flex: 1, minWidth: 150 }}>
              <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 3 }}>Type</label>
              <select value={newForm.type} onChange={e => setNewForm(f => ({ ...f, type: e.target.value }))} style={inputS}>
                {typesQ.map(t => <option key={t.val} value={t.val}>{t.label}</option>)}
              </select>
            </div>
            {!typesQ.find(t => t.val === newForm.type)?.is_common && (
              <div style={{ flex: 1, minWidth: 160 }}>
                <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 3 }}>Formation (optionnel)</label>
                <select value={newForm.formation_id} onChange={e => setNewForm(f => ({ ...f, formation_id: e.target.value }))} style={inputS}>
                  <option value="">Toutes formations</option>
                  {formations.map(f => <option key={f.formation_id} value={f.formation_id}>{f.intitule}</option>)}
                </select>
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              <button onClick={() => setCreating(false)} style={{ padding: '0.4rem 0.8rem', borderRadius: '7px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.82rem' }}>Annuler</button>
              <button onClick={creer} disabled={!newForm.titre.trim()} style={{ padding: '0.4rem 0.9rem', borderRadius: '7px', background: 'var(--accent-blue)', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem' }}>Creer</button>
            </div>
          </div>
        </div>
      )}

      {/* Liste */}
      {loading ? <Loading /> : filtered.length === 0 ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)', background: 'var(--bg-elevated)', borderRadius: '10px', fontSize: '0.9rem' }}>
          Aucun questionnaire. Cliquez sur "+ Nouveau questionnaire" pour commencer.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.8rem' }}>
          {filtered.map(q => {
            const typeCfg = typesQ.find(t => t.val === q.type);
            const formation = formations.find(f => f.formation_id === q.formation_id);
            return (
              <div key={q.questionnaire_id} onClick={() => setSelectedId(q.questionnaire_id)}
                style={{ padding: '1rem', borderRadius: '10px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', cursor: 'pointer', transition: 'border-color 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent-blue)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
                  <span style={{ padding: '0.15rem 0.55rem', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 700, background: typeCfg?.bg, color: typeCfg?.color }}>{typeCfg?.label}</span>
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      if (window.confirm(`Supprimer le questionnaire "${q.titre}" et toutes ses questions ?`)) {
                        apiFetch('/questionnaires/' + q.questionnaire_id, { method: 'DELETE' })
                          .then(() => charger())
                          .catch(err => alert('Erreur : ' + err.message));
                      }
                    }}
                    title="Supprimer"
                    style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '0.85rem', padding: '0 2px', lineHeight: 1 }}>
                    ✕
                  </button>
                </div>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-primary)', marginBottom: '0.3rem' }}>{q.titre}</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                  {q.nb_questions} question{q.nb_questions > 1 ? 's' : ''}
                  {formation && ' — ' + formation.intitule}
                  {!q.formation_id && q.type !== 'satisfaction' && ' — Toutes formations'}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}



// ─── Onglet Documents communs ─────────────────────────────────────
const API_BASE_DC = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';

const USAGES = [
  { val: '',            label: '— Non associé —' },
  { val: 'convocation', label: 'Convocation' },
  { val: 'devis',       label: 'Devis' },
  { val: 'convention',  label: 'Convention' },
  { val: 'contrat',     label: 'Contrat de mission' },
  { val: 'commande',    label: 'Commande formateur' },
  { val: 'inscription', label: "Fiche d'inscription" },
];

function DocRow({ doc, isEditing, token, onEdit, onCancelEdit, onSave, onDelete }) {
  const [localDoc, setLocalDoc] = useState({ ...doc });
  const [inlineUsage, setInlineUsage] = useState(doc.usage || '');
  const [saveMsg, setSaveMsg] = useState('');
  useEffect(() => { setLocalDoc({ ...doc }); setInlineUsage(doc.usage || ''); }, [doc.id, doc.usage]); // eslint-disable-line

  const usageLabelColor = inlineUsage ? 'var(--text-primary)' : 'var(--text-tertiary)';

  function telecharger() {
    fetch(`${API_BASE_DC}/documents-communs/${doc.id}/download`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob()).then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = doc.nom_original;
        a.click();
      });
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px 100px', gap: '0.8rem', alignItems: 'center', padding: '0.75rem 1rem', borderRadius: '8px', background: 'var(--bg-card)', border: '1px solid var(--border)', marginBottom: '0.4rem' }}>
      {/* Nom + description */}
      <div>
        <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={doc.nom_original}>{doc.nom_original}</div>
        {isEditing ? (
          <input value={localDoc.description || ''} onChange={e => setLocalDoc(d => ({ ...d, description: e.target.value }))}
            placeholder="Description (optionnel)"
            style={{ width: '100%', marginTop: '0.25rem', padding: '0.2rem 0.4rem', borderRadius: '4px', border: '1px solid var(--border)', fontSize: '0.78rem', background: 'var(--bg-elevated)', color: 'var(--text-primary)', boxSizing: 'border-box' }} />
        ) : (
          <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>{doc.description || '—'}</div>
        )}
      </div>

      {/* Menu déroulant usage */}
      {isEditing ? (
        <select value={localDoc.usage || ''}
          onChange={e => setLocalDoc(d => ({ ...d, usage: e.target.value || null }))}
          style={{ padding: '0.35rem 0.6rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.85rem', width: '100%' }}>
          {USAGES.map(u => <option key={u.val} value={u.val}>{u.label}</option>)}
        </select>
      ) : (
        <div>
          <select value={inlineUsage}
            onChange={e => {
              const v = e.target.value;
              setInlineUsage(v);
              setSaveMsg('');
              onSave({ ...doc, usage: v || null }, (ok, err) => {
                setSaveMsg(ok ? '✓' : ('❌ ' + (err || 'Erreur')));
                setTimeout(() => setSaveMsg(''), 3000);
              });
            }}
            style={{ padding: '0.35rem 0.6rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: usageLabelColor, fontSize: '0.85rem', width: '100%', cursor: 'pointer' }}>
            {USAGES.map(u => <option key={u.val} value={u.val}>{u.label}</option>)}
          </select>
          {saveMsg && <div style={{ fontSize: '0.72rem', marginTop: '0.2rem', color: couleurMessage(niveauMessage(saveMsg)) }}>{saveMsg}</div>}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: '0.3rem', justifyContent: 'flex-end' }}>
        {isEditing ? (
          <>
            <button onClick={() => onSave(localDoc)} title="Enregistrer"
              style={{ padding: '0.3rem 0.7rem', borderRadius: '5px', border: 'none', background: '#059669', color: '#fff', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 700 }}>✓ Sauv.</button>
            <button onClick={onCancelEdit} title="Annuler"
              style={{ padding: '0.3rem 0.6rem', borderRadius: '5px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.82rem' }}>✕</button>
          </>
        ) : (
          <>
            <button onClick={telecharger} title="Télécharger"
              style={{ padding: '0.3rem 0.5rem', borderRadius: '5px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.8rem' }}>⬇</button>
            <button onClick={onEdit} title="Modifier"
              style={{ padding: '0.3rem 0.5rem', borderRadius: '5px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.8rem' }}>✏</button>
            <button onClick={() => onDelete(doc.id, doc.nom_original)} title="Supprimer"
              style={{ padding: '0.3rem 0.5rem', borderRadius: '5px', border: 'none', background: 'rgba(239,68,68,0.1)', color: '#ef4444', cursor: 'pointer', fontSize: '0.8rem' }}>🗑</button>
          </>
        )}
      </div>
    </div>
  );
}

function OngletDocsCommuns() {
  const { token } = useAuth();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState('');
  const [editingId, setEditingId] = useState(null);
  const fileRef = React.useRef();

  const charger = () => {
    setLoading(true);
    fetch(`${API_BASE_DC}/documents-communs`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { setDocs(d.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  };
  useEffect(() => { charger(); }, []);

  async function uploader(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true); setMsg('');
    const fd = new FormData();
    fd.append('fichier', file);
    try {
      const r = await fetch(`${API_BASE_DC}/documents-communs`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      const d = await r.json();
      if (!r.ok) { setMsg('Erreur : ' + (d.error || 'inconnue')); }
      else { setMsg('Fichier ajouté.'); charger(); }
    } catch { setMsg('Erreur réseau'); }
    finally { setUploading(false); fileRef.current.value = ''; }
  }

  async function sauvegarder(doc, callback) {
    setDocs(prev => prev.map(d => d.id === doc.id ? { ...d, ...doc } : d));
    try {
      const r = await fetch(`${API_BASE_DC}/documents-communs/${doc.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(doc),
      });
      if (r.ok) { setEditingId(null); charger(); if (callback) callback(true); }
      else { const d = await r.json(); setMsg('Erreur : ' + d.error); charger(); if (callback) callback(false, d.error); }
    } catch { setMsg('Erreur réseau'); charger(); if (callback) callback(false, 'Erreur réseau'); }
  }

  async function supprimer(id, nom) {
    if (!window.confirm(`Supprimer "${nom}" ?`)) return;
    try {
      await fetch(`${API_BASE_DC}/documents-communs/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      charger();
    } catch { setMsg('Erreur réseau'); }
  }

  return (
    <div style={{ maxWidth: 760 }}>
      <p style={{ fontSize: '0.87rem', color: 'var(--text-secondary)', marginBottom: '1.2rem', lineHeight: 1.6 }}>
        Les documents déposés ici sont joints automatiquement aux emails selon le type associé.<br />
        Formats acceptés : PDF, DOC, DOCX — taille max 20 Mo.
      </p>

      {/* Zone d'upload */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginBottom: '1.5rem', padding: '1rem 1.2rem', background: 'var(--bg-elevated)', border: '2px dashed var(--border)', borderRadius: '10px' }}>
        <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" style={{ display: 'none' }} onChange={uploader} />
        <button onClick={() => fileRef.current.click()} disabled={uploading}
          style={{ padding: '0.5rem 1.2rem', borderRadius: '8px', border: 'none', background: '#E87722', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '0.88rem' }}>
          {uploading ? '⏳ Envoi...' : '+ Ajouter un document'}
        </button>
        <span style={{ fontSize: '0.82rem', color: 'var(--text-tertiary)' }}>PDF, DOC, DOCX — 20 Mo max</span>
        {msg && <span style={{ fontSize: '0.82rem', color: couleurMessage(niveauMessage(msg)) }}>{msg}</span>}
      </div>

      {/* En-tête colonnes */}
      {docs.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px 100px', gap: '0.8rem', padding: '0.4rem 1rem', fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          <span>Fichier / Description</span>
          <span>Associé à</span>
          <span style={{ textAlign: 'right' }}>Actions</span>
        </div>
      )}

      {loading && <div style={{ color: 'var(--text-tertiary)', fontSize: '0.9rem' }}>Chargement…</div>}
      {!loading && docs.length === 0 && (
        <div style={{ color: 'var(--text-tertiary)', fontSize: '0.88rem', padding: '1rem', textAlign: 'center', background: 'var(--bg-elevated)', borderRadius: '8px' }}>
          Aucun document commun enregistré.
        </div>
      )}

      {docs.map(doc => (
        <DocRow key={doc.id} doc={doc}
          isEditing={editingId === doc.id}
          token={token}
          onEdit={() => setEditingId(doc.id)}
          onCancelEdit={() => setEditingId(null)}
          onSave={sauvegarder}
          onDelete={supprimer}
        />
      ))}
    </div>
  );
}

// ─── Page principale ─────────────────────────────────────────────

// ─── Onglet Messages ──────────────────────────────────────────────
const TEMPLATE_VARS = {
  devis_envoi:                 ['devis_id','intitule_formation','client_name','total_ht','societe_nom','responsable'],
  devis_relance_manuelle:      ['devis_id','intitule_formation','total_ht','expire_date','client_name','societe_nom','responsable'],
  devis_relance_auto:          ['devis_id','intitule_formation','total_ht','societe_nom','responsable'],
  fiche_inscription_envoi:     ['intitule_formation','societe_nom','responsable'],
  fiche_inscription_lien_envoi:['intitule_formation','notes_client','societe_nom','responsable'],
  fiche_inscription_relance_auto:['intitule_formation','societe_nom','responsable'],
  convention_envoi:            ['intitule_formation','client_name','societe_nom','responsable'],
  convention_relance_auto:     ['intitule_formation','client_name','societe_nom','responsable'],
  convocation_auto:            ['intitule_formation','session_id','societe_nom','responsable'],
  formateur_bon_commande:      ['formateur_nom','numero_commande','session_id','intitule_formation','dates_session','societe_nom','responsable'],
  formateur_contrat_mission:   ['formateur_nom','societe_nom','responsable'],
  formateur_acces_session:     ['formateur_nom','session_id','intitule_formation','dates_session','date_debut','date_fin','pin','societe_nom','responsable'],
  envoi_documents_groupe:      ['intitule_formation','session_id','liste_stagiaires','societe_nom'],
  desinscription_envoi:        ['stagiaire_nom','intitule_formation','session_id','date_session','client_name','societe_nom','responsable'],
  report_session_envoi:        ['intitule_formation','session_id','dates_session','date_debut','societe_nom','responsable'],
  annulation_session_envoi:    ['intitule_formation','session_id','date_session','dates_session','societe_nom','responsable'],
  reouverture_session_envoi:   ['intitule_formation','session_id','date_session','dates_session','societe_nom','responsable'],
  changement_session_envoi:    ['stagiaire_nom','intitule_formation','new_session_id','dates_session','date_debut','societe_nom','responsable'],
  sms_confirmation:            ['prenom','intitule_formation','date_debut'],
  sms_rappel:                  ['prenom','intitule_formation','date_debut','lieu'],
  recyclage_relance:           ['stagiaire_nom','employeur','intitule_formation','derniere_formation','date_echeance','societe_nom','responsable'],
};

function TemplateSection({ tpl, getEdit, setEdit, insertVar, saveTemplate, resetTemplate, msgs, savingKey, resetKey, lastFocusRef }) {
  const vars = TEMPLATE_VARS[tpl.cle] || [];
  const isSaving    = savingKey === tpl.cle;
  const isResetting = resetKey  === tpl.cle;
  const msg = msgs[tpl.cle] || '';
  return (
    <SectionRepliable variante="titre" title={`${tpl.canal === 'email' ? '📧' : '📱'} ${tpl.objectif}`}>
      <span style={{ fontSize: '0.68rem', color: '#94a3b8', fontFamily: 'monospace', background: 'var(--bg-card)', padding: '0.1rem 0.4rem', borderRadius: '4px', display: 'inline-block', marginBottom: '0.9rem' }}>{tpl.cle}</span>

      {vars.length > 0 && (
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', fontWeight: 600, marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Variables — cliquer pour insérer à la position du curseur
          </div>
          <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
            {vars.map(v => (
              <button key={v} onClick={() => insertVar(tpl.cle, v)}
                style={{ fontSize: '0.72rem', padding: '0.18rem 0.55rem', borderRadius: '6px', border: '1px solid rgba(232,119,34,0.35)', background: 'rgba(232,119,34,0.07)', color: '#E87722', cursor: 'pointer', fontFamily: 'monospace', transition: 'background 0.12s' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(232,119,34,0.18)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(232,119,34,0.07)'; }}>
                {`{{${v}}}`}
              </button>
            ))}
          </div>
        </div>
      )}

      {tpl.canal !== 'sms' && (
        <div style={{ marginBottom: '0.7rem' }}>
          <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Sujet de l'email</label>
          <input type="text"
            value={getEdit(tpl.cle, 'sujet')}
            onChange={e => setEdit(tpl.cle, 'sujet', e.target.value)}
            onFocus={e => { lastFocusRef.current = { cle: tpl.cle, field: 'sujet', el: e.target }; }}
            style={{ ...inputStyle }}
          />
        </div>
      )}

      <div style={{ marginBottom: '0.9rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.25rem' }}>
          <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
            {tpl.canal === 'sms' ? 'Message SMS' : 'Corps du message'}
          </label>
          {tpl.canal === 'sms' && (
            <span style={{ fontSize: '0.72rem', fontWeight: 600,
              color: getEdit(tpl.cle, 'corps').length > 500 ? '#ef4444' : getEdit(tpl.cle, 'corps').length > 460 ? '#d97706' : 'var(--text-tertiary)' }}>
              {getEdit(tpl.cle, 'corps').length}/500 caractères
            </span>
          )}
        </div>
        <textarea
          value={getEdit(tpl.cle, 'corps')}
          onChange={e => setEdit(tpl.cle, 'corps', e.target.value)}
          onFocus={e => { lastFocusRef.current = { cle: tpl.cle, field: 'corps', el: e.target }; }}
          rows={tpl.canal === 'sms' ? 3 : 5}
          style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.65, fontFamily: 'inherit', fontSize: '0.88rem' }}
        />
        <p style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', margin: '0.2rem 0 0' }}>
          {tpl.canal === 'sms'
            ? 'Le message est tronqué à 500 caractères. {{lieu}} vaut " (lieu : 106 rue Costes et Bellonte - BULLY-LES-MINES)" si une adresse est renseignée sur la session, ou rien du tout si elle est absente.'
            : 'Les pièces jointes (programme, convocation…), le bouton de signature et la civilité de fin sont ajoutés automatiquement.'}
        </p>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
        <button className="btn-primary" onClick={() => saveTemplate(tpl.cle)} disabled={isSaving || isResetting}
          style={{ fontSize: '0.82rem', padding: '0.4rem 1rem' }}>
          {isSaving ? '⏳ Sauvegarde...' : '💾 Sauvegarder'}
        </button>
        <button onClick={() => resetTemplate(tpl.cle)} disabled={isSaving || isResetting}
          style={{ fontSize: '0.78rem', padding: '0.4rem 0.85rem', borderRadius: '6px', border: '1px solid rgba(239,68,68,0.4)', background: 'rgba(239,68,68,0.06)', color: '#f87171', cursor: 'pointer', transition: 'background 0.12s' }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.12)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.06)'; }}>
          {isResetting ? '⏳...' : '↩ Réinitialiser'}
        </button>
        {msg && (
          <span style={{ fontSize: '0.82rem', color: couleurMessage(niveauMessage(msg)), fontWeight: 600 }}>
            {msg}
          </span>
        )}
      </div>
    </SectionRepliable>
  );
}

function OngletMessages() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMap, setEditMap] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [resetKey, setResetKey] = useState(null);
  const [msgs, setMsgs] = useState({});
  const lastFocusRef = useRef(null);

  useEffect(() => {
    apiFetch('/message-templates').then(d => {
      const list = d.data || [];
      setTemplates(list);
      const map = {};
      list.forEach(t => { map[t.cle] = { sujet: t.sujet, corps: t.corps }; });
      setEditMap(map);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const getEdit = (cle, field) => editMap[cle]?.[field] ?? (templates.find(t => t.cle === cle)?.[field] ?? '');
  const setEdit = (cle, field, val) => setEditMap(m => ({ ...m, [cle]: { ...(m[cle] || {}), [field]: val } }));

  async function saveTemplate(cle) {
    setSavingKey(cle);
    try {
      await apiFetch(`/message-templates/${cle}`, { method: 'PUT', body: JSON.stringify({ sujet: getEdit(cle,'sujet'), corps: getEdit(cle,'corps') }) });
      setMsgs(m => ({ ...m, [cle]: '✓ Sauvegardé' }));
      setTimeout(() => setMsgs(m => ({ ...m, [cle]: '' })), 3000);
    } catch(e) { setMsgs(m => ({ ...m, [cle]: `❌ ${e.message}` })); }
    finally { setSavingKey(null); }
  }

  async function resetTemplate(cle) {
    if (!window.confirm('Réinitialiser ce message aux valeurs par défaut ?')) return;
    setResetKey(cle);
    try {
      const d = await apiFetch(`/message-templates/${cle}/reset`, { method: 'POST' });
      setEditMap(m => ({ ...m, [cle]: { sujet: d.sujet, corps: d.corps } }));
      setTemplates(ts => ts.map(t => t.cle === cle ? { ...t, sujet: d.sujet, corps: d.corps } : t));
      setMsgs(m => ({ ...m, [cle]: '✓ Réinitialisé' }));
      setTimeout(() => setMsgs(m => ({ ...m, [cle]: '' })), 3000);
    } catch(e) { setMsgs(m => ({ ...m, [cle]: `❌ ${e.message}` })); }
    finally { setResetKey(null); }
  }

  function insertVar(cle, varName) {
    const tag = `{{${varName}}}`;
    if (!lastFocusRef.current || lastFocusRef.current.cle !== cle) {
      setEdit(cle, 'corps', (getEdit(cle,'corps') || '') + tag);
      return;
    }
    const { field, el } = lastFocusRef.current;
    const cur = getEdit(cle, field) || '';
    const start = el.selectionStart ?? cur.length;
    const end   = el.selectionEnd   ?? cur.length;
    const newVal = cur.slice(0, start) + tag + cur.slice(end);
    setEdit(cle, field, newVal);
    setTimeout(() => { el.focus(); el.setSelectionRange(start + tag.length, start + tag.length); }, 10);
  }

  if (loading) return <Loading />;

  const clients    = templates.filter(t => t.destinataire === 'client');
  const formateurs = templates.filter(t => t.destinataire === 'formateur');
  const sms        = templates.filter(t => t.canal === 'sms');

  const tplProps = tpl => ({ key: tpl.cle, tpl, getEdit, setEdit, insertVar, saveTemplate, resetTemplate, msgs, savingKey, resetKey, lastFocusRef });

  return (
    <div>
      <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1.2rem', lineHeight: 1.6 }}>
        Personnalisez les messages envoyés par email ou SMS. Les variables
        <code style={{ background: 'var(--bg-elevated)', padding: '0.1rem 0.4rem', borderRadius: '4px', fontSize: '0.8rem', margin: '0 0.2rem' }}>{'{{variable}}'}</code>
        sont remplacées automatiquement à l'envoi.
      </p>

      {sms.length > 0 && (
        <>
          <SectionTitle>SMS stagiaires</SectionTitle>
          {sms.map(tpl => <TemplateSection {...tplProps(tpl)} />)}
        </>
      )}

      {clients.length > 0 && (
        <>
          <SectionTitle>Messages clients</SectionTitle>
          {clients.map(tpl => <TemplateSection {...tplProps(tpl)} />)}
        </>
      )}

      {formateurs.length > 0 && (
        <>
          <SectionTitle>Messages formateurs</SectionTitle>
          {formateurs.map(tpl => <TemplateSection {...tplProps(tpl)} />)}
        </>
      )}
    </div>
  );
}

// ─── Types de questionnaire (liste configurable) ─────────────────

function TypesQEditor({ params, onChange }) {
  function parse(raw) {
    if (!raw) return TYPES_QUESTIONNAIRE;
    try { return JSON.parse(raw); } catch { return TYPES_QUESTIONNAIRE; }
  }
  const raw = params.types_questionnaire;
  const init = React.useMemo(() => parse(raw), [raw]); // eslint-disable-line
  const [types, setTypesLocal] = React.useState(init);
  const [newType, setNewType] = React.useState({ label: '', color: '#6366f1', bg: '#eef2ff', is_common: false });
  const [editing, setEditing] = React.useState(null);

  React.useEffect(() => { setTypesLocal(parse(raw)); }, [raw]); // eslint-disable-line

  async function setTypes(arr) {
    setTypesLocal(arr);
    onChange('types_questionnaire', JSON.stringify(arr));
    try { await apiFetch('/parametres', { method: 'PUT', body: JSON.stringify({ types_questionnaire: JSON.stringify(arr) }) }); } catch {}
  }

  function slugify(label) {
    return label.toLowerCase()
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '');
  }

  function ajouter() {
    const label = newType.label.trim();
    if (!label) return;
    const val = slugify(label);
    if (!val || types.some(t => t.val === val)) { alert('Ce type existe déjà ou le libellé est invalide.'); return; }
    setTypes([...types, { val, label, color: newType.color, bg: newType.bg, is_common: newType.is_common }]);
    setNewType({ label: '', color: '#6366f1', bg: '#eef2ff', is_common: false });
  }

  function supprimer(i) {
    if (!window.confirm(`Supprimer le type "${types[i].label}" ?`)) return;
    setTypes(types.filter((_, idx) => idx !== i));
  }

  function update(i, field, value) {
    setTypes(types.map((t, idx) => idx === i ? { ...t, [field]: value } : t));
  }

  function monter(i) {
    if (i === 0) return;
    const arr = [...types]; [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]]; setTypes(arr);
  }

  function descendre(i) {
    if (i === types.length - 1) return;
    const arr = [...types]; [arr[i], arr[i + 1]] = [arr[i + 1], arr[i]]; setTypes(arr);
  }

  return (
    <div>
      <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: '0.8rem' }}>
        Définissez les types de questionnaire disponibles. Le champ <strong>val</strong> (identifiant interne) est généré automatiquement et ne peut pas être modifié.
        Activez <strong>Commun</strong> pour qu'un questionnaire de ce type soit sélectionné automatiquement sans choix manuel dans la session.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '0.8rem' }}>
        {types.map((t, i) => (
          <div key={t.val} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '8px', padding: '0.4rem 0.6rem', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
              <button onClick={() => monter(i)} disabled={i === 0} style={{ background: 'none', border: 'none', cursor: i === 0 ? 'default' : 'pointer', color: i === 0 ? '#94a3b8' : 'var(--text-secondary)', fontSize: '0.7rem', padding: '0 2px', lineHeight: 1 }}>▲</button>
              <button onClick={() => descendre(i)} disabled={i === types.length - 1} style={{ background: 'none', border: 'none', cursor: i === types.length - 1 ? 'default' : 'pointer', color: i === types.length - 1 ? '#94a3b8' : 'var(--text-secondary)', fontSize: '0.7rem', padding: '0 2px', lineHeight: 1 }}>▼</button>
            </div>
            <span style={{ padding: '0.15rem 0.6rem', borderRadius: '10px', fontSize: '0.72rem', fontWeight: 700, background: t.bg, color: t.color, minWidth: '80px', textAlign: 'center', flexShrink: 0 }}>{t.label}</span>
            <span style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', fontFamily: 'monospace', minWidth: '100px' }}>{t.val}</span>
            {editing === i ? (
              <>
                <input value={t.label} onChange={e => update(i, 'label', e.target.value)}
                  style={{ ...inputStyle, width: 150, fontSize: '0.82rem', padding: '0.2rem 0.4rem' }} placeholder="Libellé" />
                <label style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 3 }}>Couleur
                  <input type="color" value={t.color} onChange={e => update(i, 'color', e.target.value)} style={{ marginLeft: 4, width: 28, height: 22, border: 'none', background: 'none', cursor: 'pointer' }} />
                </label>
                <label style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 3 }}>Fond
                  <input type="color" value={t.bg} onChange={e => update(i, 'bg', e.target.value)} style={{ marginLeft: 4, width: 28, height: 22, border: 'none', background: 'none', cursor: 'pointer' }} />
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.75rem', cursor: 'pointer', color: 'var(--text-secondary)' }}>
                  <input type="checkbox" checked={!!t.is_common} onChange={e => update(i, 'is_common', e.target.checked)} />
                  Commun
                </label>
                <button onClick={() => setEditing(null)} style={{ padding: '0.2rem 0.6rem', borderRadius: '5px', border: 'none', background: '#16a34a', color: '#fff', cursor: 'pointer', fontSize: '0.78rem' }}>OK</button>
              </>
            ) : (
              <>
                <span style={{ fontSize: '0.72rem', color: t.is_common ? '#16a34a' : '#94a3b8', fontWeight: 600 }}>{t.is_common ? '● Commun' : '○ Spécifique'}</span>
                <div style={{ flex: 1 }} />
                <button onClick={() => setEditing(i)} style={{ padding: '0.15rem 0.5rem', borderRadius: '5px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.75rem' }}>✏</button>
                <button onClick={() => supprimer(i)} style={{ padding: '0.15rem 0.5rem', borderRadius: '5px', border: 'none', background: 'rgba(239,68,68,0.1)', color: '#ef4444', cursor: 'pointer', fontSize: '0.75rem' }}>✕</button>
              </>
            )}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center', background: 'var(--bg-elevated)', border: '1px dashed var(--border)', borderRadius: '8px', padding: '0.5rem 0.6rem' }}>
        <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 600 }}>Nouveau type :</span>
        <input value={newType.label} onChange={e => setNewType(n => ({ ...n, label: e.target.value }))}
          onKeyDown={e => e.key === 'Enter' && ajouter()}
          placeholder="Libellé (ex: Évaluation formative)"
          style={{ ...inputStyle, flex: 1, minWidth: 180, fontSize: '0.82rem', padding: '0.2rem 0.4rem' }} />
        <label style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 3 }}>Couleur
          <input type="color" value={newType.color} onChange={e => setNewType(n => ({ ...n, color: e.target.value }))} style={{ marginLeft: 4, width: 28, height: 22, border: 'none', background: 'none', cursor: 'pointer' }} />
        </label>
        <label style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 3 }}>Fond
          <input type="color" value={newType.bg} onChange={e => setNewType(n => ({ ...n, bg: e.target.value }))} style={{ marginLeft: 4, width: 28, height: 22, border: 'none', background: 'none', cursor: 'pointer' }} />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.75rem', cursor: 'pointer', color: 'var(--text-secondary)' }}>
          <input type="checkbox" checked={newType.is_common} onChange={e => setNewType(n => ({ ...n, is_common: e.target.checked }))} />
          Commun
        </label>
        <button onClick={ajouter} style={{ padding: '0.3rem 0.8rem', borderRadius: '6px', border: 'none', background: '#E87722', color: '#fff', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, whiteSpace: 'nowrap' }}>+ Ajouter</button>
      </div>
    </div>
  );
}

// ─── Onglet Organismes financeurs ────────────────────────────────

function OngletOrganismesFinanceurs() {
  const { token } = useAuth();
  const [organismes, setOrganismes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ nom: '', type: '', siret: '', adresse: '', contact_nom: '', contact_email: '', contact_tel: '', notes: '' });
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  // ── Types de financeurs ──
  const [types, setTypes] = useState([]);
  const [newType, setNewType] = useState('');
  const [editingType, setEditingType] = useState(null); // { type_id, label }
  const [typeMsg, setTypeMsg] = useState('');

  const API = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';
  const hdrs = () => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' });

  function loadTypes() {
    fetch(`${API}/types-financeurs`, { headers: hdrs() })
      .then(r => r.json()).then(d => setTypes(Array.isArray(d) ? d : [])).catch(() => {});
  }

  async function addType() {
    if (!newType.trim()) return;
    try {
      const r = await fetch(`${API}/types-financeurs`, { method: 'POST', headers: hdrs(), body: JSON.stringify({ label: newType.trim() }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setNewType(''); setTypeMsg('✓ Type ajouté'); setTimeout(() => setTypeMsg(''), 2000); loadTypes();
    } catch (e) { setTypeMsg('❌ ' + e.message); }
  }

  async function saveType(t) {
    try {
      const r = await fetch(`${API}/types-financeurs/${t.type_id}`, { method: 'PUT', headers: hdrs(), body: JSON.stringify({ label: editingType.label, position: t.position }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setEditingType(null); setTypeMsg('✓ Modifié'); setTimeout(() => setTypeMsg(''), 2000); loadTypes();
    } catch (e) { setTypeMsg('❌ ' + e.message); }
  }

  async function deleteType(id, label) {
    if (!window.confirm(`Supprimer le type "${label}" ?`)) return;
    try {
      await fetch(`${API}/types-financeurs/${id}`, { method: 'DELETE', headers: hdrs() });
      setTypeMsg('✓ Supprimé'); setTimeout(() => setTypeMsg(''), 2000); loadTypes();
    } catch (e) { setTypeMsg('❌ ' + e.message); }
  }

  function load() {
    setLoading(true);
    fetch(`${API}/organismes-financeurs`, { headers: hdrs() })
      .then(r => r.json()).then(d => setOrganismes(Array.isArray(d) ? d : []))
      .catch(() => {}).finally(() => setLoading(false));
  }
  useEffect(() => { load(); loadTypes(); }, []);

  function resetForm() {
    setForm({ nom: '', type: '', siret: '', adresse: '', contact_nom: '', contact_email: '', contact_tel: '', notes: '' });
    setEditing(null);
  }

  function startEdit(o) {
    setForm({ nom: o.nom || '', type: o.type || '', siret: o.siret || '', adresse: o.adresse || '', contact_nom: o.contact_nom || '', contact_email: o.contact_email || '', contact_tel: o.contact_tel || '', notes: o.notes || '' });
    setEditing(o.organisme_id);
    setShowForm(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.nom.trim()) { setMsg('❌ Nom requis'); return; }
    setSaving(true); setMsg('');
    try {
      const method = editing ? 'PUT' : 'POST';
      const url = editing ? `${API}/organismes-financeurs/${editing}` : `${API}/organismes-financeurs`;
      const r = await fetch(url, { method, headers: hdrs(), body: JSON.stringify(form) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error || 'Erreur'); }
      setMsg(editing ? '✓ Organisme modifié' : '✓ Organisme ajouté');
      setTimeout(() => setMsg(''), 2500);
      setShowForm(false); resetForm(); load();
    } catch (e) { setMsg('❌ ' + e.message); }
    finally { setSaving(false); }
  }

  async function handleDelete(id) {
    if (!window.confirm('Supprimer cet organisme financeur ?')) return;
    try {
      await fetch(`${API}/organismes-financeurs/${id}`, { method: 'DELETE', headers: hdrs() });
      setMsg('✓ Supprimé');
      setTimeout(() => setMsg(''), 2000);
      load();
    } catch (e) { setMsg('❌ ' + e.message); }
  }

  // TYPE_OPTIONS importé depuis src/constants/financeurs.js (voir import en tête de fichier)

  const fldStyle = { ...inputStyle, marginBottom: 0 };

  return (
    <div style={{ maxWidth: '680px' }}>
      <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
        Gérez les types de financeurs, puis les organismes (OPCO, France Travail, Région…) utilisés dans le module de préfacturation.
      </p>

      {/* ── Section Types de financeurs ── */}
      <div style={{ marginBottom: '2rem', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', padding: '1rem 1.2rem' }}>
        <h4 style={{ margin: '0 0 0.9rem', fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Types de financeurs
        </h4>

        {typeMsg && (
          <div style={{ padding: '0.3rem 0.6rem', borderRadius: '5px', fontSize: '0.8rem', marginBottom: '0.7rem',
            ...styleMessage(niveauMessage(typeMsg)) }}>
            {typeMsg}
          </div>
        )}

        {/* Liste */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.9rem' }}>
          {types.map(t => (
            <div key={t.type_id} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '0.25rem 0.6rem', borderRadius: '20px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', fontSize: '0.83rem' }}>
              {editingType?.type_id === t.type_id ? (
                <>
                  <input
                    value={editingType.label}
                    onChange={e => setEditingType(et => ({ ...et, label: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter') saveType(t); if (e.key === 'Escape') setEditingType(null); }}
                    style={{ ...inputStyle, width: '110px', padding: '0.15rem 0.4rem', fontSize: '0.82rem' }}
                    autoFocus
                  />
                  <button onClick={() => saveType(t)} style={{ background: 'none', border: 'none', color: 'var(--accent-emerald)', cursor: 'pointer', fontSize: '0.85rem', padding: '0 0.2rem' }}>✓</button>
                  <button onClick={() => setEditingType(null)} style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', fontSize: '0.85rem', padding: '0 0.2rem' }}>✕</button>
                </>
              ) : (
                <>
                  <span style={{ color: 'var(--text-primary)' }}>{t.label}</span>
                  <button onClick={() => setEditingType({ type_id: t.type_id, label: t.label })} style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', fontSize: '0.75rem', padding: '0 0.1rem', lineHeight: 1 }} title="Renommer">✏</button>
                  <button onClick={() => deleteType(t.type_id, t.label)} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '0.75rem', padding: '0 0.1rem', lineHeight: 1 }} title="Supprimer">✕</button>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Ajout rapide */}
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <input
            value={newType}
            onChange={e => setNewType(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addType()}
            placeholder="Nouveau type (ex : Agefiph)"
            style={{ ...inputStyle, width: '220px' }}
          />
          <button onClick={addType} disabled={!newType.trim()}
            style={{ padding: '0.4rem 0.9rem', borderRadius: '6px', border: 'none', background: 'var(--accent-orange)', color: '#fff', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', opacity: newType.trim() ? 1 : 0.4 }}>
            + Ajouter
          </button>
        </div>
      </div>

      {msg && (
        <div style={{ padding: '0.4rem 0.7rem', borderRadius: '6px', fontSize: '0.82rem', marginBottom: '0.9rem',
          ...styleMessage(niveauMessage(msg)) }}>
          {msg}
        </div>
      )}

      <button onClick={() => { resetForm(); setShowForm(s => !s); }}
        style={{ padding: '0.4rem 1rem', borderRadius: '7px', border: 'none', background: 'var(--accent-orange)', color: '#fff', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', marginBottom: '1.2rem' }}>
        {showForm && !editing ? '✕ Annuler' : '+ Nouvel organisme'}
      </button>

      {showForm && (
        <form onSubmit={handleSubmit} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', padding: '1.2rem', marginBottom: '1.5rem' }}>
          <h4 style={{ margin: '0 0 1rem', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {editing ? 'Modifier l\'organisme' : 'Nouvel organisme'}
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.7rem', marginBottom: '0.7rem' }}>
            <div>
              <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '0.25rem' }}>Nom *</label>
              <input value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))} style={fldStyle} placeholder="ex : OPCO EP" required />
            </div>
            <div>
              <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '0.25rem' }}>Type</label>
              <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                style={{ ...fldStyle, cursor: 'pointer' }}>
                <option value="">— sélectionner —</option>
                {types.map(t => <option key={t.type_id} value={t.label}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '0.25rem' }}>SIRET</label>
              <input value={form.siret} onChange={e => setForm(f => ({ ...f, siret: e.target.value }))} style={fldStyle} placeholder="14 chiffres" maxLength={14} />
            </div>
            <div>
              <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '0.25rem' }}>Contact</label>
              <input value={form.contact_nom} onChange={e => setForm(f => ({ ...f, contact_nom: e.target.value }))} style={fldStyle} placeholder="Nom du contact" />
            </div>
            <div>
              <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '0.25rem' }}>Email contact</label>
              <input type="email" value={form.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))} style={fldStyle} placeholder="contact@opco.fr" />
            </div>
            <div>
              <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '0.25rem' }}>Tél contact</label>
              <input value={form.contact_tel} onChange={e => setForm(f => ({ ...f, contact_tel: e.target.value }))} style={fldStyle} placeholder="0X XX XX XX XX" />
            </div>
          </div>
          <div style={{ marginBottom: '0.7rem' }}>
            <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '0.25rem' }}>Adresse</label>
            <input value={form.adresse} onChange={e => setForm(f => ({ ...f, adresse: e.target.value }))} style={fldStyle} placeholder="Adresse postale" />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '0.25rem' }}>Notes</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              rows={2} style={{ ...fldStyle, resize: 'vertical' }} placeholder="Informations complémentaires…" />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button type="submit" disabled={saving}
              style={{ padding: '0.4rem 1.1rem', borderRadius: '7px', border: 'none', background: 'var(--accent-orange)', color: '#fff', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
              {saving ? 'Enregistrement…' : (editing ? '💾 Modifier' : '✓ Ajouter')}
            </button>
            <button type="button" onClick={() => { setShowForm(false); resetForm(); }}
              style={{ padding: '0.4rem 0.9rem', borderRadius: '7px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', fontSize: '0.85rem', cursor: 'pointer' }}>
              Annuler
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>Chargement…</div>
      ) : organismes.length === 0 ? (
        <div style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem', padding: '1.5rem', textAlign: 'center', border: '1px dashed var(--border)', borderRadius: '8px' }}>
          Aucun organisme financeur enregistré
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          {organismes.map(o => (
            <div key={o.organisme_id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '8px', padding: '0.8rem 1rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{o.nom}</span>
                  {o.type && (
                    <span style={{ padding: '0.1rem 0.5rem', borderRadius: '8px', fontSize: '0.72rem', fontWeight: 700, background: 'rgba(232,119,34,0.12)', color: 'var(--accent-orange)' }}>
                      {o.type}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', marginTop: '0.15rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  {o.contact_nom && <span>👤 {o.contact_nom}</span>}
                  {o.contact_email && <span>✉ {o.contact_email}</span>}
                  {o.contact_tel && <span>📞 {o.contact_tel}</span>}
                  {o.siret && <span>SIRET : {o.siret}</span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
                <button onClick={() => startEdit(o)}
                  style={{ padding: '0.3rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', fontSize: '0.8rem', cursor: 'pointer' }}>
                  ✏
                </button>
                <button onClick={() => handleDelete(o.organisme_id)}
                  style={{ padding: '0.3rem 0.7rem', borderRadius: '6px', border: '1px solid rgba(239,68,68,0.3)', background: 'transparent', color: '#f87171', fontSize: '0.8rem', cursor: 'pointer' }}>
                  🗑
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Onglet configuration Fiche de Progrès Formateur ────────────────────────
function OngletFicheProgres() {
  const API = process.env.REACT_APP_API_URL || `${window.location.origin}/api`;
  const token = localStorage.getItem('gf_token');
  const [colonnes, setColonnes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    fetch(`${API}/fiche-progres/colonnes`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { setColonnes(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const update = (idx, field, val) => {
    setColonnes(prev => prev.map((c, i) => i === idx ? { ...c, [field]: val } : c));
  };

  const ajouter = () => {
    const cle = `col_${Date.now()}`;
    setColonnes(prev => [...prev, { cle, libelle: 'Nouvelle colonne', ordre: prev.length + 1, largeur_pct: 25, actif: true, type: 'textarea', options_json: null }]);
  };

  const supprimer = (idx) => {
    setColonnes(prev => prev.filter((_, i) => i !== idx));
  };

  const monter = (idx) => {
    if (idx === 0) return;
    setColonnes(prev => { const a = [...prev]; [a[idx-1], a[idx]] = [a[idx], a[idx-1]]; return a.map((c,i) => ({...c, ordre: i+1})); });
  };

  const descendre = (idx) => {
    if (idx === colonnes.length - 1) return;
    setColonnes(prev => { const a = [...prev]; [a[idx], a[idx+1]] = [a[idx+1], a[idx]]; return a.map((c,i) => ({...c, ordre: i+1})); });
  };

  const sauvegarder = async () => {
    setSaving(true); setMsg('');
    try {
      const r = await fetch(`${API}/fiche-progres/colonnes`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(colonnes.map((c, i) => ({ ...c, ordre: i + 1 }))),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setMsg('✓ Colonnes enregistrées');
      setTimeout(() => setMsg(''), 3000);
    } catch (e) { setMsg('Erreur : ' + e.message); }
    setSaving(false);
  };

  const totalPct = colonnes.reduce((s, c) => s + (Number(c.largeur_pct) || 0), 0);

  if (loading) return <div style={{ padding: '1rem', color: 'var(--text-secondary)' }}>Chargement…</div>;

  return (
    <div>
      <h2 className="card-title" style={{ marginBottom: '0.3rem' }}>Fiche Progrès — Configuration du tableau</h2>
      <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: '1.2rem' }}>
        Définissez les colonnes du tableau de la fiche de progrès formateur. Le champ <strong>Entête</strong> est le titre affiché en haut de chaque colonne.
        Pour les colonnes de type <em>Menu déroulant</em>, saisissez les choix disponibles (un par ligne).
        Les colonnes inactives sont masquées. La somme des largeurs est normalisée à 100 % dans le PDF.
      </p>

      {msg && <div style={{ padding: '0.5rem 0.8rem', borderRadius: 6, marginBottom: '1rem', fontSize: '0.85rem',
                            ...styleMessage(niveauMessage(msg)) }}>{msg}</div>}

      {/* Ligne de légende */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem',
                    padding: '0.25rem 0.8rem', fontSize: '0.72rem', fontWeight: 700,
                    color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        <div style={{ width: 36 }} />
        <div style={{ flex: 2 }}>Entête du tableau</div>
        <div style={{ flex: 1 }}>Clé</div>
        <div style={{ width: 80, textAlign: 'center' }}>Largeur</div>
        <div style={{ width: 110, textAlign: 'center' }}>Type de saisie</div>
        <div style={{ width: 50, textAlign: 'center' }}>Actif</div>
        <div style={{ width: 32 }} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '1rem' }}>
        {colonnes.map((col, idx) => (
          <React.Fragment key={idx}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem',
                        padding: '0.6rem 0.8rem', borderRadius: 8,
                        background: col.actif ? 'var(--bg-elevated)' : '#f1f5f9',
                        border: '1px solid var(--border)', opacity: col.actif ? 1 : 0.6 }}>
            {/* Ordre */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <button onClick={() => monter(idx)} disabled={idx === 0}
                style={{ padding: '1px 6px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg)', cursor: 'pointer', fontSize: '0.7rem', lineHeight: 1 }}>▲</button>
              <button onClick={() => descendre(idx)} disabled={idx === colonnes.length - 1}
                style={{ padding: '1px 6px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg)', cursor: 'pointer', fontSize: '0.7rem', lineHeight: 1 }}>▼</button>
            </div>
            {/* Entête */}
            <input value={col.libelle} onChange={e => update(idx, 'libelle', e.target.value)}
              style={{ flex: 2, padding: '0.35rem 0.6rem', borderRadius: 6, border: '1px solid var(--border)',
                       background: 'var(--bg)', fontSize: '0.88rem' }}
              placeholder="Entête de la colonne" />
            {/* Clé (lecture seule) */}
            <input value={col.cle} readOnly
              style={{ flex: 1, padding: '0.35rem 0.6rem', borderRadius: 6, border: '1px solid var(--border)',
                       background: '#f1f5f9', fontSize: '0.78rem', color: 'var(--text-secondary)', fontFamily: 'monospace' }}
              title="Clé technique (non modifiable)" />
            {/* Largeur % */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <input type="number" min={5} max={80} value={col.largeur_pct}
                onChange={e => update(idx, 'largeur_pct', Number(e.target.value))}
                style={{ width: 56, padding: '0.35rem 0.4rem', borderRadius: 6, border: '1px solid var(--border)',
                         background: 'var(--bg)', fontSize: '0.88rem', textAlign: 'center' }} />
              <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>%</span>
            </div>
            {/* Type */}
            <select value={col.type || 'textarea'} onChange={e => update(idx, 'type', e.target.value)}
              style={{ padding: '0.35rem 0.4rem', borderRadius: 6, border: '1px solid var(--border)',
                       background: 'var(--bg)', fontSize: '0.82rem' }}>
              <option value="textarea">Texte libre</option>
              <option value="select">Menu déroulant</option>
            </select>
            {/* Actif */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: '0.82rem',
                            color: col.actif ? '#16a34a' : '#6b7280', fontWeight: 600, whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={!!col.actif} onChange={e => update(idx, 'actif', e.target.checked)} />
              Actif
            </label>
            {/* Supprimer */}
            <button onClick={() => supprimer(idx)}
              style={{ padding: '0.3rem 0.6rem', borderRadius: 6, border: '1px solid rgba(239,68,68,0.3)',
                       background: 'transparent', color: '#f87171', cursor: 'pointer', fontSize: '0.85rem' }}>
              🗑
            </button>
          </div>
          {/* Options (si type=select) */}
          {col.type === 'select' && (
            <div style={{ marginTop: '-0.2rem', padding: '0.5rem 0.8rem 0.6rem 3rem',
                          background: '#f8faff', border: '1px solid var(--border)',
                          borderTop: 'none', borderRadius: '0 0 8px 8px' }}>
              <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
                OPTIONS (une par ligne)
              </label>
              <textarea rows={4}
                value={(() => { try { return (JSON.parse(col.options_json || '[]')).join('\n'); } catch { return col.options_json || ''; } })()}
                onChange={e => {
                  const opts = e.target.value.split('\n').map(s => s.trim()).filter(Boolean);
                  update(idx, 'options_json', JSON.stringify(opts));
                }}
                style={{ width: '100%', padding: '0.3rem 0.5rem', borderRadius: 6, border: '1px solid var(--border)',
                         background: 'var(--bg)', fontSize: '0.83rem', fontFamily: 'inherit', resize: 'vertical' }}
                placeholder="Point positif&#10;Point d'amélioration&#10;Difficulté rencontrée" />
            </div>
          )}
          </React.Fragment>
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', flexWrap: 'wrap' }}>
        <button onClick={ajouter}
          style={{ padding: '0.4rem 0.9rem', borderRadius: 7, border: '1.5px dashed #1a3668',
                   background: '#f0f5ff', color: '#1a3668', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>
          + Ajouter une colonne
        </button>
        <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
          Total largeurs : {totalPct}% {totalPct !== 100 ? `(sera normalisé à 100% dans le PDF)` : ''}
        </span>
        <div style={{ flex: 1 }} />
        <button onClick={sauvegarder} disabled={saving}
          style={{ padding: '0.45rem 1.2rem', borderRadius: 8, border: 'none',
                   background: '#1a3668', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: '0.88rem' }}>
          {saving ? '⏳ Enregistrement…' : '💾 Enregistrer'}
        </button>
      </div>
    </div>
  );
}

// ── Onglet Numérotation ───────────────────────────────────────────────────────
function NumeroRow({ label, description, current, onApply }) {
  const [valeur, setValeur] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg,    setMsg]    = useState('');

  useEffect(() => { if (current != null) setValeur(String(current)); }, [current]);

  async function appliquer() {
    const n = parseInt(valeur, 10);
    if (!n || n < 1) { setMsg('❌ Entier >= 1 requis'); return; }
    setSaving(true); setMsg('');
    try {
      const next = await onApply(n);
      setMsg(`✓ Fixé à ${next}`);
      setTimeout(() => setMsg(''), 3500);
    } catch (e) { setMsg(`❌ ${e.message}`); }
    finally { setSaving(false); }
  }

  return (
    <div style={{ padding: '1.1rem 1.2rem', background: 'var(--bg-elevated)', borderRadius: '10px', border: '1px solid var(--border)', marginBottom: '1rem' }}>
      <div style={{ fontWeight: 700, fontSize: '0.92rem', marginBottom: '0.25rem' }}>{label}</div>
      <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>{description}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', flexWrap: 'wrap' }}>
        <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
          Valeur actuelle : <strong style={{ color: 'var(--text-primary)' }}>{current ?? '—'}</strong>
        </div>
        <input type="number" min="1" value={valeur} onChange={e => setValeur(e.target.value)}
          style={{ width: '120px', padding: '0.45rem 0.6rem', borderRadius: '6px',
            border: '1px solid var(--border)', background: 'var(--bg-card)',
            color: 'var(--text-primary)', fontSize: '0.92rem' }} />
        <button onClick={appliquer} disabled={saving}
          style={{ padding: '0.45rem 1rem', borderRadius: '7px', border: 'none',
            background: '#1a3668', color: '#fff', fontWeight: 700, fontSize: '0.85rem',
            cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
          {saving ? '⏳…' : 'Appliquer'}
        </button>
        {msg && (
          <span style={{ fontSize: '0.83rem', fontWeight: 600,
            color: couleurMessage(niveauMessage(msg)) }}>
            {msg}
          </span>
        )}
      </div>
    </div>
  );
}

function OngletNumerotation() {
  const [seqs,    setSeqs]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch('/admin/sequences').then(setSeqs).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: '1rem', color: 'var(--text-secondary)' }}>Chargement…</div>;

  return (
    <div style={{ maxWidth: '560px' }}>
      <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1.2rem' }}>
        Ces réglages s'appliquent aux prochains documents créés. Utile avant l'import d'anciennes données pour éviter les conflits de numérotation.
      </p>

      <NumeroRow
        label="Numéro d'ordre des devis (NNN)"
        description={`Le numéro NNN dans le format DE-FOR ${new Date().getFullYear().toString().slice(-2)}-NNN. Fonctionne comme un plancher.`}
        current={seqs?.devis_next_ordre}
        onApply={async n => {
          const r = await apiFetch('/admin/devis-ordre', { method: 'POST', body: JSON.stringify({ valeur: n }) });
          setSeqs(s => ({ ...s, devis_next_ordre: r.devis_next_ordre }));
          return r.devis_next_ordre;
        }}
      />

      <NumeroRow
        label="Numéro d'ordre des sessions (XX)"
        description={`Le numéro XX dans le format SE${new Date().getFullYear().toString().slice(-2)}-NN-XX, où NN est le N° de la formation. Fonctionne comme un plancher.`}
        current={seqs?.session_next_ordre}
        onApply={async n => {
          const r = await apiFetch('/admin/session-ordre', { method: 'POST', body: JSON.stringify({ valeur: n }) });
          setSeqs(s => ({ ...s, session_next_ordre: r.session_next_ordre }));
          return r.session_next_ordre;
        }}
      />

      <NumeroRow
        label="Numéro de convention"
        description="Entier simple utilisé comme identifiant de convention (1, 2, 1001…). La prochaine convention créée portera exactement ce numéro."
        current={seqs?.convention_next}
        onApply={async n => {
          const r = await apiFetch('/admin/convention-sequence', { method: 'POST', body: JSON.stringify({ valeur: n }) });
          setSeqs(s => ({ ...s, convention_next: r.convention_next }));
          return r.convention_next;
        }}
      />

      <NumeroRow
        label="Numéro d'ordre des commandes formateur (XXX)"
        description={`Le numéro XXX dans le format FOR${new Date().getFullYear().toString().slice(-2)}-XXX. Fonctionne comme un plancher.`}
        current={seqs?.commande_next_ordre}
        onApply={async n => {
          const r = await apiFetch('/admin/commande-ordre', { method: 'POST', body: JSON.stringify({ valeur: n }) });
          setSeqs(s => ({ ...s, commande_next_ordre: r.commande_next_ordre }));
          return r.commande_next_ordre;
        }}
      />

      <NumeroRow
        label="Numéro d'ordre des préfacturations (NNN)"
        description={`Le numéro NNN dans le format PF-${new Date().getFullYear().toString().slice(-2)}NNN (ex: PF-26001). Fonctionne comme un plancher.`}
        current={seqs?.prefacture_next_ordre}
        onApply={async n => {
          const r = await apiFetch('/admin/prefacture-ordre', { method: 'POST', body: JSON.stringify({ valeur: n }) });
          setSeqs(s => ({ ...s, prefacture_next_ordre: r.prefacture_next_ordre }));
          return r.prefacture_next_ordre;
        }}
      />

      <div style={{ padding: '0.8rem 1rem', background: 'rgba(245,158,11,0.08)',
        border: '1px solid rgba(245,158,11,0.3)', borderRadius: '8px', fontSize: '0.82rem', color: '#d97706' }}>
        ⚠️ À utiliser uniquement avant la création de nouvelles sessions/conventions ou lors d'un import.
      </div>
    </div>
  );
}

// ─── Éditeur de signature email (type Outlook) ──────────────────────────────
function SignatureEditor() {
  const editorRef      = useRef(null);
  const fileRef        = useRef(null);
  const savedRangeRef  = useRef(null);
  const [saving, setSaving]       = useState(false);
  const [msg, setMsg]             = useState('');
  const [preview, setPreview]     = useState(false);
  const [htmlContent, setHtmlContent] = useState('');
  const [selectedImg, setSelectedImg] = useState(null);
  const [imgToolbarPos, setImgToolbarPos] = useState({ top: 0, left: 0 });
  const [imgCustomPx, setImgCustomPx]     = useState('');
  const token = localStorage.getItem('gf_token');

  useEffect(() => {
    fetch(`${API_BASE}/utilisateurs/me/signature`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => {
        const sig = d.signature_email || '';
        setHtmlContent(sig);
        if (editorRef.current) editorRef.current.innerHTML = sig;
      })
      .catch(() => {});
  // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!preview && editorRef.current) {
      editorRef.current.innerHTML = htmlContent;
    }
  // eslint-disable-next-line
  }, [preview]);

  const exec = (cmd, val = null) => {
    document.execCommand(cmd, false, val);
    editorRef.current?.focus();
    setHtmlContent(editorRef.current?.innerHTML || '');
  };

  const insertLink = () => {
    const url = window.prompt('URL du lien (ex: https://cejconcept.groupe-gi.fr) :');
    if (url) exec('createLink', url);
  };

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      savedRangeRef.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const insertImage = e => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      editorRef.current?.focus();
      const sel = window.getSelection();
      if (savedRangeRef.current && sel) {
        sel.removeAllRanges();
        sel.addRange(savedRangeRef.current);
      }
      document.execCommand('insertHTML', false, `<img src="${ev.target.result}" style="max-width:100%;vertical-align:middle">`);
      savedRangeRef.current = null;
      setHtmlContent(editorRef.current?.innerHTML || '');
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleEditorClick = e => {
    if (selectedImg && selectedImg !== e.target) selectedImg.style.outline = '';
    if (e.target.tagName === 'IMG') {
      const img = e.target;
      img.style.outline = '2px solid var(--accent-orange)';
      setSelectedImg(img);
      const currentW = img.style.width ? parseInt(img.style.width) : (img.offsetWidth || '');
      setImgCustomPx(String(currentW));
      const rect = img.getBoundingClientRect();
      const editorRect = editorRef.current.getBoundingClientRect();
      setImgToolbarPos({ top: rect.bottom - editorRect.top + 6, left: Math.max(0, rect.left - editorRect.left) });
    } else {
      setSelectedImg(null);
    }
  };

  const applyImgWidth = val => {
    if (!selectedImg) return;
    selectedImg.style.width = val;
    selectedImg.style.height = 'auto';
    setHtmlContent(editorRef.current?.innerHTML || '');
  };

  const deleteSelectedImg = () => {
    if (!selectedImg) return;
    selectedImg.remove();
    setSelectedImg(null);
    setHtmlContent(editorRef.current?.innerHTML || '');
  };

  const handleSave = async () => {
    setSaving(true); setMsg('');
    const sig = editorRef.current?.innerHTML || '';
    try {
      const r = await fetch(`${API_BASE}/utilisateurs/me/signature`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ signature_email: sig }),
      });
      if (!r.ok) throw new Error('Erreur serveur');
      setHtmlContent(sig);
      setMsg('✓ Signature enregistrée');
      setTimeout(() => setMsg(''), 3000);
    } catch { setMsg('❌ Erreur lors de l\'enregistrement'); }
    finally { setSaving(false); }
  };

  const handleClear = () => {
    if (editorRef.current) editorRef.current.innerHTML = '';
    setHtmlContent('');
  };

  const tb = { padding: '0.3rem 0.55rem', background: 'transparent', border: '1px solid var(--border)', borderRadius: '4px', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '0.82rem', lineHeight: 1 };
  const sep = <span style={{ borderLeft: '1px solid var(--border)', alignSelf: 'stretch', margin: '0 0.1rem' }} />;

  return (
    <div>
      {/* Barre d'outils */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.2rem', alignItems: 'center', padding: '0.5rem 0.6rem', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderBottom: 'none', borderRadius: '8px 8px 0 0' }}>
        <button onMouseDown={e => { e.preventDefault(); exec('bold'); }} style={tb} title="Gras"><strong>G</strong></button>
        <button onMouseDown={e => { e.preventDefault(); exec('italic'); }} style={tb} title="Italique"><em>I</em></button>
        <button onMouseDown={e => { e.preventDefault(); exec('underline'); }} style={{ ...tb, textDecoration: 'underline' }} title="Souligné">S</button>
        <button onMouseDown={e => { e.preventDefault(); exec('strikeThrough'); }} style={{ ...tb, textDecoration: 'line-through' }} title="Barré">B</button>
        {sep}
        <select title="Taille" style={{ ...tb, padding: '0.2rem 0.3rem', background: 'var(--bg-elevated)', color: 'var(--text-primary)' }}
          onMouseDown={e => e.stopPropagation()}
          onChange={e => { exec('fontSize', e.target.value); e.target.value = ''; }}>
          <option value=""     style={{ background: 'var(--bg-card)', color: 'var(--text-primary)' }}>Taille</option>
          <option value="1"    style={{ background: 'var(--bg-card)', color: 'var(--text-primary)' }}>Très petit</option>
          <option value="2"    style={{ background: 'var(--bg-card)', color: 'var(--text-primary)' }}>Petit</option>
          <option value="3"    style={{ background: 'var(--bg-card)', color: 'var(--text-primary)' }}>Normal</option>
          <option value="4"    style={{ background: 'var(--bg-card)', color: 'var(--text-primary)' }}>Grand</option>
          <option value="5"    style={{ background: 'var(--bg-card)', color: 'var(--text-primary)' }}>Très grand</option>
          <option value="6"    style={{ background: 'var(--bg-card)', color: 'var(--text-primary)' }}>Titre</option>
        </select>
        <label title="Couleur du texte" style={{ ...tb, display: 'flex', alignItems: 'center', gap: '0.2rem', cursor: 'pointer' }}>
          <span style={{ fontWeight: 700, color: 'var(--accent-orange)' }}>A</span>
          <input type="color" style={{ width: 18, height: 16, border: 'none', padding: 0, cursor: 'pointer', background: 'none' }}
            onInput={e => exec('foreColor', e.target.value)} />
        </label>
        {sep}
        <button onMouseDown={e => { e.preventDefault(); exec('justifyLeft'); }} style={tb} title="Aligné à gauche">⬅</button>
        <button onMouseDown={e => { e.preventDefault(); exec('justifyCenter'); }} style={tb} title="Centré">≡</button>
        <button onMouseDown={e => { e.preventDefault(); exec('justifyRight'); }} style={tb} title="Aligné à droite">➡</button>
        {sep}
        <button onMouseDown={e => { e.preventDefault(); exec('insertUnorderedList'); }} style={tb} title="Liste à puces">• Liste</button>
        {sep}
        <button onMouseDown={e => { e.preventDefault(); insertLink(); }} style={tb} title="Insérer un lien">🔗</button>
        <label title="Insérer une image (logo, photo...)" style={{ ...tb, cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }} onMouseDown={saveSelection}>
          🖼 Image
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={insertImage} />
        </label>
        {sep}
        <button onMouseDown={e => { e.preventDefault(); exec('removeFormat'); }} style={{ ...tb, color: 'var(--text-tertiary)' }} title="Effacer la mise en forme">✕ Format</button>
        <button onMouseDown={e => { e.preventDefault(); setPreview(v => !v); }}
          style={{ ...tb, marginLeft: 'auto', background: preview ? 'var(--accent-orange)' : 'transparent', color: preview ? '#fff' : 'var(--text-primary)', borderColor: preview ? 'var(--accent-orange)' : 'var(--border)' }}>
          {preview ? '✏ Éditer' : '👁 Aperçu'}
        </button>
      </div>

      {/* Zone d'édition / aperçu */}
      {preview ? (
        <div style={{ minHeight: 220, border: '1px solid var(--border)', borderRadius: '0 0 8px 8px', padding: '1rem', background: '#fff', color: '#1e293b' }}
          dangerouslySetInnerHTML={{ __html: htmlContent }} />
      ) : (
        <div style={{ position: 'relative' }}>
          {selectedImg && (
            <div style={{ position: 'absolute', top: imgToolbarPos.top, left: imgToolbarPos.left, background: '#1e293b', borderRadius: 6, padding: '0.3rem 0.6rem', display: 'flex', gap: '0.3rem', alignItems: 'center', zIndex: 50, boxShadow: '0 2px 10px rgba(0,0,0,0.4)', whiteSpace: 'nowrap' }}>
              <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>Largeur :</span>
              {['25%', '50%', '75%', '100%'].map(p => (
                <button key={p} onMouseDown={e => { e.preventDefault(); applyImgWidth(p); }}
                  style={{ padding: '0.15rem 0.4rem', background: '#334155', border: 'none', borderRadius: 3, color: '#e2e8f0', cursor: 'pointer', fontSize: '0.75rem' }}>
                  {p}
                </button>
              ))}
              <input type="number" min="10" max="2000" value={imgCustomPx}
                onMouseDown={e => e.stopPropagation()}
                onChange={e => { setImgCustomPx(e.target.value); if (e.target.value) applyImgWidth(e.target.value + 'px'); }}
                style={{ width: 58, padding: '0.15rem 0.3rem', background: '#334155', border: '1px solid #475569', borderRadius: 3, color: '#e2e8f0', fontSize: '0.75rem' }}
                placeholder="px" />
              <span style={{ color: '#64748b', fontSize: '0.75rem' }}>px</span>
              <button onMouseDown={e => { e.preventDefault(); deleteSelectedImg(); }}
                style={{ padding: '0.15rem 0.45rem', background: '#7f1d1d', border: 'none', borderRadius: 3, color: '#fca5a5', cursor: 'pointer', fontSize: '0.75rem', marginLeft: '0.2rem' }}>
                🗑
              </button>
            </div>
          )}
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            onClick={handleEditorClick}
            onInput={() => setHtmlContent(editorRef.current?.innerHTML || '')}
            style={{ minHeight: 220, border: '1px solid var(--border)', borderRadius: '0 0 8px 8px', padding: '1rem', background: 'var(--bg-card)', outline: 'none', lineHeight: 1.6, color: 'var(--text-primary)' }}
          />
        </div>
      )}

      <p style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', margin: '0.4rem 0 1rem' }}>
        Conseil : insérez vos images (logo oval, photo, badge Qualiopi) via le bouton 🖼. Elles seront intégrées directement dans les emails sans dépendance externe.
      </p>

      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
        <button className="btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? '⏳ Enregistrement…' : '✓ Sauvegarder la signature'}
        </button>
        <button onClick={handleClear}
          style={{ padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}>
          Effacer
        </button>
        {msg && <span style={{ fontSize: '0.85rem', color: couleurMessage(niveauMessage(msg)) }}>{msg}</span>}
      </div>
    </div>
  );
}

function OngletSignatures() {
  return (
    <div>
      <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.4rem' }}>Ma signature email</h3>
      <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1.4rem', lineHeight: 1.5 }}>
        Cette signature sera automatiquement insérée en bas de chaque email envoyé depuis l'application
        (devis, conventions, fiches d'inscription). Chaque utilisateur possède sa propre signature.
      </p>
      <SignatureEditor />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

// ─── Onglet Satisfaction ──────────────────────────────────────────────────────

const COULEURS_IND = ['#2563eb','#6366f1','#059669','#d97706','#dc2626','#7c3aed','#0891b2','#475569'];

function OngletSatisfaction() {
  const [cfg, setCfg] = useState(null);
  const [indicateurs, setIndicateurs] = useState([]);
  const [loadingCfg, setLoadingCfg] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [newInd, setNewInd] = useState({ nom: '', section_nom: '', couleur: '#2563eb', ordre: 0 });
  const [editInd, setEditInd] = useState(null);

  async function load() {
    try {
      const [c, inds] = await Promise.all([
        apiFetch('/satisfaction/config'),
        apiFetch('/satisfaction/indicateurs'),
      ]);
      setCfg(c);
      setIndicateurs(inds);
    } catch (e) { console.error(e); }
    finally { setLoadingCfg(false); }
  }

  useEffect(() => { load(); }, []);

  async function saveCfg() {
    setSaving(true); setSaveMsg('');
    try {
      await apiFetch('/satisfaction/config', { method: 'PUT', body: JSON.stringify(cfg) });
      setSaveMsg('✓ Configuration enregistrée');
      setTimeout(() => setSaveMsg(''), 3000);
    } catch (e) { setSaveMsg(`❌ ${e.message}`); }
    finally { setSaving(false); }
  }

  async function addIndicateur() {
    if (!newInd.nom.trim()) return;
    try {
      await apiFetch('/satisfaction/indicateurs', { method: 'POST', body: JSON.stringify(newInd) });
      setNewInd({ nom: '', section_nom: '', couleur: '#2563eb', ordre: 0 });
      load();
    } catch (e) { alert(e.message); }
  }

  async function saveIndicateur() {
    if (!editInd?.nom?.trim()) return;
    try {
      await apiFetch(`/satisfaction/indicateurs/${editInd.id}`, { method: 'PUT', body: JSON.stringify(editInd) });
      setEditInd(null); load();
    } catch (e) { alert(e.message); }
  }

  async function deleteIndicateur(id) {
    if (!window.confirm('Supprimer cet indicateur ?')) return;
    try { await apiFetch(`/satisfaction/indicateurs/${id}`, { method: 'DELETE' }); load(); }
    catch (e) { alert(e.message); }
  }

  if (loadingCfg) return <Loading />;

  const rowStyle = { display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 0.8rem', borderRadius: '8px', border: '1px solid var(--border)', marginBottom: '0.5rem', background: 'var(--bg-elevated)' };
  const iStyle = { padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.88rem', flex: 1 };

  return (
    <div>
      <SectionTitle>Règles d'envoi automatique</SectionTitle>
      <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
        Le questionnaire client (à froid) est envoyé automatiquement après la fin de chaque session.
      </p>

      <Field label="Activer l'envoi automatique">
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={cfg?.actif !== false} onChange={e => setCfg(c => ({ ...c, actif: e.target.checked }))} />
          <span style={{ fontSize: '0.9rem', color: 'var(--text-primary)' }}>Envoi automatique activé</span>
        </label>
      </Field>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <Field label="Délai d'envoi après fin de session" hint="En jours">
          <input type="number" min="0" max="60" value={cfg?.delai_envoi_jours ?? 7}
            onChange={e => setCfg(c => ({ ...c, delai_envoi_jours: parseInt(e.target.value) || 0 }))}
            style={inputStyle} />
        </Field>
        <Field label="Délai entre chaque relance" hint="En jours">
          <input type="number" min="1" max="60" value={cfg?.delai_relance_jours ?? 7}
            onChange={e => setCfg(c => ({ ...c, delai_relance_jours: parseInt(e.target.value) || 7 }))}
            style={inputStyle} />
        </Field>
        <Field label="Nombre max de relances">
          <input type="number" min="0" max="5" value={cfg?.nb_max_relances ?? 2}
            onChange={e => setCfg(c => ({ ...c, nb_max_relances: parseInt(e.target.value) || 0 }))}
            style={inputStyle} />
        </Field>
        <Field label="Destinataires">
          <select value={cfg?.destinataires || 'principal'}
            onChange={e => setCfg(c => ({ ...c, destinataires: e.target.value }))} style={inputStyle}>
            <option value="principal">Contact principal uniquement</option>
            <option value="secondaire">Contact secondaire uniquement</option>
            <option value="les_deux">Les deux contacts</option>
          </select>
        </Field>
      </div>

      {saveMsg && (
        <div style={{ padding: '0.4rem 0.8rem', borderRadius: '6px', fontSize: '0.82rem', marginBottom: '0.8rem',
          ...styleMessage(niveauMessage(saveMsg)) }}>
          {saveMsg}
        </div>
      )}

      <button className="btn-primary" onClick={saveCfg} disabled={saving} style={{ marginBottom: '2rem' }}>
        {saving ? '⏳...' : '✓ Enregistrer les règles'}
      </button>

      <SectionTitle>Indicateurs Dashboard</SectionTitle>
      <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: '0.8rem' }}>
        Chaque indicateur correspond au <strong>nom exact d'un chapitre</strong> dans le questionnaire de satisfaction.
        Si le questionnaire évolue, il suffit de mettre à jour le champ "Chapitre" ci-dessous.
      </p>

      {indicateurs.map(ind => (
        editInd?.id === ind.id ? (
          <div key={ind.id} style={{ ...rowStyle, flexDirection: 'column', alignItems: 'stretch', gap: '0.5rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <input placeholder="Nom de l'indicateur" value={editInd.nom} onChange={e => setEditInd(i => ({ ...i, nom: e.target.value }))} style={{ ...iStyle, minWidth: '160px' }} />
              <input placeholder="Chapitre exact du questionnaire" value={editInd.section_nom || ''} onChange={e => setEditInd(i => ({ ...i, section_nom: e.target.value }))} style={{ ...iStyle, minWidth: '200px' }} />
              <input type="number" placeholder="Ordre" value={editInd.ordre || 0} onChange={e => setEditInd(i => ({ ...i, ordre: parseInt(e.target.value) || 0 }))} style={{ ...iStyle, width: '70px', flex: 'none' }} />
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                {COULEURS_IND.map(c => (
                  <button key={c} onClick={() => setEditInd(i => ({ ...i, couleur: c }))} style={{
                    width: 22, height: 22, borderRadius: '50%', background: c, border: editInd.couleur === c ? '2px solid var(--text-primary)' : '2px solid transparent', cursor: 'pointer',
                  }} />
                ))}
              </div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.82rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={editInd.actif !== false} onChange={e => setEditInd(i => ({ ...i, actif: e.target.checked }))} />
              Actif (affiché sur le Dashboard)
            </label>
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              <button className="btn-primary" onClick={saveIndicateur} style={{ fontSize: '0.82rem', padding: '0.3rem 0.8rem' }}>✓ Sauvegarder</button>
              <button className="btn-secondary" onClick={() => setEditInd(null)} style={{ fontSize: '0.82rem', padding: '0.3rem 0.8rem' }}>Annuler</button>
            </div>
          </div>
        ) : (
          <div key={ind.id} style={{ ...rowStyle, opacity: ind.actif ? 1 : 0.5 }}>
            <span style={{ width: 12, height: 12, borderRadius: '50%', background: ind.couleur, flexShrink: 0 }} />
            <span style={{ fontWeight: 600, fontSize: '0.9rem', flex: 1 }}>{ind.nom}</span>
            {ind.section_nom && (
              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                Chapitre : « {ind.section_nom} »
              </span>
            )}
            <span style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', minWidth: '30px' }}>#{ind.ordre}</span>
            <button onClick={() => setEditInd({ ...ind })} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.82rem', color: 'var(--text-secondary)', padding: '0.2rem 0.5rem' }}>✏</button>
            <button onClick={() => deleteIndicateur(ind.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.82rem', color: '#dc2626', padding: '0.2rem 0.5rem' }}>✕</button>
          </div>
        )
      ))}

      <div style={{ ...rowStyle, background: '#f0fdf4', border: '1px dashed #86efac' }}>
        <span style={{ fontSize: '0.82rem', color: '#16a34a', fontWeight: 700, marginRight: '0.3rem' }}>+</span>
        <input placeholder="Nom de l'indicateur (ex: Formateur)" value={newInd.nom} onChange={e => setNewInd(i => ({ ...i, nom: e.target.value }))} style={{ ...iStyle, minWidth: '140px' }} />
        <input placeholder="Chapitre exact du questionnaire" value={newInd.section_nom} onChange={e => setNewInd(i => ({ ...i, section_nom: e.target.value }))} style={{ ...iStyle, minWidth: '180px' }} />
        <input type="number" placeholder="Ordre" value={newInd.ordre} onChange={e => setNewInd(i => ({ ...i, ordre: parseInt(e.target.value) || 0 }))} style={{ ...iStyle, width: '65px', flex: 'none' }} />
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          {COULEURS_IND.map(c => (
            <button key={c} onClick={() => setNewInd(i => ({ ...i, couleur: c }))} style={{
              width: 20, height: 20, borderRadius: '50%', background: c, border: newInd.couleur === c ? '2px solid var(--text-primary)' : '2px solid transparent', cursor: 'pointer',
            }} />
          ))}
        </div>
        <button className="btn-primary" onClick={addIndicateur} style={{ fontSize: '0.82rem', padding: '0.3rem 0.8rem', flexShrink: 0 }}>Ajouter</button>
      </div>

      <p style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', marginTop: '0.8rem' }}>
        Le champ « Chapitre » doit correspondre exactement au texte d'une question de type <em>chapitre</em> dans le questionnaire de satisfaction (casse et accents inclus).
      </p>
    </div>
  );
}

const RGPD_CHAMPS_STAGIAIRES = [
  { cle: 'nom',                 label: 'Nom de famille' },
  { cle: 'prenom',              label: 'Prénom' },
  { cle: 'prenoms',             label: 'Prénoms complets' },
  { cle: 'nom_jeune_fille',     label: 'Nom de jeune fille' },
  { cle: 'civilite',            label: 'Civilité' },
  { cle: 'email',               label: 'Email' },
  { cle: 'tel',                 label: 'Téléphone' },
  { cle: 'date_naissance',      label: 'Date de naissance' },
  { cle: 'lieu_naissance',      label: 'Lieu de naissance' },
  { cle: 'num_securite_sociale',label: 'N° sécurité sociale' },
  { cle: 'adresse',             label: 'Adresse ligne 1' },
  { cle: 'adresse2',            label: 'Adresse ligne 2' },
  { cle: 'cp',                  label: 'Code postal' },
  { cle: 'ville',               label: 'Ville' },
  { cle: 'entreprise',          label: 'Entreprise' },
  { cle: 'poste',               label: 'Poste occupé' },
];
const RGPD_CHAMPS_CLIENTS = [
  { cle: 'contact_prenom',   label: 'Contact principal — Prénom' },
  { cle: 'contact_nom',      label: 'Contact principal — Nom' },
  { cle: 'contact_email',    label: 'Contact principal — Email' },
  { cle: 'contact2_prenom',  label: 'Contact secondaire — Prénom' },
  { cle: 'contact2_nom',     label: 'Contact secondaire — Nom' },
  { cle: 'contact2_email',   label: 'Contact secondaire — Email' },
  { cle: 'tel',              label: 'Téléphone' },
  { cle: 'adresse',          label: 'Adresse' },
  { cle: 'cp',               label: 'Code postal' },
  { cle: 'ville',            label: 'Ville' },
];

const RGPD_DEFAULT = {
  stagiaires: { retention_ans: 5, date_reference: 'derniere_inscription',
    champs: RGPD_CHAMPS_STAGIAIRES.map(c => c.cle) },
  clients:    { retention_ans: 3, date_reference: 'dernier_devis',
    champs: RGPD_CHAMPS_CLIENTS.map(c => c.cle) },
  audit_logs: { retention_ans: 1 },
};

// Durée de conservation des signatures numérisées des formateurs (rgpd_config.signature_formateur = { duree_mois, modifiee_le })
// Contrairement aux autres blocs RGPD, la suppression est AUTOMATIQUE (tâche quotidienne) : échéance, formateur archivé, retrait.
function BlocSignaturesFormateurs({ cfg, onChange }) {
  const sf = cfg.signature_formateur || { duree_mois: 36, modifiee_le: null };
  const [bilan, setBilan] = useState(null);
  useEffect(() => {
    let annule = false;
    const t = setTimeout(() => {
      apiFetch(`/rgpd/signatures-formateurs?duree_mois=${sf.duree_mois}`).then(b => { if (!annule) setBilan(b); }).catch(() => {});
    }, 300);
    return () => { annule = true; clearTimeout(t); };
  }, [sf.duree_mois]);
  const champ = { width: 90, padding: '0.35rem 0.5rem', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.88rem' };
  return (
    <div style={{ marginBottom: '1.5rem', padding: '1rem 1.2rem', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 10 }}>
      <div style={{ fontWeight: 700, marginBottom: '0.3rem' }}>✍ Signatures numérisées des formateurs (attestations)</div>
      <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', margin: '0 0 0.8rem' }}>
        Ces signatures sont <strong>supprimées automatiquement</strong> à l'échéance, à l'archivage de la fiche du formateur ou à son retrait. Un renouvellement est demandé 30 jours puis 7 jours avant l'échéance.
      </p>
      <label style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
        Durée de conservation
        <input type="number" min="1" max="120" value={sf.duree_mois}
          onChange={e => onChange({ duree_mois: Math.max(1, parseInt(e.target.value, 10) || 1), modifiee_le: new Date().toISOString().slice(0, 10) })}
          style={champ} />
        mois
      </label>
      {bilan && (
        <p style={{ fontSize: '0.82rem', margin: '0.6rem 0 0', color: bilan.deja_echues > 0 || bilan.echeant_sous_30j > 0 ? COULEUR_ATTENTION : 'var(--text-secondary)' }}>
          {bilan.total === 0
            ? 'Aucune signature de formateur enregistrée pour le moment.'
            : `Avec cette durée : ${bilan.total} signature${bilan.total > 1 ? 's' : ''} enregistrée${bilan.total > 1 ? 's' : ''} — ${bilan.deja_echues} déjà échue${bilan.deja_echues > 1 ? 's' : ''} (délai de grâce de ${bilan.delai_grace_jours} jours avant suppression), ${bilan.echeant_sous_30j} échéant dans les 30 jours.`}
        </p>
      )}
      <p style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', margin: '0.5rem 0 0' }}>Pensez à cliquer sur « Enregistrer la configuration » ci-dessous.</p>
    </div>
  );
}

function OngletRGPD({ params, onChange, onSave, saving }) {
  const [cfg, setCfg] = useState(() => {
    try { return JSON.parse(params.rgpd_config || 'null') || RGPD_DEFAULT; } catch { return RGPD_DEFAULT; }
  });
  const [alertes, setAlertes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [purging, setPurging] = useState(false);
  const [anonymising, setAnonymising] = useState(null);
  const [msg, setMsg] = useState('');

  function updateCfg(next) {
    setCfg(next);
    onChange('rgpd_config', JSON.stringify(next));
  }

  function setEntityField(entity, field, value) {
    updateCfg({ ...cfg, [entity]: { ...cfg[entity], [field]: value } });
  }

  function toggleChamp(entity, cle) {
    const current = cfg[entity]?.champs || [];
    const next = current.includes(cle) ? current.filter(c => c !== cle) : [...current, cle];
    setEntityField(entity, 'champs', next);
  }

  async function verifier() {
    setLoading(true); setMsg('');
    try { setAlertes(await apiFetch('/rgpd/alertes')); }
    catch (e) { setMsg('Erreur : ' + e.message); }
    finally { setLoading(false); }
  }

  async function anonymiser(id) {
    if (!window.confirm(`Anonymiser définitivement le stagiaire ${id} ?\nCette action est irréversible.`)) return;
    setAnonymising(id);
    try {
      await apiFetch(`/rgpd/stagiaires/${id}/anonymiser`, { method: 'POST' });
      setAlertes(a => ({ ...a, stagiaires: a.stagiaires.filter(s => s.stagiaire_id !== id) }));
      setMsg('Stagiaire anonymisé.');
    } catch (e) { setMsg('Erreur : ' + e.message); }
    finally { setAnonymising(null); }
  }

  async function purgerLogs() {
    if (!window.confirm(`Supprimer les logs de plus de ${alertes?.audit_logs?.seuil_ans} an(s) ?\n(${alertes?.audit_logs?.count} lignes)`)) return;
    setPurging(true);
    try {
      const r = await apiFetch('/rgpd/audit-logs/purger', { method: 'DELETE' });
      setMsg(`${r.supprimees} lignes supprimées.`);
      setAlertes(a => ({ ...a, audit_logs: { ...a.audit_logs, count: 0 } }));
    } catch (e) { setMsg('Erreur : ' + e.message); }
    finally { setPurging(false); }
  }

  const inputNum = { width: '70px', padding: '0.35rem 0.5rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.88rem' };
  const selectStyle = { padding: '0.35rem 0.5rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.88rem' };
  const blockStyle = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', padding: '1.2rem', marginBottom: '1rem' };
  const labelSec = { fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.6rem', display: 'block' };

  function EntityBlock({ title, entity, refOptions, champsOptions }) {
    const ec = cfg[entity] || {};
    return (
      <div style={blockStyle}>
        <h4 style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>{title}</h4>
        <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
          <div>
            <span style={labelSec}>Durée de conservation</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input type="number" min="1" max="99" style={inputNum}
                value={ec.retention_ans || ''}
                onChange={e => setEntityField(entity, 'retention_ans', parseInt(e.target.value) || 1)} />
              <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>an(s)</span>
            </div>
          </div>
          {refOptions && (
            <div>
              <span style={labelSec}>Date de référence</span>
              <select style={selectStyle} value={ec.date_reference || refOptions[0].value}
                onChange={e => setEntityField(entity, 'date_reference', e.target.value)}>
                {refOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          )}
        </div>
        {champsOptions && (
          <div>
            <span style={labelSec}>Champs à anonymiser</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem 1.2rem' }}>
              {champsOptions.map(c => (
                <label key={c.cle} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.83rem', color: 'var(--text-primary)', cursor: 'pointer' }}>
                  <input type="checkbox"
                    checked={(ec.champs || []).includes(c.cle)}
                    onChange={() => toggleChamp(entity, c.cle)} />
                  {c.label}
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <h3 style={{ marginBottom: '0.5rem', color: 'var(--text-primary)' }}>Configuration RGPD</h3>
      <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
        Aucune suppression automatique. Les alertes listent les enregistrements dépassant les seuils configurés pour une action manuelle.
      </p>

      <EntityBlock title="Stagiaires" entity="stagiaires" champsOptions={RGPD_CHAMPS_STAGIAIRES}
        refOptions={[
          { value: 'derniere_inscription', label: 'Date de la dernière inscription' },
          { value: 'updated_at',           label: 'Date de dernière modification' },
          { value: 'created_at',           label: 'Date de création' },
        ]} />

      <EntityBlock title="Clients" entity="clients" champsOptions={RGPD_CHAMPS_CLIENTS}
        refOptions={[
          { value: 'dernier_devis', label: 'Date du dernier devis' },
          { value: 'created_at',    label: 'Date de création' },
        ]} />

      <EntityBlock title="Logs d'audit (adresses IP, connexions)" entity="audit_logs" refOptions={null} champsOptions={null} />

      <BlocSignaturesFormateurs cfg={cfg} onChange={sf => updateCfg({ ...cfg, signature_formateur: sf })} />

      <button className="btn-primary" onClick={onSave} disabled={saving} style={{ marginBottom: '2rem' }}>
        {saving ? 'Enregistrement...' : 'Enregistrer la configuration'}
      </button>

      <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '1.5rem 0' }} />
      <h3 style={{ marginBottom: '0.8rem', color: 'var(--text-primary)' }}>Alertes RGPD</h3>
      <button className="btn-secondary" onClick={verifier} disabled={loading}>
        {loading ? 'Vérification...' : 'Vérifier les alertes'}
      </button>
      {msg && <p style={{ marginTop: '0.8rem', fontSize: '0.85rem', color: couleurMessage(niveauMessage(msg)) }}>{msg}</p>}

      {alertes && (
        <div style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <div>
            <h4 style={{ marginBottom: '0.5rem' }}>
              Stagiaires — {alertes.stagiaires.length} enregistrement(s) à traiter
            </h4>
            {alertes.stagiaires.length === 0
              ? <p style={{ fontSize: '0.85rem', color: '#10b981' }}>Aucun stagiaire à traiter.</p>
              : <div style={{ overflowX: 'auto' }}>
                  <table className="data-table" style={{ width: '100%', fontSize: '0.82rem' }}>
                    <thead><tr><th>ID</th><th>Nom</th><th>Email</th><th>Dernière activité</th><th></th></tr></thead>
                    <tbody>
                      {alertes.stagiaires.map(s => (
                        <tr key={s.stagiaire_id}>
                          <td>{s.stagiaire_id}</td>
                          <td>{s.nom} {s.prenom}</td>
                          <td style={{ color: 'var(--text-secondary)' }}>{s.email || '—'}</td>
                          <td>{s.derniere_activite ? new Date(s.derniere_activite).toLocaleDateString('fr-FR') : '—'}</td>
                          <td>
                            <button className="btn-secondary"
                              style={{ fontSize: '0.75rem', padding: '0.2rem 0.6rem', color: '#ef4444', borderColor: '#ef4444' }}
                              disabled={anonymising === s.stagiaire_id}
                              onClick={() => anonymiser(s.stagiaire_id)}>
                              {anonymising === s.stagiaire_id ? '...' : 'Anonymiser'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
            }
          </div>

          <div>
            <h4 style={{ marginBottom: '0.5rem' }}>
              Clients — {alertes.clients.length} enregistrement(s) à traiter
            </h4>
            {alertes.clients.length === 0
              ? <p style={{ fontSize: '0.85rem', color: '#10b981' }}>Aucun client à traiter.</p>
              : <div style={{ overflowX: 'auto' }}>
                  <table className="data-table" style={{ width: '100%', fontSize: '0.82rem' }}>
                    <thead><tr><th>ID</th><th>Client</th><th>Email contact</th><th>Dernière activité</th></tr></thead>
                    <tbody>
                      {alertes.clients.map(c => (
                        <tr key={c.client_id}>
                          <td>{c.client_id}</td>
                          <td>{c.nom_client}</td>
                          <td style={{ color: 'var(--text-secondary)' }}>{c.contact_email || '—'}</td>
                          <td>{c.derniere_activite ? new Date(c.derniere_activite).toLocaleDateString('fr-FR') : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', marginTop: '0.4rem' }}>
                    La désactivation des clients se fait depuis la fiche client.
                  </p>
                </div>
            }
          </div>

          <div>
            <h4 style={{ marginBottom: '0.5rem' }}>Logs d'audit — {alertes.audit_logs.count} ligne(s) à purger</h4>
            {alertes.audit_logs.count === 0
              ? <p style={{ fontSize: '0.85rem', color: '#10b981' }}>Aucun log à purger.</p>
              : <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{alertes.audit_logs.count} ligne(s)</span>
                  <button className="btn-secondary"
                    style={{ fontSize: '0.8rem', color: '#ef4444', borderColor: '#ef4444' }}
                    onClick={purgerLogs} disabled={purging}>
                    {purging ? 'Suppression...' : 'Purger les logs'}
                  </button>
                </div>
            }
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Onglet Mon Compte ───────────────────────────────────────────
function OngletMonCompte() {
  const { token, user, refreshUser } = useAuth();
  const location = useLocation();
  const setup2faRequired = location.state?.setup2fa === true;
  const [form, setForm] = useState({ ancien: '', nouveau: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [isError, setIsError] = useState(false);

  // Téléphone
  const [telephone, setTelephone] = useState('');
  const [telSaving, setTelSaving] = useState(false);
  const [telMsg, setTelMsg] = useState('');

  // 2FA
  const [totpEnabled, setTotpEnabled] = useState(false);
  const [qrData, setQrData] = useState(null);    // { qr, secret } pendant le setup
  const [confirmCode, setConfirmCode] = useState('');
  const [totpMsg, setTotpMsg] = useState('');
  const [totpLoading, setTotpLoading] = useState(false);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE}/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(u => {
        if (!u) return;
        setTelephone(u.telephone || '');
        setTotpEnabled(u.totp_enabled || false);
      }).catch(() => {});
  }, [token]);

  async function handleSave() {
    if (!form.ancien || !form.nouveau) { setMsg('Remplissez tous les champs.'); setIsError(true); return; }
    if (form.nouveau !== form.confirm) { setMsg('Les nouveaux mots de passe ne correspondent pas.'); setIsError(true); return; }
    if (form.nouveau.length < 6) { setMsg('Le nouveau mot de passe doit contenir au moins 6 caractères.'); setIsError(true); return; }
    setSaving(true); setMsg('');
    try {
      const res = await fetch(`${API_BASE}/me/password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ancien: form.ancien, nouveau: form.nouveau }),
      });
      const data = await res.json();
      if (!res.ok) { setMsg(data.error || 'Erreur'); setIsError(true); }
      else { setMsg('✓ Mot de passe modifié avec succès.'); setIsError(false); setForm({ ancien: '', nouveau: '', confirm: '' }); }
    } catch { setMsg('Erreur réseau'); setIsError(true); }
    finally { setSaving(false); }
  }

  async function handleSaveTelephone() {
    setTelSaving(true); setTelMsg('');
    try {
      const res = await fetch(`${API_BASE}/me/telephone`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ telephone }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Erreur');
      setTelMsg('✓ Numéro enregistré.');
    } catch (e) { setTelMsg(`❌ ${e.message}`); }
    finally { setTelSaving(false); }
  }

  async function handleSetup2fa() {
    setTotpLoading(true); setTotpMsg('');
    try {
      const res = await fetch(`${API_BASE}/auth/2fa/setup`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur');
      setQrData(data);
    } catch (e) { setTotpMsg(`❌ ${e.message}`); }
    finally { setTotpLoading(false); }
  }

  async function handleConfirm2fa() {
    setTotpLoading(true); setTotpMsg('');
    try {
      const res = await fetch(`${API_BASE}/auth/2fa/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ code: confirmCode }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur');
      setTotpEnabled(true); setQrData(null); setConfirmCode('');
      setTotpMsg('✓ Double authentification activée avec succès.');
      refreshUser();
    } catch (e) { setTotpMsg(`❌ ${e.message}`); }
    finally { setTotpLoading(false); }
  }

  const iStyle = { ...inputStyle, maxWidth: 320 };
  const msgBox = (m, err) => m ? (
    <div style={{ padding: '0.5rem 0.8rem', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.85rem',
      background: err ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)',
      color: err ? '#f87171' : '#10b981', border: `1px solid ${err ? 'rgba(239,68,68,0.3)' : 'rgba(16,185,129,0.3)'}` }}>
      {m}
    </div>
  ) : null;

  return (
    <div style={{ maxWidth: 520 }}>
      {/* ── Bandeau setup 2FA obligatoire ── */}
      {setup2faRequired && !totpEnabled && (
        <div style={{ padding: '0.9rem 1.1rem', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.4)', borderRadius: '8px', marginBottom: '1.5rem', display: 'flex', gap: '0.7rem', alignItems: 'flex-start' }}>
          <span style={{ fontSize: '1.2rem' }}>⚠️</span>
          <div>
            <div style={{ fontWeight: 700, color: '#f59e0b', fontSize: '0.9rem', marginBottom: '0.2rem' }}>Configuration requise</div>
            <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>La double authentification est obligatoire. Configurez Microsoft Authenticator ci-dessous pour accéder à l'application.</div>
          </div>
        </div>
      )}
      {/* ── Mot de passe ── */}
      <h4 style={{ color: 'var(--text-primary)', marginBottom: '1.2rem' }}>Changer mon mot de passe</h4>
      <Field label="Mot de passe actuel">
        <input type="password" value={form.ancien} onChange={e => setForm(f => ({ ...f, ancien: e.target.value }))} style={iStyle} autoComplete="current-password" />
      </Field>
      <Field label="Nouveau mot de passe">
        <input type="password" value={form.nouveau} onChange={e => setForm(f => ({ ...f, nouveau: e.target.value }))} style={iStyle} autoComplete="new-password" />
      </Field>
      <Field label="Confirmer le nouveau mot de passe">
        <input type="password" value={form.confirm} onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))} style={iStyle} autoComplete="new-password" />
      </Field>
      {msgBox(msg, isError)}
      <button className="btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Enregistrement...' : 'Modifier le mot de passe'}</button>

      {/* ── Téléphone portable (secours SMS) ── */}
      <hr style={{ margin: '2rem 0', border: 'none', borderTop: '1px solid var(--border)' }} />
      <h4 style={{ color: 'var(--text-primary)', marginBottom: '1.2rem' }}>Téléphone portable</h4>
      <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
        Utilisé comme secours si vous n'avez pas accès à Microsoft Authenticator.
      </p>
      <Field label="Numéro de portable">
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <input type="tel" value={telephone} onChange={e => setTelephone(e.target.value)} placeholder="06 00 00 00 00" style={{ ...iStyle, flex: 1 }} />
          <button className="btn-secondary" onClick={handleSaveTelephone} disabled={telSaving} style={{ whiteSpace: 'nowrap' }}>
            {telSaving ? '...' : 'Enregistrer'}
          </button>
        </div>
      </Field>
      {telMsg && <div style={{ fontSize: '0.82rem', color: couleurMessage(niveauMessage(telMsg)), marginBottom: '0.5rem' }}>{telMsg}</div>}

      {/* ── Double authentification ── */}
      <hr style={{ margin: '2rem 0', border: 'none', borderTop: '1px solid var(--border)' }} />
      <h4 style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>Double authentification (2FA)</h4>

      {totpEnabled ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', padding: '0.8rem 1rem', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: '8px' }}>
          <span style={{ fontSize: '1.2rem' }}>✅</span>
          <div>
            <div style={{ fontWeight: 600, color: '#10b981', fontSize: '0.9rem' }}>Activée</div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>Microsoft Authenticator est configuré sur votre compte.</div>
          </div>
        </div>
      ) : (
        <>
          <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
            Sécurisez votre compte avec Microsoft Authenticator. Un code vous sera demandé à chaque connexion.
          </p>
          {!qrData ? (
            <button className="btn-primary" onClick={handleSetup2fa} disabled={totpLoading}>
              {totpLoading ? 'Génération...' : '🔐 Configurer la double authentification'}
            </button>
          ) : (
            <div style={{ padding: '1.2rem', background: 'var(--bg-elevated)', borderRadius: '10px', border: '1px solid var(--border)' }}>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                <strong style={{ color: 'var(--text-primary)' }}>1.</strong> Ouvrez <strong>Microsoft Authenticator</strong><br />
                <strong style={{ color: 'var(--text-primary)' }}>2.</strong> Appuyez sur <em>Ajouter un compte → Autre compte</em><br />
                <strong style={{ color: 'var(--text-primary)' }}>3.</strong> Scannez ce QR code :
              </p>
              <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                <img src={qrData.qr} alt="QR Code 2FA" style={{ width: 180, height: 180, borderRadius: 8, background: '#fff', padding: 8 }} />
              </div>
              <p style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', marginBottom: '1rem', textAlign: 'center' }}>
                Ou saisissez manuellement : <code style={{ background: 'var(--bg-card)', padding: '0.1rem 0.4rem', borderRadius: 4 }}>{qrData.secret}</code>
              </p>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.6rem' }}>
                <strong style={{ color: 'var(--text-primary)' }}>4.</strong> Entrez le code à 6 chiffres affiché :
              </p>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input
                  type="text" inputMode="numeric" value={confirmCode} onChange={e => setConfirmCode(e.target.value)}
                  placeholder="000 000" maxLength={7}
                  style={{ ...iStyle, textAlign: 'center', fontSize: '1.2rem', letterSpacing: '0.2rem', flex: 1 }}
                />
                <button className="btn-primary" onClick={handleConfirm2fa} disabled={totpLoading || confirmCode.replace(/\s/g,'').length < 6}>
                  {totpLoading ? '...' : 'Activer'}
                </button>
              </div>
              <button onClick={() => { setQrData(null); setConfirmCode(''); }} style={{ marginTop: '0.6rem', background: 'none', border: 'none', color: 'var(--text-tertiary)', fontSize: '0.78rem', cursor: 'pointer' }}>
                Annuler
              </button>
            </div>
          )}
        </>
      )}
      {totpMsg && <div style={{ fontSize: '0.82rem', color: couleurMessage(niveauMessage(totpMsg)), marginTop: '0.8rem' }}>{totpMsg}</div>}
    </div>
  );
}

const TABS = [
  { key: 'societe',        label: '🏢 Société',        roleMin: 'admin',       configurable: true },
  { key: 'regles',         label: '⚙ Règles',          roleMin: 'admin',       configurable: true },
  { key: 'documents',      label: '📄 Documents',       roleMin: 'admin',       configurable: true },
  { key: 'utilisateurs',   label: '👥 Utilisateurs',    roleMin: 'admin',       configurable: false },
  { key: 'email',          label: '✉ Email',            roleMin: 'admin',       configurable: false },
  { key: 'questionnaires', label: '📋 Questionnaires',  roleMin: 'admin',       configurable: true },
  { key: 'messages',       label: '✉ Messages',         roleMin: 'admin',       configurable: true },
  { key: 'signatures',     label: '✍ Signature email',  roleMin: 'utilisateur', configurable: false },
  { key: 'rgpd',           label: '🔒 RGPD',            roleMin: 'admin',       configurable: false },
  { key: 'moncompte',      label: '🔑 Mon compte',      roleMin: 'visiteur',    configurable: false },
];

export default function Parametres() {
  const { hasRole, canAccessOnglet, user } = useAuth();
  const routerLocation = useLocation();
  const visibleTabs = TABS.filter(t => {
    if (user?.role === 'admin') return true;
    // Onglets configurables : l'accès est accordé explicitement via les permissions du rôle
    if (t.configurable) return canAccessOnglet('parametres', t.key);
    // Onglets non-configurables (Utilisateurs, RGPD, Mon compte, Signature) : vérification du rôle minimum
    return hasRole(t.roleMin);
  });
  const [activeTab, setActiveTab] = useState(() =>
    routerLocation.state?.setup2fa ? 'moncompte' : (visibleTabs[0]?.key || 'societe')
  );
  const [params, setParams] = useState({});
  const [loadingParams, setLoadingParams] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  useEffect(() => {
    apiFetch('/parametres').then(d => { setParams(d); setLoadingParams(false); }).catch(() => setLoadingParams(false));
  }, []);

  function handleChange(key, val) {
    setParams(p => ({ ...p, [key]: val }));
  }

  async function handleSave() {
    setSaving(true); setSaveMsg('');
    try {
      await apiFetch('/parametres', { method: 'PUT', body: JSON.stringify(params) });
      setSaveMsg('✓ Paramètres enregistrés');
      setTimeout(() => setSaveMsg(''), 3000);
    } catch (e) { setSaveMsg(`❌ ${e.message}`); }
    finally { setSaving(false); }
  }

  if (visibleTabs.length === 0) return (
    <div className="page"><p style={{ color: 'var(--text-secondary)' }}>Accès non autorisé.</p></div>
  );

  return (
    <div className="page">
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 className="page-title">Paramètres</h1>
        <p className="page-subtitle">Configuration de l'application</p>
      </div>

      {saveMsg && (
        <div style={{ padding: '0.5rem 0.8rem', borderRadius: '6px', fontSize: '0.85rem', marginBottom: '1rem',
          ...styleMessage(niveauMessage(saveMsg)) }}>
          {saveMsg}
        </div>
      )}

      <div className="card">
        <div className="tab-bar" style={{ marginBottom: '1.5rem' }}>
          {visibleTabs.map(t => (
            <button key={t.key} className={`tab-btn ${activeTab === t.key ? 'active' : ''}`}
              onClick={() => setActiveTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>

        {loadingParams ? <Loading /> : (
          <>
            {activeTab === 'societe'      && <OngletSociete params={params} onChange={handleChange} onSave={handleSave} saving={saving} />}
            {activeTab === 'regles'       && <OngletRegles params={params} onChange={handleChange} onSave={handleSave} saving={saving} />}
            {activeTab === 'documents'    && <OngletDocuments params={params} onParamChange={handleChange} />}
            {activeTab === 'utilisateurs' && <><OngletUtilisateurs /><OngletRoles /></>}
            {activeTab === 'email'        && <OngletEmail params={params} onChange={handleChange} onSave={handleSave} saving={saving} />}
            {activeTab === 'questionnaires' && <OngletQuestionnaires typesQ={parseTypesQ(params.types_questionnaire)} />}
            {activeTab === 'messages'       && <OngletMessages />}
            {activeTab === 'signatures'     && <OngletSignatures />}
            {activeTab === 'rgpd'           && <OngletRGPD params={params} onChange={handleChange} onSave={handleSave} saving={saving} />}
            {activeTab === 'moncompte'      && <OngletMonCompte />}
          </>
        )}
      </div>
    </div>
  );
}
